import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormControl,
  InputLabel,
  CircularProgress,
  RadioGroup,
  Chip,
} from "@mui/material";
import {
  Package,
  ArrowLeft,
  Info,
  CloudUpload,
  Calendar,
  MapPin,
  Navigation,
  Trash2,
  Plus,
  PlusCircle,
  ChevronDown,
  Check,
  CheckCircle,
  Percent,
  Gift,
  Clock,
  Building2,
  Store,
  MessageSquare,
  Search,
  X,
  AlertTriangle,
  Link,
  FileText,
  NotepadTextDashed,
  Zap,
  ShoppingBag,
  MapPinHouse,
  Target,
  Phone,
  Truck,
  Wand2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { toast } from "react-toastify";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import { useMutation } from "react-query";
import branchService from "../../../requests/branches/BranchService";
import { useSelector } from "react-redux";
import BranchManagementModal from "../../../components/Branches/BranchManagementModal";
import {
  regionOptions,
  locationsByRegion,
} from "../../../utils/data";
import { useMarketplaceCategories } from "../../../hooks/useMarketplaceCategories";
import {
  isFashionCategory,
  sizesForPayload,
  sumSizeQuantities,
} from "../../../utils/fashionSizes";
import PricingSection from "../../../components/Offers/PricingSection";
import QuantitySection from "../../../components/Offers/QuantitySection";
import FashionSizesField from "../../../components/Deals/NewDealForm/FashionSizesField";
import axios from "axios";
import Config from "../../../Config";
import SendersRequest from "../../../requests/vendor/SendersRequest";
import Loader from "../../../components/Loader";
import InlinePartnerModal from "../../../components/Partners/InlinePartnerModal";
import PermissionDeniedModal from "../../../components/Permissions/PermissionDeniedModal";
import { canCreatePartners } from "../../../utils/rolesPermissions";
import { steps } from "../../../components/Deals/NewDealForm/Steps";
import { generateTermsFromTemplate } from "../../../components/Deals/NewDealForm/generateTermsFromTemplate";
import {
  validateStep as validateNewDealStep,
  isValidUrl,
} from "../../../components/Deals/NewDealForm/validateNewDealStep";
import {
  buildDevAutofillState,
  isLocalDevEnvironment,
} from "../../../components/Deals/NewDealForm/devAutofill";
import ReviewStep from "../../../components/Deals/NewDealForm/ReviewStep";
import DealFormStepper from "../../../components/Deals/NewDealForm/DealFormStepper";
import BackToOffersButton from "../../../components/Deals/NewDealForm/BackToOffersButton";

const DEAL_CREATED_TOAST_KEY = "dealboxx_deal_created_toast";

const NewDealForm = () => {
  const navigate = useNavigate();
  const { categories: marketplaceCategories, loading: marketplaceCategoriesLoading } =
    useMarketplaceCategories();
  const [imagePreview, setImagePreview] = React.useState(null);
  const [isDragOverStep2, setIsDragOverStep2] = useState(false);
  const [isDragOverPrimaryImage, setIsDragOverPrimaryImage] = useState(false);
  const [primaryImagePreview, setPrimaryImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPriceValid, setIsPriceValid] = useState(true);
  const [redemptionLocationType, setRedemptionLocationType] =
    useState("branch");
  const [partners, setPartners] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [expandedPartners, setExpandedPartners] = useState({});

  // Redemption location state (separate from target location)
  const [redemptionLocationTypeStep4, setRedemptionLocationTypeStep4] =
    useState("branch");
  const [selectedRedemptionBranches, setSelectedRedemptionBranches] = useState(
    []
  );
  const [selectedRedemptionPartners, setSelectedRedemptionPartners] = useState(
    []
  );
  const [expandedRedemptionPartners, setExpandedRedemptionPartners] = useState(
    {}
  );
  const [selectAllRedemptionLocations, setSelectAllRedemptionLocations] =
    useState(false);
  const [freeDeliveryLocationType, setFreeDeliveryLocationType] =
    useState("region");
  const [selectedFreeDeliveryBranches, setSelectedFreeDeliveryBranches] =
    useState([]);
  const [selectedFreeDeliveryPartners, setSelectedFreeDeliveryPartners] =
    useState([]);
  const [expandedFreeDeliveryPartners, setExpandedFreeDeliveryPartners] =
    useState({});
  const [selectAllFreeDeliveryLocations, setSelectAllFreeDeliveryLocations] =
    useState(false);
  const [freeDeliveryLocationSearchTerms, setFreeDeliveryLocationSearchTerms] =
    useState({});
  const [
    freeDeliveryLocationDropdownOpen,
    setFreeDeliveryLocationDropdownOpen,
  ] = useState({});
  const [redemptionLocationSearchTerms, setRedemptionLocationSearchTerms] =
    useState({});
  const [redemptionLocationDropdownOpen, setRedemptionLocationDropdownOpen] =
    useState({});
  const [senderIds, setSenderIds] = useState([]);
  const [showSenderIdModal, setShowSenderIdModal] = useState(false);
  const [hasPendingSenderId, setHasPendingSenderId] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [locationSearchTerms, setLocationSearchTerms] = useState({});
  const [locationDropdownOpen, setLocationDropdownOpen] = useState({});
  const [showInlinePartnerModal, setShowInlinePartnerModal] = useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [branchSearchTerm, setBranchSearchTerm] = useState("");

  // Branch management functions
  const fetchBranches = useCallback(async () => {
    setIsLoadingBranches(true);
    try {
      const response = await branchService.getBranches();
      const { data } = response;
      if (data?.status_code === "000" && data?.in_error === false) {
        setBranches(data?.data || []);
      } else {
        console.log(data?.reason || "Failed to fetch branches");
      }
    } catch (error) {
      console.log(error?.response?.reason || "Failed to fetch branches");
    } finally {
      setIsLoadingBranches(false);
    }
  }, []);

  const [stepValidation, setStepValidation] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });
  const [formData, setFormData] = React.useState({
    title: "",
    product_name: "",
    productCategory: "",
    product_subcategory: "",
    sizes: [],
    category: [],
    gender: [],
    location: "",
    expiry_date: "",
    start_date: new Date().toISOString().slice(0, 10),
    redemption_type: [],
    redemption_instructions: "",
    refund_policy: "",
    online_link: "",
    redemption_limits: [],
    exclusions: [],
    phone_number: "",
    sms_content: "",
    price: "",
    discounted_price: "",
    terms_and_conditions: "",
    region_location: [
      {
        region: "",
        location: "",
      },
    ],
    redemption_region_location: [
      {
        region: "",
        location: "",
      },
    ],
    partner_location: [],
    redemption_partner_location: [],
    target_branches: [],
    description: "",
    promote_deal: 0,
    vendor_id: null,
    image: null, // Primary image field
    // New offer type fields
    offer_type: "discount",
    // Discount specific fields
    discount_original_price: "",
    discount_sale_price: "",
    // Bundle specific fields
    original_sum: "",
    bundle_price: "",
    bundle_savings: "",
    // BOGO specific fields
    bogo_item_price: "",
    bogo_quantity: 1,
    bogo_free_quantity: 1,
    // Validity fields
    validity_type: "regular",
    flash_start_time: "",
    flash_end_time: "",
    flash_start_date: "",
    flash_duration: null,
    images: [],
    properties: [],
    min_age: "",
    max_age: "",
    no_age_limit: false,
    sku: "",
    delivery_fee: "not_apply",
    free_delivery_areas: [],
    bundle_skus: [],
    quantity: 0,
  });
  const [daysToExpiry, setDaysToExpiry] = useState(null);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [selectAllLocations, setSelectAllLocations] = useState(false);

  // Terms and Conditions state
  const [termsInputType, setTermsInputType] = useState("custom");
  const [termsUrl, setTermsUrl] = useState("");
  const [templateFields, setTemplateFields] = useState({
    vendor: "",
    validityStart: "",
    validityEnd: "",
    location: "",
    privacyLink: "",
    doaPeriod: "7",
    redemptionProcess: "",
  });

  // Category-specific terms state
  const [categorySpecificTerms, setCategorySpecificTerms] = useState({
    // Electronics & Gadgets
    warrantyPeriod: "6",
    warrantyPeriodCustom: "",
    doaReplacementDays: "7",
    // Groceries
    returnWindow: "24",
    // Fashion & Accessories
    fashionReturnDays: "7",
    // Real Estate & Housing
    advanceNoticeDays: "30",
    // Furniture
    furnitureWarrantyPeriod: "6",
    furnitureWarrantyPeriodCustom: "",
    furnitureReturnDays: "7",
    // Beauty, Spas & Wellness
    rescheduleNoticeHours: "24",
    packageValidityMonths: "3",
    // Baby & Mother Care
    babyReturnDays: "7",
    babyWarrantyPeriod: "6",
    // Tourism & Travel
    travelNoticeHours: "48",
    // Sports
    sportsReturnDays: "7",
    sportsWarrantyPeriod: "3",
    // Automotive
    autoWarrantyPeriod: "3",
    autoWarrantyPeriodCustom: "",
    autoReturnDays: "7",
    // Events and Entertainment
    nameTransferHours: "24",
    // Stationery
    stationeryReturnDays: "7",
  });

  // Refs for file inputs
  const fileInputRef = useRef(null);
  const changeImageInputRef = useRef(null);
  const formContainerRef = useRef(null);




  const user = useSelector((state) => state?.user?.user);
  const userPermissions = useSelector((state) => state?.user?.userPermissions);
  const isMainVendor = useSelector((state) => state?.user?.isMainVendor);
  const vendor_id = user?.id || user?.data?.data?.id;
  const token = localStorage.getItem("dealboxx_token");

  // Initialize vendor name from Redux user data
  useEffect(() => {
    if (user && !templateFields.vendor) {
      const brandName = user?.brand_name || "";
      if (brandName) {
        setTemplateFields((prev) => ({
          ...prev,
          vendor: brandName,
        }));
      }
    }
  }, [user, templateFields.vendor]);

  // Initialize flash sale default time when validity type is flash_sale
  useEffect(() => {
    if (formData.validity_type === "flash_sale" && !formData.flash_start_time) {
      const today = new Date().toISOString().split("T")[0];
      const defaultTime = `${today}T09:00`;
      const startDate = new Date(defaultTime);
      const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours default

      setFormData((prev) => ({
        ...prev,
        flash_start_time: defaultTime,
        flash_start_date: today,
        flash_duration: 2,
        flash_end_time: endDate.toISOString().slice(0, 16),
      }));
    }
  }, [formData.validity_type, formData.flash_start_time]);

  // Generate terms and conditions from template
  // Update terms and conditions when template fields or type changes
  const updateTermsAndConditions = useCallback(() => {
    let finalTerms = "";

    switch (termsInputType) {
      case "url":
        finalTerms = termsUrl || "";
        break;
      case "template":
        finalTerms = generateTermsFromTemplate({
          formData,
          templateFields,
          startDate,
          endDate,
          redemptionLocationTypeStep4,
          selectAllRedemptionLocations,
          selectedRedemptionBranches,
          categorySpecificTerms,
          selectAllFreeDeliveryLocations,
        });
        break;
      case "custom":
      default:
        finalTerms = formData.terms_and_conditions;
        break;
    }

    if (termsInputType !== "custom") {
      setFormData((prev) => ({
        ...prev,
        terms_and_conditions: finalTerms,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    termsInputType,
    termsUrl,
    formData.terms_and_conditions,
    formData,
    templateFields,
    startDate,
    endDate,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
    categorySpecificTerms,
    selectAllFreeDeliveryLocations,
  ]);

  // Update template fields when user data changes
  useEffect(() => {
    if (user) {
      const brandName =
        user?.company_name ||
        user?.business_name ||
        user?.account_display_name ||
        user?.name ||
        "";
      if (brandName && brandName !== templateFields.vendor) {
        setTemplateFields((prev) => ({
          ...prev,
          vendor: brandName,
        }));
      }
    }
  }, [user, templateFields.vendor]);

  // Update terms when template fields or type changes
  useEffect(() => {
    if (termsInputType === "template" || termsInputType === "url") {
      updateTermsAndConditions();
    }
  }, [
    templateFields,
    termsInputType,
    startDate,
    endDate,
    formData.category,
    formData.redemption_type,
    formData.redemption_region_location,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
    selectedRedemptionPartners,
    formData.refund_policy,
    termsUrl,
    updateTermsAndConditions,
    categorySpecificTerms,
    formData.online_link,
    formData.redemption_limits,
    formData.exclusions,
    formData.discount_original_price,
    formData.discount_sale_price,
    formData.original_sum,
    formData.bundle_price,
    formData.bundle_savings,
    formData.bogo_item_price,
    formData.bogo_quantity,
    formData.bogo_free_quantity,
    formData.delivery_fee,
    formData.free_delivery_areas,
    selectAllFreeDeliveryLocations,
  ]);

  useEffect(() => {
    if (formData.expiry_date) {
      const startDate = new Date(formData.start_date);
      const expiryDate = new Date(formData.expiry_date);
      startDate.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);
      const timeDiff = expiryDate - startDate;
      const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      setDaysToExpiry(daysDiff);
    } else {
      setDaysToExpiry(null);
    }
  }, [formData.expiry_date, formData.start_date]);

  useEffect(() => {
    setValidationErrors({});
  }, [formData]);

  const selectedMarketplaceCategory = marketplaceCategories.find(
    (item) => item?.name === formData.productCategory
  );
  const marketplaceSubcategories = Array.isArray(selectedMarketplaceCategory?.subcategories)
    ? selectedMarketplaceCategory.subcategories
    : [];

  // Sync product category to legacy category array for Step 3 and category-specific logic
  useEffect(() => {
    if (!formData.productCategory) return;
    const category = marketplaceCategories.find((item) => item?.name === formData.productCategory);
    const subcategory = (category?.subcategories || []).find(
      (item) => item?.name === formData.product_subcategory
    );
    setFormData((prev) => {
      if (!prev.productCategory) return prev;
      const categoryIds = category?.id != null ? [category.id] : marketplaceCategories.length ? [] : prev.category_ids || [];
      const subcategoryIds = subcategory?.id != null
        ? [subcategory.id]
        : marketplaceCategories.length
          ? []
          : prev.subcategory_ids || [];
      const sameCategory = Array.isArray(prev.category) && prev.category.length === 1 && prev.category[0] === prev.productCategory;
      const sameCategoryIds = JSON.stringify(prev.category_ids || []) === JSON.stringify(categoryIds);
      const sameSubcategoryIds = JSON.stringify(prev.subcategory_ids || []) === JSON.stringify(subcategoryIds);
      if (sameCategory && sameCategoryIds && sameSubcategoryIds) return prev;
      return {
        ...prev,
        category: [prev.productCategory],
        category_ids: categoryIds,
        subcategory_ids: subcategoryIds,
      };
    });
  }, [formData.productCategory, formData.product_subcategory, marketplaceCategories]);

  // Validate current step (delegates to shared validator with current form context)
  const validateStepContext = {
    formData,
    startDate,
    endDate,
    redemptionLocationType,
    selectedBranches,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
  };
  const validateStep = (step) =>
    validateNewDealStep(step, validateStepContext);

  // Update step validation
  useEffect(() => {
    const stepsToValidate = currentStep === 5 ? [1, 2, 3, 4, 5] : [currentStep];
    setStepValidation((prev) => {
      const next = { ...prev };
      stepsToValidate.forEach((step) => {
        const errors = validateStep(step);
        next[step] = Object.keys(errors).length === 0;
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    currentStep,
    startDate,
    endDate,
    redemptionLocationType,
    selectedBranches,
    redemptionLocationTypeStep4,
    selectAllRedemptionLocations,
    selectedRedemptionBranches,
    selectAllLocations,
  ]);

  const createDealMutation = useMutation({
    mutationFn: (formData) => {
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }
      return DealsRequest.createDeal(formData);
    },
    onSuccess: () => {
      setIsSubmitting(false);
    },
    onError: (error) => {
      if (
        error.message === "Authentication token not found. Please login again."
      ) {
        toast.error(error.message);
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        toast.error(error.message || "Failed to create deal");
        setIsSubmitting(false);
      }
    },
  });

  const fetchInitialData = async () => {
    try {
      setIsInitialLoading(true);
      setIsLoadingPartners(true);

      // Make all API calls in parallel
      const [partnersResponse, senderIdResponse, branchesResponse] =
        await Promise.all([
          axios.get(`${Config.backendDomain}vendor/partner`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
            },
          }),
          SendersRequest.getAllSenders(),
          branchService.getBranches(),
        ]);

      // Process partners data
      const partnersData = partnersResponse?.data;
      if (
        partnersData?.data?.status_code === "000" &&
        partnersData?.data?.in_error === false
      ) {
        setPartners(partnersData?.data?.data);
      } else {
        toast.error(partnersData?.data?.reason || "Failed to fetch partners");
      }

      // Process sender IDs data
      if (
        senderIdResponse?.data?.status_code === "000" &&
        senderIdResponse?.data?.in_error === false
      ) {
        const allSenders = senderIdResponse?.data?.data || [];
        const approvedSenders = allSenders.filter(
          (sender) => sender.status === "Approved"
        );
        const pendingSenders = allSenders.filter(
          (sender) => sender.status === "Pending"
        );

        setSenderIds(approvedSenders);
        setHasPendingSenderId(pendingSenders.length > 0);

        if (allSenders.length === 0) {
          setShowSenderIdModal(true);
        }
      } else {
        toast.error(
          senderIdResponse?.data?.reason || "Failed to fetch sender IDs"
        );
      }

      // Process branches data
      if (
        branchesResponse.status &&
        branchesResponse.data?.status_code === "000" &&
        !branchesResponse.data?.in_error
      ) {
        setBranches(branchesResponse.data?.data || []);
      } else {
        console.log(branchesResponse.message || "Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setIsLoadingPartners(false);
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".location-dropdown")) {
        setLocationDropdownOpen({});
      }

      // Handle free delivery dropdowns
      const deliveryDropdowns = document.querySelectorAll(
        "[data-delivery-dropdown]"
      );
      deliveryDropdowns.forEach((dropdown, index) => {
        if (!dropdown.contains(event.target)) {
          setFreeDeliveryLocationDropdownOpen((prev) => ({
            ...prev,
            [index]: false,
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const userCanAddPartner = canCreatePartners(userPermissions, isMainVendor);
  console.log("userCanAddPartner", userCanAddPartner);

  // Permission check for Create_Offer
  const hasCreateOfferPermission =
    userPermissions &&
    userPermissions["Deals_Management"] &&
    userPermissions["Deals_Management"].includes("Create_Offer");

  if (!hasCreateOfferPermission && !isMainVendor) {
    return (
      <PermissionDeniedModal message="You do not have permission to create a new offer." />
    );
  }

  const nextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setValidationErrors({});
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      // Scroll to top when moving to next step
      setTimeout(() => {
        if (formContainerRef.current) {
          formContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setTimeout(() => {
        if (formContainerRef.current) {
          formContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  const goToStep = (step) => {
    // Only allow going to previous steps or next step if current is valid
    if (step < currentStep || stepValidation[currentStep]) {
      setCurrentStep(step);
      // Scroll to top when jumping to a step
      setTimeout(() => {
        if (formContainerRef.current) {
          formContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        toast.error("Image size must not exceed 2MB");
        // Clear the input value
        e.target.value = "";
        return;
      }

      try {
        // Validate that file is a valid File object
        if (!(file instanceof File)) {
          throw new Error("Invalid file object");
        }
        
        // Convert to base64
        const base64Image = await convertFileToBase64(file);
        
        setImagePreview(URL.createObjectURL(file));
        setFormData((prev) => ({
          ...prev,
          image: base64Image,
        }));
      } catch (error) {
        console.error("Error processing image:", error);
        toast.error("Error processing image. Please try again.");
        // Clear the input value on error
        e.target.value = "";
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("Image size must not exceed 2MB");
        return;
      }

      try {
        // Validate that file is a valid File object
        if (!(file instanceof File)) {
          throw new Error("Invalid file object");
        }
        
        // Convert to base64
        const base64Image = await convertFileToBase64(file);
        
        // Set the preview
        setImagePreview(URL.createObjectURL(file));
        // Add the file to formData
        setFormData((prev) => ({
          ...prev,
          image: base64Image,
        }));
      } catch (error) {
        console.error("Error processing dropped image:", error);
        toast.error("Error processing image. Please try again.");
      }
    }
  };

  const handlePartnerAdded = async () => {
    try {
      setIsLoadingPartners(true);
      const response = await axios.get(
        `${Config.backendDomain}vendor/partner`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
          },
        }
      );

      const partnersData = response?.data;
      if (
        partnersData?.data?.status_code === "000" &&
        partnersData?.data?.in_error === false
      ) {
        setPartners(partnersData?.data?.data);
      }
    } catch (error) {
      console.error("Error refreshing partners:", error);
    } finally {
      setIsLoadingPartners(false);
    }
  };

  const handleAddPartnerClick = () => {
    const hasPermission = canCreatePartners(userPermissions, isMainVendor);
    if (hasPermission) {
      setShowInlinePartnerModal(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  if (isInitialLoading) {
    return <Loader />;
  }

  const handlePartnerToggle = (partnerId) => {
    if (selectedPartners.includes(partnerId)) {
      // Remove partner
      setSelectedPartners(selectedPartners.filter((id) => id !== partnerId));

      // Remove partner branches from formData
      setFormData((prevData) => ({
        ...prevData,
        partner_location: prevData.partner_location.filter(
          (location) => location.partner_id !== partnerId
        ),
      }));
    } else {
      // Add partner
      setSelectedPartners([...selectedPartners, partnerId]);

      // Initialize partner in formData with no selected branches
      const partner = partners.find((p) => p.partner_slug === partnerId);
      if (partner) {
        setFormData((prevData) => ({
          ...prevData,
          partner_location: [
            ...prevData.partner_location,
            {
              partner_id: partnerId,
              partner_name: partner.name,
              branches: [],
            },
          ],
        }));

        // Auto-expand the partner when selected
        setExpandedPartners((prev) => ({
          ...prev,
          [partnerId]: true,
        }));
      }
    }
  };

  const togglePartnerExpansion = (partnerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedPartners((prev) => ({
      ...prev,
      [partnerId]: !prev[partnerId],
    }));
  };

  const handleBranchToggle = (partnerId, branch) => {
    setFormData((prevData) => {
      const partnerIndex = prevData.partner_location.findIndex(
        (p) => p.partner_id === partnerId
      );

      if (partnerIndex === -1) return prevData;

      const partnerLocation = [...prevData.partner_location];
      const currentBranches = partnerLocation[partnerIndex].branches;

      // Check if branch is already selected
      const branchIndex = currentBranches.findIndex(
        (b) => b.branch_slug === branch.branch_slug
      );

      if (branchIndex === -1) {
        // Add branch
        partnerLocation[partnerIndex] = {
          ...partnerLocation[partnerIndex],
          branches: [...currentBranches, branch],
        };
      } else {
        // Remove branch
        partnerLocation[partnerIndex] = {
          ...partnerLocation[partnerIndex],
          branches: currentBranches.filter(
            (b) => b.branch_slug !== branch.branch_slug
          ),
        };
      }

      return {
        ...prevData,
        partner_location: partnerLocation,
      };
    });
  };

  const isBranchSelected = (partnerId, branchSlug) => {
    const partnerLocation = formData.partner_location.find(
      (p) => p.partner_id === partnerId
    );
    if (!partnerLocation) return false;

    return partnerLocation.branches.some((b) => b.branch_slug === branchSlug);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setValidationErrors({});
    setIsSubmitting(true);

    if (!vendor_id) {
      toast.error("Vendor ID not found. Please try logging in again.");
      setIsSubmitting(false);
      return;
    }

    const errors = {};

    // Only validate expiry date for regular deals, not flash sales
    if (formData.validity_type === "regular") {
      if (!endDate) {
        errors.expiry_date = "Expiry date is required";
      } else {
        const today = new Date();
        const expiryDate = new Date(endDate);
        if (expiryDate <= today) {
          errors.expiry_date = "Expiry date must be in the future";
        }
      }
    } else if (formData.validity_type === "flash_sale") {
      // Validate flash sale specific fields
      if (!formData.flash_start_time) {
        errors.flash_start_time = "Flash start time is required";
      }
      if (!formData.flash_end_time) {
        errors.flash_end_time = "Flash end time is required";
      }
      if (!formData.flash_duration) {
        errors.flash_duration = "Flash duration is required";
      }
    }

    if (!formData.phone_number) {
      errors.phone_number = "Phone number is required";
    } else if (formData.phone_number.length !== 9) {
      errors.phone_number = "Phone number must be exactly 9 digits after 233";
    }

    // if (!formData.description || formData.description.trim().length < 10) {
    //   errors.description = "Description must be at least 10 characters long";
    // }

    if (
      !formData.terms_and_conditions ||
      formData.terms_and_conditions.trim().length < 10
    ) {
      errors.terms_and_conditions =
        "Terms & Conditions must be at least 10 characters long";
    }

    // Validate targeting location based on type
    if (redemptionLocationType === "region") {
      if (
        formData.region_location.length === 0 ||
        (formData.region_location.length === 1 &&
          !formData.region_location[0].region &&
          !formData.region_location[0].location)
      ) {
        errors.region_location =
          "Please select at least one region and location";
      }
    } else if (redemptionLocationType === "branch") {
      if (selectedBranches.length === 0) {
        errors.branch_location = "Please select at least one branch";
      }
    } else if (redemptionLocationType === "partner") {
      const validPartnerLocations = formData.partner_location.filter(
        (partner) => partner.branches && partner.branches.length > 0
      );

      if (validPartnerLocations.length === 0) {
        errors.partner_location = "Please select at least one partner branch";
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      setIsSubmitting(false);
      return;
    }

    // Prepare payload based on targeting and redemption location types
    const updatedFormData = {
      ...formData,
      phone_number: `233${formData.phone_number}`,
      vendor_id: vendor_id,
      expiry_date: endDate,
      gender: formData.gender.length > 0 ? formData.gender.join(", ") : null,
      sizes: sizesForPayload(
        formData.sizes,
        formData.productCategory,
        formData.product_subcategory
      ),
      sms_count: formData.sms_content
        ? Math.ceil(formData.sms_content.length / 160) || 1
        : 1,
    };

    // When sizes are set, quantity Available must equal sum of size stocks
    if (updatedFormData.sizes.length > 0) {
      updatedFormData.quantity = sumSizeQuantities(updatedFormData.sizes);
    }
    // Clear out fields that don't belong to the selected offer type
    if (formData.offer_type === "discount") {
      // Keep discount fields, set bundle and bogo fields to null
      updatedFormData.original_sum = null;
      updatedFormData.bundle_price = null;
      updatedFormData.bundle_savings = null;
      updatedFormData.bundle_skus = [];
      updatedFormData.bogo_item_price = null;
      updatedFormData.bogo_quantity = null;
      updatedFormData.bogo_free_quantity = null;
      // Set legacy price fields for backward compatibility
      updatedFormData.price = formData.discount_original_price;
      updatedFormData.discounted_price = formData.discount_sale_price;
    } else if (formData.offer_type === "bundle") {
      // Keep bundle fields, set discount and bogo fields to null
      updatedFormData.discount_original_price = null;
      updatedFormData.discount_sale_price = null;
      updatedFormData.bogo_item_price = null;
      updatedFormData.bogo_quantity = null;
      updatedFormData.bogo_free_quantity = null;
      // Clear legacy price fields
      updatedFormData.price = null;
      updatedFormData.discounted_price = null;
    } else if (formData.offer_type === "bogo") {
      // Keep bogo fields, set discount and bundle fields to null
      updatedFormData.discount_original_price = null;
      updatedFormData.discount_sale_price = null;
      updatedFormData.original_sum = null;
      updatedFormData.bundle_price = null;
      updatedFormData.bundle_savings = null;
      updatedFormData.bundle_skus = [];
      // Clear legacy price fields
      updatedFormData.price = null;
      updatedFormData.discounted_price = null;
    }

    // ========== VALIDITY TYPE SPECIFIC FIELDS ==========
    // Clear out fields that don't belong to the selected validity type
    if (formData.validity_type === "flash_sale") {
      // Flash sale doesn't need regular expiry_date
      updatedFormData.expiry_date = null;
    } else {
      // Regular deal doesn't need flash fields
      updatedFormData.flash_start_time = null;
      updatedFormData.flash_end_time = null;
      updatedFormData.flash_start_date = null;
      updatedFormData.flash_duration = null;
    }

    // ========== TARGETING LOCATION (Step 3) ==========
    // Handle region-based, branch-based, or partner-based targeting
    if (redemptionLocationType === "region") {
      // Region-based targeting
      updatedFormData.partner_location = [];
      updatedFormData.target_branches = [];
      // region_location is already in formData
    } else if (redemptionLocationType === "branch") {
      // Branch-based targeting
      updatedFormData.region_location = [];
      updatedFormData.partner_location = [];
      updatedFormData.target_branches = selectedBranches.map(branch => ({
        branch_id: branch.id,
        branch_slug: branch.slug,
        name: branch.name,
        location: branch.location,
        region: branch.region,
        google_url: branch.google_url || null,
      }));
    } else if (redemptionLocationType === "partner") {
      // Partner-based targeting
      updatedFormData.region_location = [];
      updatedFormData.target_branches = [];
      // Filter out partners with no selected branches
      updatedFormData.partner_location = updatedFormData.partner_location.filter(
        (partner) => partner.branches && partner.branches.length > 0
      );
    }

    // ========== REDEMPTION LOCATION (Step 4) ==========
    // Handle region-based, branch-based, or partner-based redemption
    if (formData.redemption_type.includes("In-store")) {
      if (redemptionLocationTypeStep4 === "region") {
        // Region-based redemption
        updatedFormData.redemption_branches = [];
        updatedFormData.redemption_partners = [];
        // redemption_region_location is already in formData
      } else if (redemptionLocationTypeStep4 === "branch") {
        // Branch-based redemption
        updatedFormData.redemption_region_location = [];
        updatedFormData.redemption_partners = [];
        updatedFormData.redemption_branches = selectedRedemptionBranches.map(branch => ({
          branch_id: branch.id,
          branch_slug: branch.slug,
          name: branch.name,
          location: branch.location,
          region: branch.region,
          google_url: branch.google_url || null,
        }));
      } else if (redemptionLocationTypeStep4 === "partner") {
        // Partner-based redemption
        updatedFormData.redemption_region_location = [];
        updatedFormData.redemption_branches = [];
        updatedFormData.redemption_partners = formData.redemption_partner_location
          .filter(partner => partner.branches && partner.branches.length > 0)
          .map(partner => ({
            partner_id: partner.partner_id,
            partner_name: partner.partner_name,
            branches: partner.branches.map(branch => ({
              branch_slug: branch.branch_slug,
              branch_id: branch.id,
              name: branch.name,
              location: branch.location,
              region: branch.region,
              google_url: branch.google_url || null,
            })),
          }));
      }
    } else {
      // Online only redemption - clear all in-store location fields
      updatedFormData.redemption_region_location = [];
      updatedFormData.redemption_branches = [];
      updatedFormData.redemption_partners = [];
    }

    // ========== CLEANUP UNUSED REDEMPTION FIELDS ==========
    // Remove redemption_partner_location as it's a helper field, not for the backend
    delete updatedFormData.redemption_partner_location;

    // ========== DELIVERY FEE CLEANUP ==========
    // Clear delivery-related fields if delivery doesn't apply
    if (formData.delivery_fee === "not_apply") {
      updatedFormData.free_delivery_areas = [];
    }

    // ========== FREE DELIVERY AREAS ==========
    // Handle nationwide free delivery vs specific areas
    if (formData.delivery_fee === "free_within_location") {
      if (selectAllFreeDeliveryLocations) {
        // Nationwide free delivery
        updatedFormData.free_delivery_areas = ["Nationwide"];
      }
      // Otherwise, use the specific areas already in formData.free_delivery_areas
    }

    // Log the complete payload for debugging
    console.log("📦 Complete Deal Payload:", JSON.stringify(updatedFormData, null, 2));

    try {
      const envelope = await createDealMutation.mutateAsync(updatedFormData);

      if (!envelope || String(envelope.status_code) !== "001" || envelope.in_error) {
        toast.error(envelope?.reason || "Failed to create deal");
        setIsSubmitting(false);
        return;
      }

      sessionStorage.setItem(
        DEAL_CREATED_TOAST_KEY,
        envelope.reason || envelope.message || "Deal created successfully!"
      );
      setIsSubmitting(false);
      window.location.replace("/offers");
    } catch (error) {
      console.error("Error creating deal:", error);
      toast.error(error.message || "Failed to create deal");
      setIsSubmitting(false);
    }
  };

  const addRegionLocation = () => {
    if (formData.region_location.length >= 5) {
      toast.error("Maximum of 5 locations allowed");
      return;
    }
    setFormData({
      ...formData,
      region_location: [
        ...formData.region_location,
        { region: "", location: "" },
      ],
    });
  };

  const removeRegionLocation = (index) => {
    const newRegionLocation = formData.region_location.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      region_location: newRegionLocation,
    });
    // Clean up search terms and dropdown state
    const newSearchTerms = { ...locationSearchTerms };
    const newDropdownOpen = { ...locationDropdownOpen };
    delete newSearchTerms[index];
    delete newDropdownOpen[index];
    setLocationSearchTerms(newSearchTerms);
    setLocationDropdownOpen(newDropdownOpen);
  };

  // Redemption location helper functions
  const addRedemptionRegionLocation = () => {
    if (formData.redemption_region_location.length >= 5) {
      toast.error("Maximum of 5 locations allowed");
      return;
    }
    setFormData({
      ...formData,
      redemption_region_location: [
        ...formData.redemption_region_location,
        { region: "", location: "" },
      ],
    });
  };

  const removeRedemptionRegionLocation = (index) => {
    const newRegionLocation = formData.redemption_region_location.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      redemption_region_location: newRegionLocation,
    });
    // Clean up search terms and dropdown state
    const newSearchTerms = { ...redemptionLocationSearchTerms };
    const newDropdownOpen = { ...redemptionLocationDropdownOpen };
    delete newSearchTerms[index];
    delete newDropdownOpen[index];
    setRedemptionLocationSearchTerms(newSearchTerms);
    setRedemptionLocationDropdownOpen(newDropdownOpen);
  };

  // Free delivery location helper functions
  const addFreeDeliveryRegionLocation = () => {
    if (formData.free_delivery_areas.length >= 5) {
      toast.error("Maximum of 5 locations allowed");
      return;
    }
    setFormData({
      ...formData,
      free_delivery_areas: [
        ...formData.free_delivery_areas,
        { region: "", location: "" },
      ],
    });
  };

  const removeFreeDeliveryRegionLocation = (index) => {
    const newRegionLocation = formData.free_delivery_areas.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      free_delivery_areas: newRegionLocation,
    });
    // Clean up search terms and dropdown state
    const newSearchTerms = { ...freeDeliveryLocationSearchTerms };
    const newDropdownOpen = { ...freeDeliveryLocationDropdownOpen };
    delete newSearchTerms[index];
    delete newDropdownOpen[index];
    setFreeDeliveryLocationSearchTerms(newSearchTerms);
    setFreeDeliveryLocationDropdownOpen(newDropdownOpen);
  };

  const toggleFreeDeliveryBranchSelection = (branch) => {
    const isSelected = selectedFreeDeliveryBranches.some(
      (b) => b.id === branch.id
    );
    if (isSelected) {
      setSelectedFreeDeliveryBranches(
        selectedFreeDeliveryBranches.filter((b) => b.id !== branch.id)
      );
    } else {
      setSelectedFreeDeliveryBranches([
        ...selectedFreeDeliveryBranches,
        branch,
      ]);
    }
  };

  const toggleFreeDeliveryPartnerExpansion = (partnerId) => {
    setExpandedFreeDeliveryPartners((prev) => ({
      ...prev,
      [partnerId]: !prev[partnerId],
    }));
  };

  const handleFreeDeliveryBranchToggle = (branch) => {
    toggleFreeDeliveryBranchSelection(branch);
  };

  const isFreeDeliveryBranchSelected = (branch) => {
    return selectedFreeDeliveryBranches.some((b) => b.id === branch.id);
  };

  const handleFreeDeliveryLocationSearch = (index, value) => {
    setFreeDeliveryLocationSearchTerms((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleFreeDeliveryLocationSelect = (index, region, location) => {
    const newAreas = [...formData.free_delivery_areas];
    newAreas[index] = { region, location };
    setFormData({
      ...formData,
      free_delivery_areas: newAreas,
    });
    setFreeDeliveryLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: false,
    }));
  };

  const toggleFreeDeliveryLocationDropdown = (index) => {
    setFreeDeliveryLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getFilteredFreeDeliveryLocations = (index) => {
    const searchTerm = freeDeliveryLocationSearchTerms[index] || "";

    const filtered = [];

    // If no search term, show all regions
    if (!searchTerm) {
      regionOptions.forEach((region) => {
        filtered.push({ region, location: "All locations in " + region });
      });
      return filtered.slice(0, 20); // Limit initial results
    }

    // Search through regions
    regionOptions.forEach((region) => {
      if (region.toLowerCase().includes(searchTerm.toLowerCase())) {
        filtered.push({ region, location: "All locations in " + region });
      }

      // Search through locations within each region
      const locations = locationsByRegion[region] || [];
      locations.forEach((location) => {
        if (location.toLowerCase().includes(searchTerm.toLowerCase())) {
          filtered.push({ region, location });
        }
      });
    });

    // Sort results: exact matches first, then partial matches
    return filtered.sort((a, b) => {
      const aRegion = a.region.toLowerCase();
      const bRegion = b.region.toLowerCase();
      const aLocation = a.location.toLowerCase();
      const bLocation = b.location.toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      // Exact region matches first
      if (aRegion === searchLower && bRegion !== searchLower) return -1;
      if (bRegion === searchLower && aRegion !== searchLower) return 1;

      // Exact location matches second
      if (aLocation === searchLower && bLocation !== searchLower) return -1;
      if (bLocation === searchLower && aLocation !== searchLower) return 1;

      // Starts with matches third
      if (aRegion.startsWith(searchLower) && !bRegion.startsWith(searchLower))
        return -1;
      if (bRegion.startsWith(searchLower) && !aRegion.startsWith(searchLower))
        return 1;

      // Alphabetical order for remaining results
      return aRegion.localeCompare(bRegion);
    });
  };

  const toggleRedemptionBranchSelection = (branch) => {
    const isSelected = selectedRedemptionBranches.some(
      (b) => b.branch_slug === branch.branch_slug
    );
    if (isSelected) {
      setSelectedRedemptionBranches(
        selectedRedemptionBranches.filter(
          (b) => b.branch_slug !== branch.branch_slug
        )
      );
    } else {
      setSelectedRedemptionBranches([...selectedRedemptionBranches, branch]);
    }
  };

  const toggleRedemptionPartnerExpansion = (partnerSlug, e) => {
    e.stopPropagation();
    setExpandedRedemptionPartners((prev) => ({
      ...prev,
      [partnerSlug]: !prev[partnerSlug]
    }));
  };

  const handleRedemptionPartnerToggle = (partnerId) => {
    const partner = partners.find((p) => p.partner_slug === partnerId);
    if (!partner) return;

    if (selectedRedemptionPartners.includes(partnerId)) {
      // Remove partner
      setSelectedRedemptionPartners(selectedRedemptionPartners.filter((id) => id !== partnerId));

      // Remove partner branches from formData
      setFormData((prevData) => ({
        ...prevData,
        redemption_partner_location: prevData.redemption_partner_location.filter(
          (location) => location.partner_id !== partnerId
        ),
      }));
    } else {
      // Add partner
      setSelectedRedemptionPartners([...selectedRedemptionPartners, partnerId]);

      // Initialize partner in formData with no selected branches
      setFormData((prevData) => ({
        ...prevData,
        redemption_partner_location: [
          ...prevData.redemption_partner_location,
          {
            partner_id: partnerId,
            partner_name: partner.name,
            branches: [],
          },
        ],
      }));

      // Auto-expand the partner when selected
      setExpandedRedemptionPartners((prev) => ({
        ...prev,
        [partnerId]: true,
      }));
    }
  };

  const handleRedemptionBranchToggle = (partnerId, branch) => {
    setFormData((prevData) => {
      const partnerIndex = prevData.redemption_partner_location.findIndex(
        (p) => p.partner_id === partnerId
      );

      if (partnerIndex === -1) return prevData;

      const partnerLocation = [...prevData.redemption_partner_location];
      const currentBranches = partnerLocation[partnerIndex].branches;

      // Check if branch is already selected
      const branchIndex = currentBranches.findIndex(
        (b) => b.branch_slug === branch.branch_slug
      );

      if (branchIndex === -1) {
        // Add branch
        partnerLocation[partnerIndex] = {
          ...partnerLocation[partnerIndex],
          branches: [...currentBranches, branch],
        };
      } else {
        // Remove branch
        partnerLocation[partnerIndex] = {
          ...partnerLocation[partnerIndex],
          branches: currentBranches.filter(
            (b) => b.branch_slug !== branch.branch_slug
          ),
        };
      }

      return {
        ...prevData,
        redemption_partner_location: partnerLocation,
      };
    });
  };

  const isRedemptionBranchSelected = (partnerId, branchSlug) => {
    const partnerLocation = formData.redemption_partner_location.find(
      (p) => p.partner_id === partnerId
    );
    if (!partnerLocation) return false;

    return partnerLocation.branches.some((b) => b.branch_slug === branchSlug);
  };

  const isRedemptionPartnerSelected = (partnerId) => {
    return selectedRedemptionPartners.includes(partnerId);
  };

  // Redemption location search and dropdown functions
  const handleRedemptionLocationSearch = (index, searchTerm) => {
    setRedemptionLocationSearchTerms((prev) => ({
      ...prev,
      [index]: searchTerm,
    }));

    // If user is typing and there's a current selection that's different from search term, clear the selection
    const currentLocation =
      formData.redemption_region_location[index]?.location;
    if (
      currentLocation &&
      currentLocation !== "other" &&
      searchTerm !== currentLocation
    ) {
      const newRegionLocation = [...formData.redemption_region_location];
      newRegionLocation[index] = {
        ...newRegionLocation[index],
        location: "",
        customLocation: undefined,
      };
      setFormData({
        ...formData,
        redemption_region_location: newRegionLocation,
      });
    }
  };

  const handleRedemptionLocationSelect = (index, location) => {
    const newRegionLocation = [...formData.redemption_region_location];
    newRegionLocation[index] = {
      ...newRegionLocation[index],
      location: location,
      customLocation: location === "other" ? "" : undefined,
    };
    setFormData({
      ...formData,
      redemption_region_location: newRegionLocation,
    });
    // Clear search term to show selected location and close dropdown
    setRedemptionLocationSearchTerms((prev) => ({
      ...prev,
      [index]: undefined,
    }));
    setRedemptionLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: false,
    }));
  };

  const toggleRedemptionLocationDropdown = (index) => {
    setRedemptionLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getFilteredRedemptionLocations = (region, searchTerm) => {
    if (!region || !locationsByRegion[region]) return [];

    const locations = locationsByRegion[region];
    if (!searchTerm) return locations;

    return locations.filter((location) =>
      location.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleLocationSearch = (index, searchTerm) => {
    setLocationSearchTerms((prev) => ({
      ...prev,
      [index]: searchTerm,
    }));

    // If user is typing and there's a current selection that's different from search term, clear the selection
    const currentLocation = formData.region_location[index]?.location;
    if (
      currentLocation &&
      currentLocation !== "other" &&
      searchTerm !== currentLocation
    ) {
      const newRegionLocation = [...formData.region_location];
      newRegionLocation[index] = {
        ...newRegionLocation[index],
        location: "",
        customLocation: undefined,
      };
      setFormData({
        ...formData,
        region_location: newRegionLocation,
      });
    }
  };

  const handleLocationSelect = (index, location) => {
    const newRegionLocation = [...formData.region_location];
    newRegionLocation[index] = {
      ...newRegionLocation[index],
      location: location,
      customLocation: location === "other" ? "" : undefined,
    };
    setFormData({
      ...formData,
      region_location: newRegionLocation,
    });
    // Clear search term to show selected location and close dropdown
    setLocationSearchTerms((prev) => {
      const newSearchTerms = { ...prev };
      delete newSearchTerms[index];
      return newSearchTerms;
    });
    setLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: false,
    }));
  };

  const toggleLocationDropdown = (index) => {
    setLocationDropdownOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getFilteredLocations = (region, searchTerm) => {
    if (!region || !locationsByRegion[region]) return [];

    const locations = locationsByRegion[region];
    if (!searchTerm) return locations;

    return locations.filter((location) =>
      location.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const calculateDays = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleCategoryChange = (event) => {
    setFormData({
      ...formData,
      category: event.target.value,
    });
  };


  const toggleBranchSelection = (branch) => {
    setSelectedBranches((prev) => {
      const isSelected = prev.some((b) => b.branch_slug === branch.branch_slug);
      if (isSelected) {
        return prev.filter((b) => b.branch_slug !== branch.branch_slug);
      } else {
        return [...prev, branch];
      }
    });

    // Also update formData.target_branches
    setFormData(prev => ({
      ...prev,
      target_branches: (prev.target_branches || []).some(b => b.branch_slug === branch.branch_slug)
        ? (prev.target_branches || []).filter(b => b.branch_slug !== branch.branch_slug)
        : [...(prev.target_branches || []), {
            branch_id: branch.id,
            branch_slug: branch.branch_slug,
            name: branch.name,
            location: branch.location,
            region: branch.region,
            google_url: branch.google_url || null
          }]
    }));
  };

  const filteredBranches = branches.filter((branch) => {
    const searchTerm = branchSearchTerm.toLowerCase();
    return (
      branch.name.toLowerCase().includes(searchTerm) ||
      branch.location.toLowerCase().includes(searchTerm) ||
      branch.region.toLowerCase().includes(searchTerm)
    );
  });

  // Helper function to convert File to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      // Validate input
      if (!file || !(file instanceof File)) {
        reject(new Error("Invalid file object"));
        return;
      }

      if (!file.type.startsWith("image/")) {
        reject(new Error("File is not an image"));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      
      reader.onerror = (error) => {
        reject(new Error(`FileReader error: ${error.message || "Unknown error"}`));
      };
      
      reader.onabort = () => {
        reject(new Error("File reading was aborted"));
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        reject(new Error(`Failed to start reading file: ${error.message}`));
      }
    });
  };

  // Image upload handlers for Step 2
  const addStep2Images = async (files) => {
    const currentImages = formData.images || [];

    if (currentImages.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    // Validate files
    const validFiles = files.filter((file) => {
      if (!file || !(file instanceof File)) {
        console.warn("Invalid file object:", file);
        return false;
      }
      if (!file.type.startsWith("image/")) {
        console.warn("Non-image file:", file.name, file.type);
        return false;
      }
      return true;
    });

    if (validFiles.length !== files.length) {
      toast.error("Please select only valid image files");
      return;
    }

    if (validFiles.length === 0) {
      toast.error("No valid images to upload");
      return;
    }

    // Check file sizes
    const newFilesSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    if (newFilesSize > 10 * 1024 * 1024) {
      toast.error("Selected images exceed 10MB limit");
      return;
    }

    // Convert files to base64
    try {
      const base64Images = await Promise.all(
        validFiles.map(async (file) => {
          try {
            return await convertFileToBase64(file);
          } catch (error) {
            console.error("Error converting file to base64:", file.name, error);
            throw new Error(`Failed to convert ${file.name}: ${error.message}`);
          }
        })
      );

      setFormData({
        ...formData,
        images: [...currentImages, ...base64Images],
      });

      toast.success(`${validFiles.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error("Error converting images to base64:", error);
      toast.error(`Failed to process gallery images: ${error.message}`);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      console.warn("No files selected");
      return;
    }
    console.log("Uploading files:", files.map(f => ({ name: f.name, size: f.size, type: f.type })));
    addStep2Images(files);
  };

  const handleImageDragOverStep2 = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverStep2(true);
  };

  const handleImageDropStep2 = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverStep2(false);
    
    const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    if (!files.length) {
      console.warn("No files dropped");
      return;
    }
    
    console.log("Dropped files:", files.map(f => ({ name: f.name, size: f.size, type: f.type })));
    addStep2Images(files);
  };

  const handleImageDragLeaveStep2 = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverStep2(false);
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: newImages,
    });
  };

  const getTotalImageSize = () => {
    if (!formData.images || !Array.isArray(formData.images)) return 0;
    
    // Since images are stored as base64 strings, we need to estimate their size
    // Base64 encoding increases size by ~33%, so we can estimate original size
    return formData.images.reduce((total, image) => {
      if (typeof image === 'string' && image.startsWith('data:image/')) {
        // Estimate original file size from base64 string
        // Remove data URL prefix and calculate approximate original size
        const base64Data = image.split(',')[1];
        if (base64Data) {
          // Base64 to bytes conversion: (base64Length * 3) / 4
          const estimatedSize = (base64Data.length * 3) / 4;
          return total + estimatedSize;
        }
      }
      // If it's a File object (shouldn't happen in current implementation)
      if (image && typeof image.size === 'number') {
        return total + image.size;
      }
      return total;
    }, 0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0 || isNaN(bytes)) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Primary Image Upload Handlers
  const handlePrimaryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must not exceed 5MB");
      return;
    }

    try {
      // Validate that file is a valid File object
      if (!(file instanceof File)) {
        throw new Error("Invalid file object");
      }
      
      // Convert to base64
      const base64Image = await convertFileToBase64(file);
      
      setFormData({
        ...formData,
        image: base64Image,
      });
      setPrimaryImagePreview(URL.createObjectURL(file));
      toast.success("Primary image uploaded successfully!");
    } catch (error) {
      console.error("Error processing primary image:", error);
      toast.error("Error processing image. Please try again.");
    }
  };

  const handlePrimaryImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverPrimaryImage(true);
  };

  const handlePrimaryImageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverPrimaryImage(false);
    
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must not exceed 5MB");
      return;
    }

    try {
      // Validate that file is a valid File object
      if (!(file instanceof File)) {
        throw new Error("Invalid file object");
      }
      
      // Convert to base64
      const base64Image = await convertFileToBase64(file);
      
      setFormData({
        ...formData,
        image: base64Image,
      });
      setPrimaryImagePreview(URL.createObjectURL(file));
      toast.success("Primary image uploaded successfully!");
    } catch (error) {
      console.error("Error processing primary image:", error);
      toast.error("Error processing image. Please try again.");
    }
  };

  const handlePrimaryImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverPrimaryImage(false);
  };

  const removePrimaryImage = () => {
    setFormData({
      ...formData,
      image: null,
    });
    setPrimaryImagePreview(null);
    toast.info("Primary image removed");
  };

  // Category-specific description tips
  const getCategoryDescriptionTips = (categories) => {
    const tips = [];

    categories.forEach((category) => {
      switch (category) {
        case "Electronics & Gadgets":
          tips.push("Include brand, model, specifications, warranty details");
          tips.push("Mention compatibility, features, and technical specs");
          break;
        case "Groceries":
          tips.push(
            "Specify brand, quantity, expiry date, storage instructions"
          );
          tips.push("Include nutritional information if relevant");
          break;
        case "Fashion & Accessories":
          tips.push("Mention sizes available, materials, care instructions");
          tips.push("Include brand, style, color options");
          break;
        case "Real Estate & Housing":
          tips.push("Include location, size, amenities, property type");
          tips.push("Mention utilities, parking, security features");
          break;
        case "Furniture":
          tips.push("Specify dimensions, materials, assembly requirements");
          tips.push("Include style, color, condition, delivery options");
          break;
        case "Beauty, Spas & Wellness":
          tips.push("Mention services included, duration, benefits");
          tips.push("Include any special requirements or preparations");
          break;
        case "Baby & Mother Care":
          tips.push("Specify age range, safety features, materials");
          tips.push("Include care instructions and any certifications");
          break;
        case "Tourism & Travel":
          tips.push("Include destination, duration, inclusions");
          tips.push("Mention accommodation, meals, activities, transport");
          break;
        case "Sports":
          tips.push("Specify sport type, skill level, equipment included");
          tips.push("Mention safety requirements, age restrictions");
          break;
        case "Automotive":
          tips.push("Include make, model, year, condition, mileage");
          tips.push("Mention service history, features, warranty");
          break;
        case "Events and Entertainment":
          tips.push("Specify event type, date, time, venue");
          tips.push("Include age restrictions, dress code, inclusions");
          break;
        case "Restaurants & Eateries":
          tips.push("Mention cuisine type, menu items, dietary options");
          tips.push("Include ambiance, special features, group size limits");
          break;
        case "Stationery":
          tips.push("Specify brand, quantity, paper type, size");
          tips.push("Include quality, durability, intended use");
          break;
        default:
          tips.push("Include key features, benefits, and specifications");
          tips.push("Mention any important details customers should know");
      }
    });

    // Remove duplicates
    return [...new Set(tips)];
  };

  // Universal Properties System Functions
  const addProperty = () => {
    setFormData({
      ...formData,
      properties: [...(formData.properties || []), { key: "", value: "" }],
    });
  };

  const updateProperty = (index, field, value) => {
    const updatedProperties = [...(formData.properties || [])];
    updatedProperties[index] = { ...updatedProperties[index], [field]: value };
    setFormData({
      ...formData,
      properties: updatedProperties,
    });
  };

  const removeProperty = (index) => {
    const updatedProperties = [...(formData.properties || [])];
    updatedProperties.splice(index, 1);
    setFormData({
      ...formData,
      properties: updatedProperties,
    });
  };

  const getSuggestedProperties = (categories) => {
    if (!categories || categories.length === 0) return [];

    const suggestions = new Set();

    categories.forEach((category) => {
      switch (category) {
        case "Electronics & Gadgets":
          suggestions.add(
            "Brand",
            "Model",
            "Storage",
            "Screen Size",
            "Processor",
            "Warranty",
            "Condition"
          );
          break;
        case "Groceries":
          suggestions.add(
            "Brand",
            "Weight",
            "Expiry Date",
            "Origin",
            "Storage",
            "Ingredients"
          );
          break;
        case "Fashion & Accessories":
          suggestions.add(
            "Brand",
            "Size",
            "Color",
            "Material",
            "Style",
            "Care Instructions"
          );
          break;
        case "Real Estate & Housing":
          suggestions.add(
            "Property Type",
            "Location",
            "Bedrooms",
            "Bathrooms",
            "Size",
            "Amenities"
          );
          break;
        case "Furniture":
          suggestions.add(
            "Type",
            "Material",
            "Color",
            "Dimensions",
            "Assembly",
            "Style"
          );
          break;
        case "Beauty, Spas & Wellness":
          suggestions.add(
            "Service Type",
            "Duration",
            "Benefits",
            "Age Restriction",
            "Preparation"
          );
          break;
        case "Baby & Mother Care":
          suggestions.add(
            "Age Range",
            "Material",
            "Safety Features",
            "Care Instructions",
            "Brand"
          );
          break;
        case "Tourism & Travel":
          suggestions.add(
            "Destination",
            "Duration",
            "Accommodation",
            "Meals",
            "Activities"
          );
          break;
        case "Sports":
          suggestions.add(
            "Sport Type",
            "Skill Level",
            "Equipment",
            "Age Restriction",
            "Duration"
          );
          break;
        case "Automotive":
          suggestions.add(
            "Make",
            "Model",
            "Year",
            "Mileage",
            "Fuel Type",
            "Condition"
          );
          break;
        case "Events and Entertainment":
          suggestions.add(
            "Event Type",
            "Date",
            "Time",
            "Venue",
            "Dress Code",
            "Age Restriction"
          );
          break;
        case "Restaurants & Eateries":
          suggestions.add(
            "Cuisine Type",
            "Menu Highlights",
            "Dietary Options",
            "Ambiance",
            "Group Size"
          );
          break;
        case "Stationery":
          suggestions.add(
            "Brand",
            "Type",
            "Quantity",
            "Paper Quality",
            "Intended Use"
          );
          break;
        default:
          suggestions.add("Brand", "Type", "Condition", "Features");
      }
    });

    return Array.from(suggestions);
  };

  const addSuggestedProperty = (propertyName) => {
    // Check if property already exists
    const exists = formData.properties?.some(
      (prop) => prop.key.toLowerCase() === propertyName.toLowerCase()
    );

    if (!exists) {
      addProperty();
      // Set the key for the newly added property
      setTimeout(() => {
        const lastIndex = formData.properties?.length || 0;
        updateProperty(lastIndex, "key", propertyName);
      }, 100);
    }
  };

  const handlePriceValidation = (isValid) => {
    setIsPriceValid(isValid);
  };

  const handleDevAutofill = () => {
    const brandName =
      user?.brand_name ||
      user?.company_name ||
      user?.business_name ||
      user?.account_display_name ||
      user?.name ||
      "Test Vendor";

    const { formData: nextFormData, companionState } = buildDevAutofillState({
      vendorId: vendor_id,
      brandName,
      existingFormData: formData,
    });

    setFormData(nextFormData);
    setStartDate(companionState.startDate);
    setEndDate(companionState.endDate);
    setDaysToExpiry(companionState.daysToExpiry);
    setRedemptionLocationType(companionState.redemptionLocationType);
    setRedemptionLocationTypeStep4(companionState.redemptionLocationTypeStep4);
    setSelectAllLocations(companionState.selectAllLocations);
    setSelectAllRedemptionLocations(companionState.selectAllRedemptionLocations);
    setSelectedBranches(companionState.selectedBranches);
    setSelectedRedemptionBranches(companionState.selectedRedemptionBranches);
    setSelectedPartners(companionState.selectedPartners);
    setSelectedRedemptionPartners(companionState.selectedRedemptionPartners);
    setCurrentStep(companionState.currentStep);
    setStepValidation(companionState.stepValidation);
    setTermsInputType(companionState.termsInputType);
    setIsPriceValid(companionState.isPriceValid);
    setTemplateFields(companionState.templateFields);
    setValidationErrors({});

    toast.info("Dev autofill applied. Add your primary and gallery images, then publish.", {
      autoClose: 5000,
    });
  };

  // Add the Sender ID Modal component

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar page="Create New Deal" />
      <div ref={formContainerRef}>
        {/* {(hasPendingSenderId || senderIds.length === 0) && (
        <SenderIdModal
          showSenderIdModal={showSenderIdModal}
          setShowSenderIdModal={setShowSenderIdModal}
        />
      )} */}

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 py-6 bg-white border-b border-gray-200 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col justify-between items-start space-y-4 sm:flex-row sm:items-center sm:space-y-0">
              <div className="space-y-1">
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2 items-center text-2xl font-bold text-gray-900"
                >
                  <Package className="w-7 h-7 text-[#FF6C6C]" />
                  Create New Deal
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-sm text-gray-500"
                >
                  Create and customize your new deal offering
                </motion.p>
              </div>
              <div className="flex flex-wrap gap-2">
                {isLocalDevEnvironment() && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDevAutofill}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100 transition-all duration-200"
                  >
                    <Wand2 className="w-4 h-4" />
                    Fill test data
                  </motion.button>
                )}
                <BackToOffersButton />
              </div>
            </div>
          </div>
        </motion.div>

        {/* {hasPendingSenderId && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 mx-auto mt-4 max-w-7xl sm:px-6 lg:px-8"
        >
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex gap-3 items-start">
              <div className="shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  Pending Sender ID Status
                </h3>
                <div className="mt-1 text-sm text-amber-700">
                  <p>
                    Your sender ID is currently pending approval. While you can
                    create deals, SMS notifications will only be sent to
                    subscribers once your sender ID is approved. This may affect
                    customer engagement opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
              )} */}

        <DealFormStepper
          steps={steps}
          currentStep={currentStep}
          stepValidation={stepValidation}
          onGoToStep={goToStep}
        />

        {/* Form Section */}
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {/* Step 1: Deal Setup */}
                {currentStep === 1 && (
                  <div className="space-y-8">
                    {/* Deal Title */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <Package className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Deal Setup
                          </h3>
                          <p className="text-sm text-gray-500">
                            Set up your deal title and offer type
                          </p>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <TextField
                          label="Deal Title"
                          value={formData.title}
                          onChange={(e) =>
                            setFormData({ ...formData, title: e.target.value })
                          }
                          variant="outlined"
                          fullWidth
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        />

                        <TextField
                          label="Product Name"
                          placeholder="Enter the product name"
                          value={formData.product_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              product_name: e.target.value,
                            })
                          }
                          error={!!validationErrors.product_name}
                          helperText={validationErrors.product_name}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        />

                        <FormControl fullWidth error={!!validationErrors.productCategory}>
                          <InputLabel
                            id="product-category-label"
                            sx={{
                              color: "rgba(0, 0, 0, 0.6)",
                              "&.Mui-focused": { color: "#FF6C6C" },
                            }}
                          >
                            Product Category
                          </InputLabel>
                          <Select
                            labelId="product-category-label"
                            value={formData.productCategory}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData({
                                ...formData,
                                productCategory: value,
                                product_subcategory: "",
                                sizes: isFashionCategory(value)
                                  ? formData.sizes
                                  : [],
                              });
                            }}
                            label="Product Category"
                            input={
                              <OutlinedInput
                                label="Product Category"
                                sx={{
                                  borderRadius: "12px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#FF6C6C",
                                  },
                                }}
                              />
                            }
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Select category</em>
                            </MenuItem>
                            {marketplaceCategoriesLoading && (
                              <MenuItem disabled value="__loading-categories">
                                <em>Loading categories...</em>
                              </MenuItem>
                            )}
                            {!marketplaceCategoriesLoading && marketplaceCategories.length === 0 && (
                              <MenuItem disabled value="__empty-categories">
                                <em>No categories available</em>
                              </MenuItem>
                            )}
                            {formData.productCategory &&
                              !marketplaceCategories.some((cat) => cat.name === formData.productCategory) && (
                                <MenuItem value={formData.productCategory}>{formData.productCategory}</MenuItem>
                              )}
                            {marketplaceCategories.map((cat) => (
                              <MenuItem key={cat.id ?? cat.name} value={cat.name}>
                                {cat.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {validationErrors.productCategory && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {validationErrors.productCategory}
                            </span>
                          )}
                        </FormControl>

                        <FormControl
                          fullWidth
                          disabled={!formData.productCategory || marketplaceCategoriesLoading}
                          error={!!validationErrors.product_subcategory}
                        >
                          <InputLabel
                            id="product-subcategory-label"
                            sx={{
                              color: "rgba(0, 0, 0, 0.6)",
                              "&.Mui-focused": { color: "#FF6C6C" },
                            }}
                          >
                            Product Subcategory
                          </InputLabel>
                          <Select
                            labelId="product-subcategory-label"
                            value={formData.product_subcategory}
                            onChange={(e) => {
                              const value = e.target.value;
                              const keepSizes = isFashionCategory(
                                formData.productCategory,
                                value
                              );
                              setFormData({
                                ...formData,
                                product_subcategory: value,
                                sizes: keepSizes ? formData.sizes : [],
                              });
                            }}
                            label="Product Subcategory"
                            input={
                              <OutlinedInput
                                label="Product Subcategory"
                                sx={{
                                  borderRadius: "12px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#FF6C6C",
                                  },
                                }}
                              />
                            }
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Select subcategory</em>
                            </MenuItem>
                            {marketplaceCategoriesLoading && (
                              <MenuItem disabled value="__loading-subcategories">
                                <em>Loading subcategories...</em>
                              </MenuItem>
                            )}
                            {!marketplaceCategoriesLoading &&
                              formData.productCategory &&
                              marketplaceSubcategories.length === 0 && (
                                <MenuItem disabled value="__empty-subcategories">
                                  <em>No subcategories</em>
                                </MenuItem>
                              )}
                            {formData.product_subcategory &&
                              !marketplaceSubcategories.some((sub) => sub.name === formData.product_subcategory) && (
                                <MenuItem value={formData.product_subcategory}>
                                  {formData.product_subcategory}
                                </MenuItem>
                              )}
                            {marketplaceSubcategories.map((sub) => (
                              <MenuItem key={sub.id ?? sub.name} value={sub.name}>
                                {sub.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {validationErrors.product_subcategory && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {validationErrors.product_subcategory}
                            </span>
                          )}
                        </FormControl>

                        {/* Sizes — when the category or subcategory is clothing, fashion, or otherwise sized */}
                        <AnimatePresence>
                          {isFashionCategory(
                            formData.productCategory,
                            formData.product_subcategory
                          ) && (
                            <FashionSizesField
                              sizes={formData.sizes}
                              subcategory={formData.product_subcategory}
                              onChange={(sizes) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  sizes,
                                  // Sizes drive total stock; clearing sizes unlocks Quantity Available again
                                  ...(sizes.length > 0
                                    ? { quantity: sumSizeQuantities(sizes) }
                                    : {}),
                                }))
                              }
                            />
                          )}
                        </AnimatePresence>

                        {/* Quantity Available Section */}
                        <QuantitySection
                          lockedFromSizes={
                            isFashionCategory(
                              formData.productCategory,
                              formData.product_subcategory
                            ) &&
                            Array.isArray(formData.sizes) &&
                            formData.sizes.length > 0
                          }
                          onQuantityChange={(value) => {
                            setFormData({
                              ...formData,
                              quantity: value === null ? null : (value || 0),
                            });
                          }}
                          initialQuantity={formData.quantity === null || formData.quantity === undefined ? null : formData.quantity}
                          onValidationChange={(isValid) => {
                          }}
                        />

                        {/* Offer Type Selection */}
                        <div className="space-y-4">
                          <label className="text-sm font-medium text-gray-700">
                            Offer Type
                          </label>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            {/* Discount Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                                formData.offer_type === "discount"
                                  ? "border-[#FF6C6C] bg-[#FF6C6C]/5 shadow-md"
                                  : "border-gray-200 bg-white hover:border-[#FF6C6C]/50 hover:bg-[#FF6C6C]/5"
                              }`}
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  offer_type: "discount",
                                })
                              }
                            >
                              <div className="flex flex-col items-center space-y-2">
                                <div
                                  className={`rounded-full p-3 transition-colors duration-200 ${
                                    formData.offer_type === "discount"
                                      ? "bg-[#FF6C6C] text-white"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  <Percent className="w-6 h-6" />
                                </div>
                                <div className="text-center">
                                  <p
                                    className={`text-sm font-medium ${
                                      formData.offer_type === "discount"
                                        ? "text-[#FF6C6C]"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    Discount %
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Percentage off
                                  </p>
                                </div>
                              </div>
                              {formData.offer_type === "discount" && (
                                <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#FF6C6C] flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </motion.div>

                            {/* BOGO Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                                formData.offer_type === "bogo"
                                  ? "border-green-500 bg-green-50 shadow-md"
                                  : "border-gray-200 bg-white hover:border-green-300 hover:bg-green-50"
                              }`}
                              onClick={() =>
                                setFormData({ ...formData, offer_type: "bogo" })
                              }
                            >
                              <div className="flex flex-col items-center space-y-2">
                                <div
                                  className={`rounded-full p-3 transition-colors duration-200 ${
                                    formData.offer_type === "bogo"
                                      ? "bg-green-500 text-white"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  <Gift className="w-6 h-6" />
                                </div>
                                <div className="text-center">
                                  <p
                                    className={`text-sm font-medium ${
                                      formData.offer_type === "bogo"
                                        ? "text-green-600"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    BOGO
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Buy one get one
                                  </p>
                                </div>
                              </div>
                              {formData.offer_type === "bogo" && (
                                <div className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-green-500 rounded-full">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </motion.div>

                            {/* Bundle Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                                formData.offer_type === "bundle"
                                  ? "border-purple-500 bg-purple-50 shadow-md"
                                  : "border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50"
                              }`}
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  offer_type: "bundle",
                                })
                              }
                            >
                              <div className="flex flex-col items-center space-y-2">
                                <div
                                  className={`rounded-full p-3 transition-colors duration-200 ${
                                    formData.offer_type === "bundle"
                                      ? "bg-purple-500 text-white"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  <Package className="w-6 h-6" />
                                </div>
                                <div className="text-center">
                                  <p
                                    className={`text-sm font-medium ${
                                      formData.offer_type === "bundle"
                                        ? "text-purple-600"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    Bundle
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Package deal
                                  </p>
                                </div>
                              </div>
                              {formData.offer_type === "bundle" && (
                                <div className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-purple-500 rounded-full">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </motion.div>
                          </div>
                        </div>

                        {/* Conditional Pricing Fields */}
                        {formData.offer_type === "discount" && (
                          <PricingSection
                            onPriceChange={(field, value) => {
                              if (field === "price") {
                                setFormData({
                                  ...formData,
                                  discount_original_price: value,
                                });
                              } else if (field === "discounted_price") {
                                setFormData({
                                  ...formData,
                                  discount_sale_price: value,
                                });
                              }
                            }}
                            initialPrice={formData.discount_original_price}
                            initialDiscountedPrice={
                              formData.discount_sale_price
                            }
                            onValidationChange={handlePriceValidation}
                          />
                        )}

                        {formData.offer_type === "bundle" && (
                          <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                            <h4 className="mb-4 text-lg font-semibold text-purple-900">
                              Bundle Pricing
                            </h4>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              <TextField
                                label="Original Sum"
                                type="number"
                                value={formData.original_sum}
                                onChange={(e) => {
                                  const originalSum = e.target.value;
                                  const bundlePrice = formData.bundle_price;
                                  const savings =
                                    originalSum && bundlePrice
                                      ? (
                                          parseFloat(originalSum) -
                                          parseFloat(bundlePrice)
                                        ).toFixed(2)
                                      : "";
                                  setFormData({
                                    ...formData,
                                    original_sum: originalSum,
                                    bundle_savings: savings,
                                  });
                                }}
                                variant="outlined"
                                fullWidth
                                required
                                InputProps={{
                                  startAdornment: (
                                    <span className="text-gray-500">GH₵</span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                              <TextField
                                label="Bundle Price"
                                type="number"
                                value={formData.bundle_price}
                                onChange={(e) => {
                                  const bundlePrice = e.target.value;
                                  const originalSum = formData.original_sum;
                                  const savings =
                                    originalSum && bundlePrice
                                      ? (
                                          parseFloat(originalSum) -
                                          parseFloat(bundlePrice)
                                        ).toFixed(2)
                                      : "";
                                  setFormData({
                                    ...formData,
                                    bundle_price: bundlePrice,
                                    bundle_savings: savings,
                                  });
                                }}
                                variant="outlined"
                                fullWidth
                                required
                                InputProps={{
                                  startAdornment: (
                                    <span className="text-gray-500">GH₵</span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                              <TextField
                                label="Savings"
                                type="number"
                                value={formData.bundle_savings}
                                variant="outlined"
                                fullWidth
                                readOnly
                                required
                                InputProps={{
                                  startAdornment: (
                                    <span className="text-gray-500">GH₵</span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {formData.offer_type === "bogo" && (
                          <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <h4 className="mb-4 text-lg font-semibold text-green-900">
                              Buy One Get One Free
                            </h4>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              <TextField
                                label="Item Price"
                                type="number"
                                value={formData.bogo_item_price}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    bogo_item_price: e.target.value,
                                  })
                                }
                                variant="outlined"
                                fullWidth
                                required
                                InputProps={{
                                  startAdornment: (
                                    <span className="text-gray-500">GH₵</span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                              <TextField
                                label="Buy Quantity"
                                type="number"
                                value={formData.bogo_quantity}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    bogo_quantity: e.target.value,
                                  })
                                }
                                variant="outlined"
                                fullWidth
                                required
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                              <TextField
                                label="Get Free Quantity"
                                type="number"
                                value={formData.bogo_free_quantity}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    bogo_free_quantity: e.target.value,
                                  })
                                }
                                variant="outlined"
                                fullWidth
                                required
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FF6C6C",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FF6C6C",
                                  },
                                }}
                              />
                            </div>
                            {formData.bogo_item_price &&
                              formData.bogo_quantity &&
                              formData.bogo_free_quantity && (
                                <div className="p-3 mt-4 bg-white rounded-lg">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">
                                      Total Items:
                                    </span>
                                    <span className="font-semibold text-green-600">
                                      {parseInt(formData.bogo_quantity) +
                                        parseInt(
                                          formData.bogo_free_quantity
                                        )}{" "}
                                      items
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">
                                      Total Value:
                                    </span>
                                    <span className="font-semibold text-green-600">
                                      GH₵
                                      {(
                                        (parseInt(formData.bogo_quantity) +
                                          parseInt(
                                            formData.bogo_free_quantity
                                          )) *
                                        parseFloat(formData.bogo_item_price)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">
                                      Customer Pays:
                                    </span>
                                    <span className="font-semibold text-blue-600">
                                      GH₵
                                      {(
                                        parseInt(formData.bogo_quantity) *
                                        parseFloat(formData.bogo_item_price)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">
                                      Savings:
                                    </span>
                                    <span className="font-semibold text-red-600">
                                      GH₵
                                      {(
                                        parseInt(formData.bogo_free_quantity) *
                                        parseFloat(formData.bogo_item_price)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        {/* Deal Validity Period Section */}
                        <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                          <div className="flex gap-3 items-center mb-6">
                            <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                              <Calendar className="w-6 h-6 text-[#FF6C6C]" />
                            </div>
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900">
                                Deal Validity Period
                              </h3>
                              <p className="text-sm text-gray-500">
                                Set the validity period for your deal
                              </p>
                            </div>
                          </div>

                          <div className="space-y-6">
                            {/* Validity Type Selection */}
                            <div className="space-y-4">
                              <label className="text-sm font-medium text-gray-700">
                                Validity Type
                              </label>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {/* Regular Validity */}
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                                    formData.validity_type === "regular"
                                      ? "border-[#FF6C6C] bg-[#FF6C6C]/5 shadow-md"
                                      : "border-gray-200 bg-white hover:border-[#FF6C6C]/50 hover:bg-[#FF6C6C]/5"
                                  }`}
                                  onClick={() =>
                                    setFormData({
                                      ...formData,
                                      validity_type: "regular",
                                    })
                                  }
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`rounded-full p-2 transition-colors duration-200 ${
                                        formData.validity_type === "regular"
                                          ? "bg-[#FF6C6C] text-white"
                                          : "bg-gray-100 text-gray-600"
                                      }`}
                                    >
                                      <Calendar className="w-5 h-5" />
                                    </div>
                                    <div>
                                      <p
                                        className={`text-sm font-medium ${
                                          formData.validity_type === "regular"
                                            ? "text-[#FF6C6C]"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Standard Deal
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Set start and end dates
                                      </p>
                                    </div>
                                  </div>
                                  {formData.validity_type === "regular" && (
                                    <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#FF6C6C] flex items-center justify-center">
                                      <Check className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </motion.div>

                                {/* Flash Sale */}
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                                    formData.validity_type === "flash_sale"
                                      ? "border-orange-500 bg-orange-50 shadow-md"
                                      : "border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50"
                                  }`}
                                  onClick={() => {
                                    const today = new Date()
                                      .toISOString()
                                      .split("T")[0];
                                    const defaultTime = `${today}T09:00`;
                                    const startDate = new Date(defaultTime);
                                    const endDate = new Date(
                                      startDate.getTime() + 2 * 60 * 60 * 1000
                                    ); // 2 hours default

                                    setFormData({
                                      ...formData,
                                      validity_type: "flash_sale",
                                      flash_start_time: defaultTime,
                                      flash_start_date: today,
                                      flash_duration: 2,
                                      flash_end_time: endDate
                                        .toISOString()
                                        .slice(0, 16),
                                    });
                                  }}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`rounded-full p-2 transition-colors duration-200 ${
                                        formData.validity_type === "flash_sale"
                                          ? "bg-orange-500 text-white"
                                          : "bg-gray-100 text-gray-600"
                                      }`}
                                    >
                                      <Clock className="w-5 h-5" />
                                    </div>
                                    <div>
                                      <p
                                        className={`text-sm font-medium ${
                                          formData.validity_type ===
                                          "flash_sale"
                                            ? "text-orange-600"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Flash Sale
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Time-limited offer
                                      </p>
                                    </div>
                                  </div>
                                  {formData.validity_type === "flash_sale" && (
                                    <div className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-orange-500 rounded-full">
                                      <Check className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </motion.div>
                              </div>
                            </div>

                            {/* Conditional Validity Fields */}
                            {formData.validity_type === "regular" && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="space-y-4"
                              >
                                <TextField
                                  label="Start Date"
                                  type="date"
                                  value={startDate}
                                  name="start_date"
                                  InputLabelProps={{ shrink: true }}
                                  variant="outlined"
                                  fullWidth
                                  readOnly
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "12px",
                                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                                      "& fieldset": { borderColor: "#FF6C6C" },
                                    },
                                    "& .MuiInputLabel-root": {
                                      color: "#FF6C6C",
                                    },
                                  }}
                                />

                                <TextField
                                  label="End Date"
                                  type="date"
                                  value={endDate}
                                  name="expiry_date"
                                  onChange={(e) => {
                                    setEndDate(e.target.value);
                                    setFormData({
                                      ...formData,
                                      expiry_date: e.target.value,
                                    });
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{
                                    min: startDate,
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  required
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "12px",
                                      "&:hover fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#FF6C6C",
                                    },
                                  }}
                                />

                                {/* Days Calculation Preview */}
                                {calculateDays() !== null && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-4 mt-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
                                  >
                                    <div className="flex justify-between items-center">
                                      <div className="flex gap-3 items-center">
                                        <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-purple-500/10">
                                          <Calendar className="w-6 h-6 text-purple-500" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">
                                            Deal Validity
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            Total days for your Deal Validity
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-2xl font-bold text-purple-500">
                                          {calculateDays()}
                                        </span>
                                        <p className="text-xs text-gray-500">
                                          Days
                                        </p>
                                      </div>
                                    </div>
                                    <div className="pt-3 mt-3 border-t border-purple-100">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">
                                          Deal Validity Period:
                                        </span>
                                        <span className="font-medium text-purple-600">
                                          {new Date(
                                            startDate
                                          ).toLocaleDateString()}{" "}
                                          -{" "}
                                          {new Date(
                                            endDate
                                          ).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </motion.div>
                            )}

                            {formData.validity_type === "flash_sale" && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="space-y-4"
                              >
                                <div className="p-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border border-orange-200">
                                  <div className="flex gap-3 items-center mb-6">
                                    <div className="p-2 bg-orange-500 rounded-lg">
                                      <Clock className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-orange-900">
                                        Flash Sale Setup
                                      </h4>
                                      <p className="text-sm text-orange-700">
                                        Choose when your flash sale starts and
                                        how long it lasts
                                      </p>
                                    </div>
                                  </div>

                                  <div className="space-y-6">
                                    {/* Start Date & Time */}
                                    <div>
                                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                          <label className="block mb-3 text-sm font-medium text-orange-800">
                                            When does your flash sale start?
                                          </label>
                                          <TextField
                                            label="Start Date"
                                            type="date"
                                            value={
                                              formData.flash_start_date ||
                                              new Date()
                                                .toISOString()
                                                .split("T")[0]
                                            }
                                            onChange={(e) => {
                                              const startDate = e.target.value;
                                              const startTime =
                                                formData.flash_start_time ||
                                                "09:00";
                                              setFormData({
                                                ...formData,
                                                flash_start_date: startDate,
                                                flash_start_time: `${startDate}T${startTime}`,
                                              });
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            fullWidth
                                            required
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: "12px",
                                                "&:hover fieldset": {
                                                  borderColor: "#FF6C6C",
                                                },
                                                "&.Mui-focused fieldset": {
                                                  borderColor: "#FF6C6C",
                                                },
                                              },
                                              "& .MuiInputLabel-root.Mui-focused":
                                                {
                                                  color: "#FF6C6C",
                                                },
                                            }}
                                          />
                                        </div>
                                        <div className="relative w-full">
                                          <label className="block mb-3 text-sm font-medium text-orange-800">
                                            Start Time
                                          </label>
                                          <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                            {/* Hour Selector */}
                                            <div className="space-y-1">
                                              <label className="text-xs text-gray-500 truncate">
                                                Hour
                                              </label>
                                              <select
                                                value={
                                                  formData.flash_start_time
                                                    ? formData.flash_start_time
                                                        .split("T")[1]
                                                        ?.split(":")[0]
                                                    : "09"
                                                }
                                                onChange={(e) => {
                                                  const startDate =
                                                    formData.flash_start_date ||
                                                    new Date()
                                                      .toISOString()
                                                      .split("T")[0];
                                                  const currentMinute =
                                                    formData.flash_start_time
                                                      ? formData.flash_start_time
                                                          .split("T")[1]
                                                          ?.split(":")[1]
                                                      : "00";
                                                  const newTime = `${e.target.value.padStart(
                                                    2,
                                                    "0"
                                                  )}:${currentMinute}`;
                                                  setFormData({
                                                    ...formData,
                                                    flash_start_time: `${startDate}T${newTime}`,
                                                  });
                                                }}
                                                className="p-2 w-full text-sm text-center bg-white rounded-lg border border-orange-200 transition-all duration-200 sm:p-3 sm:text-base focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                                              >
                                                {Array.from(
                                                  { length: 24 },
                                                  (_, i) => (
                                                    <option
                                                      key={i}
                                                      value={i
                                                        .toString()
                                                        .padStart(2, "0")}
                                                    >
                                                      {i
                                                        .toString()
                                                        .padStart(2, "0")}
                                                    </option>
                                                  )
                                                )}
                                              </select>
                                            </div>

                                            {/* Minute Selector */}
                                            <div className="space-y-1">
                                              <label className="text-xs text-gray-500 truncate">
                                                Minute
                                              </label>
                                              <select
                                                value={
                                                  formData.flash_start_time
                                                    ? formData.flash_start_time
                                                        .split("T")[1]
                                                        ?.split(":")[1]
                                                    : "00"
                                                }
                                                onChange={(e) => {
                                                  const startDate =
                                                    formData.flash_start_date ||
                                                    new Date()
                                                      .toISOString()
                                                      .split("T")[0];
                                                  const currentHour =
                                                    formData.flash_start_time
                                                      ? formData.flash_start_time
                                                          .split("T")[1]
                                                          ?.split(":")[0]
                                                      : "09";
                                                  const newTime = `${currentHour}:${e.target.value.padStart(
                                                    2,
                                                    "0"
                                                  )}`;
                                                  setFormData({
                                                    ...formData,
                                                    flash_start_time: `${startDate}T${newTime}`,
                                                  });
                                                }}
                                                className="p-2 w-full text-sm text-center bg-white rounded-lg border border-orange-200 transition-all duration-200 sm:p-3 sm:text-base focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                                              >
                                                {Array.from(
                                                  { length: 60 },
                                                  (_, i) => (
                                                    <option
                                                      key={i}
                                                      value={i
                                                        .toString()
                                                        .padStart(2, "0")}
                                                    >
                                                      {i
                                                        .toString()
                                                        .padStart(2, "0")}
                                                    </option>
                                                  )
                                                )}
                                              </select>
                                            </div>

                                            {/* Quick Time Presets */}
                                            <div className="space-y-1">
                                              <label className="text-xs text-gray-500 truncate">
                                                Quick
                                              </label>
                                              <div className="grid grid-cols-1 gap-1">
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const startDate =
                                                      formData.flash_start_date ||
                                                      new Date()
                                                        .toISOString()
                                                        .split("T")[0];
                                                    setFormData({
                                                      ...formData,
                                                      flash_start_time: `${startDate}T09:00`,
                                                    });
                                                  }}
                                                  className="px-1 py-1 text-xs text-orange-700 bg-orange-100 rounded transition-colors duration-200 sm:px-2 hover:bg-orange-200"
                                                >
                                                  9 AM
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const startDate =
                                                      formData.flash_start_date ||
                                                      new Date()
                                                        .toISOString()
                                                        .split("T")[0];
                                                    setFormData({
                                                      ...formData,
                                                      flash_start_time: `${startDate}T12:00`,
                                                    });
                                                  }}
                                                  className="px-1 py-1 text-xs text-orange-700 bg-orange-100 rounded transition-colors duration-200 sm:px-2 hover:bg-orange-200"
                                                >
                                                  12 PM
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const startDate =
                                                      formData.flash_start_date ||
                                                      new Date()
                                                        .toISOString()
                                                        .split("T")[0];
                                                    setFormData({
                                                      ...formData,
                                                      flash_start_time: `${startDate}T18:00`,
                                                    });
                                                  }}
                                                  className="px-1 py-1 text-xs text-orange-700 bg-orange-100 rounded transition-colors duration-200 sm:px-2 hover:bg-orange-200"
                                                >
                                                  6 PM
                                                </button>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Current Selection Display */}
                                          <div className="p-3 mt-3 bg-orange-50 rounded-lg border border-orange-200">
                                            <div className="flex flex-col gap-2 items-start sm:flex-row sm:items-center">
                                              <Clock className="w-4 h-4 text-orange-600 shrink-0" />
                                              <span className="text-sm font-medium text-orange-800 break-words">
                                                Flash sale starts at:{" "}
                                                {formData.flash_start_time
                                                  ? new Date(
                                                      formData.flash_start_time
                                                    ).toLocaleTimeString(
                                                      "en-US",
                                                      {
                                                        hour: "numeric",
                                                        minute: "2-digit",
                                                        hour12: true,
                                                      }
                                                    )
                                                  : "Not set"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Duration Selection */}
                                    <div>
                                      <label className="block mb-3 text-sm font-medium text-orange-800">
                                        How long should your flash sale last?
                                      </label>
                                      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                                        {[
                                          {
                                            label: "2 Hours",
                                            value: 2,
                                            icon: "⚡",
                                          },
                                          {
                                            label: "6 Hours",
                                            value: 6,
                                            icon: "🔥",
                                          },
                                          {
                                            label: "12 Hours",
                                            value: 12,
                                            icon: "💥",
                                          },
                                          {
                                            label: "24 Hours",
                                            value: 24,
                                            icon: "🎯",
                                          },
                                        ].map((option) => (
                                          <motion.div
                                            key={option.value}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className={`cursor-pointer p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 ${
                                              formData.flash_duration ===
                                              option.value
                                                ? "border-orange-500 bg-orange-100 shadow-md"
                                                : "border-orange-200 bg-white hover:border-orange-300 hover:bg-orange-50"
                                            }`}
                                            onClick={() => {
                                              const startDateTime =
                                                formData.flash_start_time ||
                                                `${
                                                  new Date()
                                                    .toISOString()
                                                    .split("T")[0]
                                                }T09:00`;
                                              const startDate = new Date(
                                                startDateTime
                                              );

                                              // Validate start date
                                              if (isNaN(startDate.getTime())) {
                                                // Fallback to current date/time if invalid
                                                const now = new Date();
                                                const fallbackStart = new Date(
                                                  now.getFullYear(),
                                                  now.getMonth(),
                                                  now.getDate(),
                                                  9,
                                                  0,
                                                  0
                                                );
                                                const endDate = new Date(
                                                  fallbackStart.getTime() +
                                                    option.value *
                                                      60 *
                                                      60 *
                                                      1000
                                                );

                                                setFormData({
                                                  ...formData,
                                                  flash_duration: option.value,
                                                  flash_start_time:
                                                    fallbackStart
                                                      .toISOString()
                                                      .slice(0, 16),
                                                  flash_end_time: endDate
                                                    .toISOString()
                                                    .slice(0, 16),
                                                });
                                              } else {
                                                const endDate = new Date(
                                                  startDate.getTime() +
                                                    option.value *
                                                      60 *
                                                      60 *
                                                      1000
                                                );

                                                setFormData({
                                                  ...formData,
                                                  flash_duration: option.value,
                                                  flash_end_time: endDate
                                                    .toISOString()
                                                    .slice(0, 16),
                                                });
                                              }
                                            }}
                                          >
                                            <div className="text-center">
                                              <div className="mb-1 text-xl sm:mb-2 sm:text-2xl">
                                                {option.icon}
                                              </div>
                                              <div
                                                className={`text-xs sm:text-sm font-medium ${
                                                  formData.flash_duration ===
                                                  option.value
                                                    ? "text-orange-700"
                                                    : "text-gray-700"
                                                }`}
                                              >
                                                {option.label}
                                              </div>
                                            </div>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Flash Sale Preview */}
                                    {formData.flash_start_time &&
                                      formData.flash_end_time &&
                                      (() => {
                                        const startDate = new Date(
                                          formData.flash_start_time
                                        );
                                        const endDate = new Date(
                                          formData.flash_end_time
                                        );

                                        // Only show preview if dates are valid
                                        if (
                                          !isNaN(startDate.getTime()) &&
                                          !isNaN(endDate.getTime())
                                        ) {
                                          return (
                                            <motion.div
                                              initial={{ opacity: 0, y: 10 }}
                                              animate={{ opacity: 1, y: 0 }}
                                              className="p-4 bg-white rounded-xl border border-orange-200"
                                            >
                                              <div className="flex gap-3 items-center mb-3">
                                                <div className="p-2 bg-orange-500 rounded-lg">
                                                  <Clock className="w-5 h-5 text-white" />
                                                </div>
                                                <h5 className="font-semibold text-orange-900">
                                                  Flash Sale Preview
                                                </h5>
                                              </div>
                                              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                                <div className="flex justify-between">
                                                  <span className="text-sm text-gray-600">
                                                    Starts:
                                                  </span>
                                                  <span className="text-sm font-medium text-orange-700">
                                                    {startDate.toLocaleString()}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-sm text-gray-600">
                                                    Ends:
                                                  </span>
                                                  <span className="text-sm font-medium text-orange-700">
                                                    {endDate.toLocaleString()}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between md:col-span-2">
                                                  <span className="text-sm text-gray-600">
                                                    Duration:
                                                  </span>
                                                  <span className="text-sm font-medium text-orange-700">
                                                    {formData.flash_duration}{" "}
                                                    hours
                                                  </span>
                                                </div>
                                              </div>
                                            </motion.div>
                                          );
                                        }
                                        return null;
                                      })()}

                                    <div className="p-3 bg-orange-100 rounded-lg">
                                      <p className="text-sm text-orange-800">
                                        💡 <strong>Pro Tip:</strong> Flash sales
                                        work best with 2-6 hour durations.
                                        Shorter durations create more urgency!
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 1 Navigation */}
                    <div className="flex justify-between items-center p-6 bg-white rounded-xl border border-gray-100">
                      <div className="flex gap-2 items-center text-sm text-gray-500">
                        <Package className="w-4 h-4" />
                        Step 1 of 5
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={nextStep}
                        disabled={!stepValidation[1]}
                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                          stepValidation[1]
                            ? "bg-[#FF6C6C] text-white hover:bg-[#FF8F8F] shadow-lg shadow-[#FF6C6C]/20"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        Continue to Supporting Details
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Step 2: Supporting Details */}
                {currentStep === 2 && (
                  <div className="space-y-8">
                    {/* Primary Hero Image Upload */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="relative p-4 sm:p-6 md:p-8 bg-gradient-to-br from-[#FF8F8F]/10 via-[#FFB3B3]/10 to-[#FFD6D6]/10 rounded-2xl border-2 border-[#FF8F8F]/30 shadow-lg overflow-hidden"
                    >
                      {/* Decorative background elements */}
                      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF8F8F]/20 to-[#FFB3B3]/20 rounded-full blur-3xl -z-0"></div>
                      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#FF6C6C]/20 to-[#FF8F8F]/20 rounded-full blur-3xl -z-0"></div>

                      <div className="relative z-10">
                        <div className="flex gap-2 items-center mb-4 sm:gap-3 sm:mb-6">
                          <div className="p-2 sm:p-3 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-xl shadow-lg">
                            <CloudUpload className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                          </div>
                          <div>
                            <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] bg-clip-text text-transparent">
                              Primary Image
                            </h3>
                            <p className="text-xs text-gray-600 sm:text-sm">
                              Upload your main product image - this will be the first thing customers see!
                            </p>
                          </div>
                        </div>

                        {/* Upload Area or Preview */}
                        {!primaryImagePreview ? (
                          <div
                            className={`relative border-3 border-dashed rounded-xl sm:rounded-2xl p-6 sm:p-10 md:p-12 text-center transition-all duration-300 cursor-pointer ${
                              isDragOverPrimaryImage
                                ? "border-[#FF8F8F] bg-[#FF8F8F]/10 scale-[1.02] shadow-xl"
                                : "border-[#FF8F8F]/40 bg-white/80 hover:border-[#FF8F8F] hover:bg-[#FF8F8F]/5 hover:shadow-md"
                            }`}
                            onDrop={handlePrimaryImageDrop}
                            onDragOver={handlePrimaryImageDragOver}
                            onDragLeave={handlePrimaryImageDragLeave}
                            onClick={() => document.getElementById("primary-image-upload").click()}
                          >
                            <motion.div
                              animate={
                                isDragOverPrimaryImage
                                  ? { scale: 1.1, rotate: 5 }
                                  : { scale: 1, rotate: 0 }
                              }
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="flex justify-center mb-3 sm:mb-4">
                                <div
                                  className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                                    isDragOverPrimaryImage
                                      ? "bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] shadow-2xl"
                                      : "bg-gradient-to-br from-[#FF8F8F] to-[#FFB3B3] shadow-lg"
                                  }`}
                                >
                                  <CloudUpload
                                    className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white transition-all duration-300 ${
                                      isDragOverPrimaryImage ? "scale-110" : ""}`}
                                  />
                                </div>
                              </div>
                              <h4 className="mb-2 text-lg font-bold text-gray-800 sm:mb-3 sm:text-xl md:text-2xl">
                                {isDragOverPrimaryImage
                                  ? "Drop your image here!"
                                  : "Upload Your Hero Image"}
                              </h4>
                              <p className="px-2 mb-4 text-sm text-gray-600 sm:mb-6 sm:text-base">
                                {isDragOverPrimaryImage
                                  ? "Release to upload your stunning image"
                                  : "Drag and drop your image here, or click to browse"}
                              </p>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePrimaryImageUpload}
                                className="hidden"
                                id="primary-image-upload"
                              />
                              <div className="inline-flex items-center gap-2 sm:gap-3 px-4 py-2 sm:px-6 md:px-8 sm:py-3 md:py-4 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl hover:from-[#FF5A5A] hover:to-[#FF7A7A] transition-all duration-300 transform hover:scale-105">
                                <CloudUpload className="w-4 h-4 sm:w-5 sm:h-5" />
                                Choose Image
                              </div>
                              <div className="flex flex-wrap gap-2 justify-center px-2 mt-4 text-xs text-gray-500 sm:mt-6 sm:gap-4">
                                <span className="flex gap-1 items-center">
                                  <Check className="w-3 h-3 text-green-500 sm:w-4 sm:h-4" />
                                  Max 5MB
                                </span>
                                <span className="flex gap-1 items-center">
                                  <Check className="w-3 h-3 text-green-500 sm:w-4 sm:h-4" />
                                  JPG, PNG, WEBP
                                </span>
                                <span className="flex gap-1 items-center">
                                  <Check className="w-3 h-3 text-green-500 sm:w-4 sm:h-4" />
                                  Best: 720x480px
                                </span>
                              </div>
                            </motion.div>
                          </div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group"
                          >
                            <div className="overflow-hidden relative rounded-xl border-2 border-white shadow-2xl sm:rounded-2xl sm:border-4">
                              <img
                                src={primaryImagePreview}
                                alt="Primary product"
                                className="w-full h-[250px] sm:h-[350px] md:h-[400px] object-cover"
                              />
                              {/* Overlay on hover */}
                              <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 from-black/60 via-black/20 group-hover:opacity-100">
                                <div className="absolute right-0 bottom-0 left-0 p-3 sm:p-4 md:p-6">
                                  <div className="flex flex-col gap-2 justify-between items-center sm:flex-row sm:gap-0">
                                    <div className="flex gap-2 items-center text-white">
                                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                      <span className="text-sm font-semibold sm:text-base">Primary Image Set</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => document.getElementById("primary-image-upload").click()}
                                        className="flex items-center gap-1 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/90 text-[#FF6C6C] rounded-lg text-sm sm:text-base font-medium hover:bg-white transition-all duration-200 transform hover:scale-105"
                                      >
                                        <CloudUpload className="w-3 h-3 sm:w-4 sm:h-4" />
                                        Replace
                                      </button>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePrimaryImageUpload}
                                        className="hidden"
                                        id="primary-image-upload"
                                      />
                                      <button
                                        type="button"
                                        onClick={removePrimaryImage}
                                        className="flex items-center gap-1 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-red-500/90 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-red-600 transition-all duration-200 transform hover:scale-105"
                                      >
                                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Image info */}
                            <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 px-3 sm:px-4 py-2 sm:py-3 bg-white/80 backdrop-blur-sm rounded-xl border border-[#FF8F8F]/30">
                              <div className="flex gap-2 items-center text-xs text-gray-600 sm:text-sm">
                                <CheckCircle className="w-3 h-3 text-green-500 sm:w-4 sm:h-4" />
                                <span className="font-medium truncate max-w-[200px] sm:max-w-none">
                                  {formData.image?.name || "Primary image"}
                                </span>
                              </div>
                              <div className="text-xs sm:text-sm font-semibold text-[#FF6C6C]">
                                {formData.image ? formatFileSize(formData.image.size) : ""}
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Pro Tips */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-[#FF8F8F]/10 to-[#FFB3B3]/10 rounded-xl border border-[#FF8F8F]/30"
                        >
                          <div className="flex gap-2 items-start sm:gap-3">
                            <div className="shrink-0 p-1.5 sm:p-2 bg-[#FF6C6C] rounded-lg">
                              <Zap className="w-3 h-3 text-white sm:w-4 sm:h-4" />
                            </div>
                            <div className="flex-1">
                              <h4 className="mb-1.5 sm:mb-2 text-xs sm:text-sm font-bold text-[#FF6C6C]">
                                Pro Tips for Amazing Hero Images:
                              </h4>
                              <ul className="space-y-0.5 sm:space-y-1 text-[10px] sm:text-xs text-gray-700">
                                <li className="flex gap-2 items-start">
                                  <span className="text-[#FF6C6C] mt-0.5">•</span>
                                  <span>Use high-quality, well-lit images that showcase your product clearly</span>
                                </li>
                                <li className="flex gap-2 items-start">
                                  <span className="text-[#FF6C6C] mt-0.5">•</span>
                                  <span>Include the product in use or lifestyle context for better engagement</span>
                                </li>
                                <li className="flex gap-2 items-start">
                                  <span className="text-[#FF6C6C] mt-0.5">•</span>
                                  <span>Avoid cluttered backgrounds - keep the focus on your product</span>
                                </li>
                                <li className="flex gap-2 items-start">
                                  <span className="text-[#FF6C6C] mt-0.5">•</span>
                                  <span>This image will be optimized automatically for best performance</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Product Images */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="relative p-4 sm:p-6 md:p-8 bg-gradient-to-br from-[#FF8F8F]/10 via-[#FFB3B3]/10 to-[#FFD6D6]/10 rounded-2xl border-2 border-[#FF8F8F]/30 shadow-lg overflow-hidden"
                    >
                      {/* Decorative background elements */}
                      <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-[#FFB3B3]/20 to-[#FF8F8F]/20 rounded-full blur-3xl -z-0"></div>
                      <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-tr from-[#FF8F8F]/20 to-[#FF6C6C]/20 rounded-full blur-3xl -z-0"></div>

                      <div className="relative z-10">
                        <div className="flex gap-2 items-center mb-4 sm:gap-3 sm:mb-6">
                          <div className="p-2 sm:p-3 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-xl shadow-lg">
                            <CloudUpload className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                          </div>
                          <div>
                            <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] bg-clip-text text-transparent">
                              Product Images Gallery
                            </h3>
                            <p className="text-xs text-gray-600 sm:text-sm">
                              Upload up to 5 additional images (max 10MB total)
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4 sm:space-y-6">
                          {/* Image Upload Area */}
                          <div
                            className={`border-3 border-dashed rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center transition-all duration-300 cursor-pointer ${
                              isDragOverStep2
                                ? "border-[#FF8F8F] bg-[#FF8F8F]/10 scale-[1.02] shadow-xl"
                                : "border-[#FF8F8F]/40 bg-white/80 hover:border-[#FF8F8F] hover:bg-[#FF8F8F]/5 hover:shadow-md"
                            }`}
                            onDrop={handleImageDropStep2}
                            onDragOver={handleImageDragOverStep2}
                            onDragLeave={handleImageDragLeaveStep2}
                          >
                            <div className="space-y-3 sm:space-y-4">
                              <div className="flex justify-center">
                                <div
                                  className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                                    isDragOverStep2
                                      ? "bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] shadow-2xl"
                                      : "bg-gradient-to-br from-[#FF8F8F] to-[#FFB3B3] shadow-lg"
                                  }`}
                                >
                                  <CloudUpload
                                    className={`w-6 h-6 sm:w-8 sm:h-8 text-white transition-all duration-300 ${
                                      isDragOverStep2 ? "scale-110" : ""}`}
                                  />
                                </div>
                              </div>
                              <div>
                                <h4 className="mb-2 text-base font-bold text-gray-800 sm:text-lg">
                                  {isDragOverStep2
                                    ? "Drop images here!"
                                    : "Upload Gallery Images"}
                                </h4>
                                <p className="px-2 mb-3 text-xs text-gray-600 sm:mb-4 sm:text-sm">
                                  {isDragOverStep2
                                    ? "Release to upload your images"
                                    : "Drag and drop multiple images here, or click to browse"}
                                </p>
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                  className="hidden"
                                  id="image-upload"
                                />
                                <label
                                  htmlFor="image-upload"
                                  className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold cursor-pointer shadow-lg hover:shadow-xl hover:from-[#FF5A5A] hover:to-[#FF7A7A] transition-all duration-300 transform hover:scale-105"
                                >
                                  <CloudUpload className="w-4 h-4 sm:w-5 sm:h-5" />
                                  Choose Images
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Image Preview Grid */}
                          {formData.images && formData.images.length > 0 && (
                            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
                              {formData.images.map((image, index) => (
                                <motion.div 
                                  key={index} 
                                  className="relative group"
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                >
                                  <div className="overflow-hidden rounded-lg border-2 border-[#FF8F8F]/30 aspect-square shadow-md hover:shadow-lg transition-shadow">
                                    <img
                                      src={
                                        typeof image === "string"
                                          ? image
                                          : image instanceof File || image instanceof Blob
                                          ? URL.createObjectURL(image)
                                          : ""
                                      }
                                      alt={`Product ${index + 1}`}
                                      className="object-cover w-full h-full"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="flex absolute -top-2 -right-2 justify-center items-center w-6 h-6 text-white bg-red-500 rounded-full shadow-lg opacity-0 transition-all duration-200 sm:w-7 sm:h-7 group-hover:opacity-100 hover:bg-red-600 hover:scale-110"
                                  >
                                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </button>
                                </motion.div>
                              ))}
                            </div>
                          )}

                          {/* Upload Status */}
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 px-3 sm:px-4 py-2 sm:py-3 bg-white/80 backdrop-blur-sm rounded-xl border border-[#FF8F8F]/30">
                            <span className="text-xs font-medium text-gray-600 sm:text-sm">
                              {formData.images ? formData.images.length : 0}/5 images uploaded
                            </span>
                            <span
                              className={`text-xs sm:text-sm font-semibold ${
                                getTotalImageSize() > 10 * 1024 * 1024
                                  ? "text-red-500"
                                  : "text-[#FF6C6C]"
                              }`}
                            >
                              {formatFileSize(getTotalImageSize())}/10MB
                            </span>
                          </div>

                          {getTotalImageSize() > 10 * 1024 * 1024 && (
                            <motion.div 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-3 bg-red-50 rounded-xl border border-red-200 sm:p-4"
                            >
                              <div className="flex gap-2 items-start">
                                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-red-700 sm:text-sm">
                                  Total file size exceeds 10MB limit. Please remove some images.
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </motion.div>

                    {/* Product Details */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <FileText className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Product Features
                          </h3>
                          <p className="text-sm text-gray-500">
                            Fill in the specific features for your product
                          </p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        {/* Category-specific description prompts */}
                        {formData.category && formData.category.length > 0 && (
                          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                            <h4 className="mb-2 text-sm font-medium text-blue-900">
                              💡 Tips for {formData.category.join(", ")}:
                            </h4>
                            <div className="space-y-1 text-sm text-blue-700">
                              {getCategoryDescriptionTips(
                                formData.category
                              ).map((tip, index) => (
                                <div key={index}>• {tip}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Universal Properties System */}
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <label className="block text-sm font-medium text-gray-700">
                              Product Features
                            </label>
                            <button
                              type="button"
                              onClick={addProperty}
                              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-lg hover:bg-[#FF6C6C]/20 transition-colors duration-200"
                            >
                              <span className="text-lg">+</span>
                              Add Feature
                            </button>
                          </div>

                          {/* Properties List */}
                          <div className="space-y-3">
                            {formData.properties &&
                            formData.properties.length > 0 ? (
                              formData.properties.map((property, index) => (
                                <div
                                  key={index}
                                  className="flex gap-3 items-center"
                                >
                                  <div className="flex-1">
                                    <TextField
                                      label="Feature"
                                      variant="outlined"
                                      fullWidth
                                      value={property.key}
                                      onChange={(e) =>
                                        updateProperty(
                                          index,
                                          "key",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., Brand, Size, Color, Material"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "12px",
                                          "&:hover fieldset": {
                                            borderColor: "#FF6C6C",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#FF6C6C",
                                          },
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                          color: "#FF6C6C",
                                        },
                                      }}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <TextField
                                      label="Detail"
                                      variant="outlined"
                                      fullWidth
                                      value={property.value}
                                      onChange={(e) =>
                                        updateProperty(
                                          index,
                                          "value",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., Samsung, Large, Black, Cotton"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "12px",
                                          "&:hover fieldset": {
                                            borderColor: "#FF6C6C",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#FF6C6C",
                                          },
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                          color: "#FF6C6C",
                                        },
                                      }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeProperty(index)}
                                    className="flex justify-center items-center w-10 h-10 text-red-500 bg-red-50 rounded-lg transition-colors duration-200 hover:bg-red-100"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div className="p-6 text-center rounded-xl border-2 border-gray-300 border-dashed">
                                <div className="space-y-2">
                                  <div className="p-3 mx-auto bg-gray-100 rounded-full w-fit">
                                    <FileText className="w-6 h-6 text-gray-400" />
                                  </div>
                                  <p className="text-sm text-gray-500">
                                    No properties added yet. Click "Add
                                    Property" to get started.
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    Examples: Brand, Size, Color, Material,
                                    Condition, etc.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Quick Add Suggestions */}
                          {formData.category &&
                            formData.category.length > 0 && (
                              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                                <h4 className="mb-3 text-sm font-medium text-blue-900">
                                  💡 Suggested properties for{" "}
                                  {formData.category.join(", ")}:
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {getSuggestedProperties(
                                    formData.category
                                  ).map((suggestion, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() =>
                                        addSuggestedProperty(suggestion)
                                      }
                                      className="px-3 py-1 text-xs text-blue-700 bg-blue-100 rounded-full transition-colors duration-200 hover:bg-blue-200"
                                    >
                                      + {suggestion}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>

                        {/* SKU Section */}
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <label className="block text-sm font-medium text-gray-700">
                              Product SKU
                              <span className="ml-1 text-red-500">*</span>
                            </label>
                          </div>

                          {formData.offer_type === "bundle" ? (
                            /* Bundle SKU Management */
                            <div className="space-y-4">
                              <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-600">
                                  Add SKUs for each item in your bundle package
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      bundle_skus: [
                                        ...formData.bundle_skus,
                                        { product_name: "", sku: "" },
                                      ],
                                    });
                                  }}
                                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-lg hover:bg-[#FF6C6C]/20 transition-colors duration-200"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add Item
                                </button>
                              </div>

                              {/* Bundle SKU List */}
                              <div className="space-y-3">
                                {formData.bundle_skus &&
                                formData.bundle_skus.length > 0 ? (
                                  formData.bundle_skus.map((item, index) => (
                                    <div
                                      key={index}
                                      className="flex flex-col gap-3 items-start p-4 bg-gray-50 rounded-xl border border-gray-200 md:flex-row md:items-center"
                                    >
                                      <div className="flex-1 w-full">
                                        <TextField
                                          label="Product Name"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          value={item.product_name}
                                          onChange={(e) => {
                                            const newBundleSkus = [
                                              ...formData.bundle_skus,
                                            ];
                                            newBundleSkus[index].product_name =
                                              e.target.value;
                                            setFormData({
                                              ...formData,
                                              bundle_skus: newBundleSkus,
                                            });
                                          }}
                                          placeholder="e.g., Samsung Galaxy S23"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "12px",
                                              "&:hover fieldset": {
                                                borderColor: "#FF6C6C",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#FF6C6C",
                                              },
                                            },
                                            "& .MuiInputLabel-root.Mui-focused":
                                              {
                                                color: "#FF6C6C",
                                              },
                                          }}
                                        />
                                      </div>
                                      <div className="flex-1 w-full">
                                        <TextField
                                          label="SKU"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          value={item.sku}
                                          onChange={(e) => {
                                            const newBundleSkus = [
                                              ...formData.bundle_skus,
                                            ];
                                            newBundleSkus[index].sku =
                                              e.target.value;
                                            setFormData({
                                              ...formData,
                                              bundle_skus: newBundleSkus,
                                            });
                                          }}
                                          placeholder="e.g., SAMS23-128GB-BLK"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "12px",
                                              "&:hover fieldset": {
                                                borderColor: "#FF6C6C",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#FF6C6C",
                                              },
                                            },
                                            "& .MuiInputLabel-root.Mui-focused":
                                              {
                                                color: "#FF6C6C",
                                              },
                                          }}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newBundleSkus =
                                            formData.bundle_skus.filter(
                                              (_, i) => i !== index
                                            );
                                          setFormData({
                                            ...formData,
                                            bundle_skus: newBundleSkus,
                                          });
                                        }}
                                        className="flex justify-center items-center w-10 h-10 text-red-500 bg-red-50 rounded-lg transition-colors duration-200 hover:bg-red-100"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-6 text-center rounded-xl border-2 border-gray-300 border-dashed">
                                    <div className="space-y-2">
                                      <div className="p-3 mx-auto bg-gray-100 rounded-full w-fit">
                                        <Package className="w-6 h-6 text-gray-400" />
                                      </div>
                                      <p className="text-sm text-gray-500">
                                        No bundle items added yet. Click "Add
                                        Item" to get started.
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        Add each product and its corresponding
                                        SKU in your bundle.
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            /* Single SKU Input for Non-Bundle Offers */
                            <TextField
                              label="Product SKU"
                              variant="outlined"
                              fullWidth
                              value={formData.sku}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sku: e.target.value,
                                })
                              }
                              placeholder="e.g., SAMS23-128GB-BLK"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px",
                                  "&:hover fieldset": {
                                    borderColor: "#FF6C6C",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#FF6C6C",
                                  },
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                  color: "#FF6C6C",
                                },
                              }}
                            />
                          )}
                        </div>

                        {/* Additional Notes */}
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Additional Notes
                          </label>
                          <TextField
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.additional_notes || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                additional_notes: e.target.value,
                              })
                            }
                            placeholder="Any additional information, special instructions, or important details customers should know..."
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                "&:hover fieldset": { borderColor: "#FF6C6C" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#FF6C6C",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FF6C6C",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Deal Details */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <h3 className="mb-6 text-lg font-semibold text-gray-900">
                        Deal Details
                      </h3>
                      <div className="space-y-6">
                        <TextField
                          label="Brand Phone Number"
                          variant="outlined"
                          fullWidth
                          value={formData.phone_number}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/\D/g, "");
                            value = value.slice(0, 9);
                            setFormData({ ...formData, phone_number: value });
                          }}
                          error={!!validationErrors.phone_number}
                          helperText={
                            validationErrors.phone_number ||
                            "Format: 233XXXXXXXXX"
                          }
                          required
                          InputProps={{
                            startAdornment: (
                              <span className="text-gray-500">233</span>
                            ),
                          }}
                          placeholder=""
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        />

                        <div className="space-y-4">
                          {/* Information Section */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-blue-50 rounded-xl border border-blue-200"
                          >
                            <div className="flex gap-3 items-start">
                              <div className="shrink-0">
                                <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
                              </div>
                              <div className="flex-1">
                                <h4 className="mb-1 text-sm font-medium text-blue-900">
                                  SMS Notification Details
                                </h4>
                                <p className="text-sm text-blue-700">
                                  This message will be attached to automated SMS
                                  notifications sent to subscribers of the
                                  application who are potential customers. Keep
                                  it concise and engaging to attract customers
                                  to your deal.
                                </p>
                              </div>
                            </div>
                          </motion.div>

                          {/* SMS Content Input */}
                          <div className="relative">
                            <TextField
                              label="SMS Content"
                              variant="outlined"
                              fullWidth
                              multiline
                              rows={3}
                              value={formData.sms_content}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow any length input, we'll handle truncation in onPaste
                                setFormData({
                                  ...formData,
                                  sms_content: value,
                                });
                              }}
                              onPaste={(e) => {
                                e.preventDefault();
                                const pastedText =
                                  e.clipboardData.getData("text");
                                const currentText = formData.sms_content || "";
                                const combinedText = currentText + pastedText;

                                if (combinedText.length > 80) {
                                  // Truncate intelligently at word boundaries
                                  const truncated = combinedText
                                    .substring(0, 80)
                                    .trim();
                                  const lastSpaceIndex =
                                    truncated.lastIndexOf(" ");
                                  const finalText =
                                    lastSpaceIndex > 0
                                      ? truncated.substring(0, lastSpaceIndex)
                                      : truncated;

                                  setFormData({
                                    ...formData,
                                    sms_content: finalText,
                                  });

                                  // Show toast notification
                                  toast.info(
                                    `Content truncated to ${finalText.length} characters. Original paste was ${combinedText.length} characters.`
                                  );
                                } else {
                                  setFormData({
                                    ...formData,
                                    sms_content: combinedText,
                                  });
                                }
                              }}
                              placeholder="E.g. 50% OFF on all items! Limited time offer. Visit us today!"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px",
                                  "&:hover fieldset": {
                                    borderColor: "#FF6C6C",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#FF6C6C",
                                  },
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                  color: "#FF6C6C",
                                },
                              }}
                            />
                            <div className="flex justify-between items-center px-2 mt-2">
                              <span className="text-xs text-gray-500">
                                Custom message for potential customers
                              </span>
                              <div className="flex gap-2 items-center">
                                {formData.sms_content &&
                                  formData.sms_content.length > 80 && (
                                    <div className="flex gap-1 items-center px-2 py-1 bg-amber-50 rounded-full border border-amber-200">
                                      <AlertTriangle className="w-3 h-3 text-amber-600" />
                                      <span className="text-xs font-medium text-amber-700">
                                        Truncated
                                      </span>
                                    </div>
                                  )}
                                <span
                                  className={`text-xs font-medium ${
                                    formData.sms_content.length > 80
                                      ? "text-red-500"
                                      : formData.sms_content.length > 70
                                      ? "text-amber-500"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {formData.sms_content.length}/80
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 Navigation */}
                    <div className="flex flex-col gap-4 justify-between items-center p-6 bg-white rounded-xl border border-gray-100 sm:flex-row">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={prevStep}
                        className="px-6 py-3 w-full font-medium text-gray-600 bg-gray-100 rounded-xl transition-all duration-200 sm:w-auto hover:bg-gray-200"
                      >
                        <span className="hidden sm:inline">Back to Basic Info</span>
                        <span className="sm:hidden">Back</span>
                      </motion.button>
                      <div className="flex gap-2 items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span className="hidden sm:inline">Step 2 of 5</span>
                        <span className="sm:hidden">2/3</span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={nextStep}
                        disabled={!stepValidation[2]}
                        className={`w-full sm:w-auto px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                          stepValidation[2]
                            ? "bg-[#FF6C6C] text-white hover:bg-[#FF8F8F] shadow-lg shadow-[#FF6C6C]/20"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <span className="hidden sm:inline">Continue to Targeting</span>
                        <span className="sm:hidden">Next</span>
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Step 3: Targeting Criteria */}
                {currentStep === 3 && (
                  <div className="space-y-8">
                    {/* Category Selection */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <Target className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Category Selection
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select the categories for your deal
                          </p>
                        </div>
                      </div>

                      <FormControl fullWidth>
                        <InputLabel
                          id="category-label"
                          sx={{
                            color: "rgba(0, 0, 0, 0.6)",
                            "&.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        >
                          Categories
                        </InputLabel>
                        <Select
                          labelId="category-label"
                          multiple
                          value={formData.category}
                          onChange={handleCategoryChange}
                          input={
                            <OutlinedInput
                              label="Categories"
                              sx={{
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#FF6C6C",
                                  },
                              }}
                            />
                          }
                          renderValue={(selected) => selected.join(", ")}
                          sx={{
                            borderRadius: "12px",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#FF6C6C",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#FF6C6C",
                            },
                          }}
                        >
                          {marketplaceCategoriesLoading && (
                            <MenuItem disabled value="__loading-category-list">
                              Loading categories...
                            </MenuItem>
                          )}
                          {(Array.isArray(formData.category) ? formData.category : [])
                            .filter((name) => !marketplaceCategories.some((cat) => cat.name === name))
                            .map((name) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                          {marketplaceCategories.map((category) => (
                            <MenuItem key={category.id ?? category.name} value={category.name}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {/* Gender Selection */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <Target className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Gender Targeting
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select the target gender for your deal
                          </p>
                        </div>
                      </div>

                      <FormControl fullWidth>
                        <InputLabel
                          id="gender-label"
                          sx={{
                            color: "rgba(0, 0, 0, 0.6)",
                            "&.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        >
                          Gender
                        </InputLabel>
                        <Select
                          labelId="gender-label"
                          value={
                            formData.gender.length > 0 ? formData.gender[0] : ""
                          }
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            setFormData({
                              ...formData,
                              gender: [selectedValue],
                            });
                          }}
                          input={
                            <OutlinedInput
                              label="Gender"
                              sx={{
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#FF6C6C",
                                  },
                              }}
                            />
                          }
                          sx={{
                            borderRadius: "12px",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#FF6C6C",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#FF6C6C",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Gender</em>
                          </MenuItem>
                          <MenuItem value="All Genders">All Genders</MenuItem>
                          {["Male", "Female"].map((gender) => (
                            <MenuItem key={gender} value={gender}>
                              {gender}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {/* Age Range Selection */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <Target className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Age Range Targeting
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select the target age range for your deal
                          </p>
                        </div>
                      </div>

                      {/* No Age Limit Checkbox */}
                      <div className="mb-4">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.no_age_limit}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  no_age_limit: e.target.checked,
                                  min_age: e.target.checked
                                    ? ""
                                    : formData.min_age,
                                  max_age: e.target.checked
                                    ? ""
                                    : formData.max_age,
                                });
                              }}
                              sx={{
                                color: "#FF6C6C",
                                "&.Mui-checked": {
                                  color: "#FF6C6C",
                                },
                              }}
                            />
                          }
                          label={
                            <span className="font-medium text-gray-700">
                              No Age Limit (Available to all ages)
                            </span>
                          }
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <FormControl fullWidth disabled={formData.no_age_limit}>
                          <InputLabel
                            id="min-age-label"
                            sx={{
                              color: "rgba(0, 0, 0, 0.6)",
                              "&.Mui-focused": {
                                color: "#FF6C6C",
                              },
                            }}
                          >
                            Minimum Age
                          </InputLabel>
                          <Select
                            labelId="min-age-label"
                            value={formData.min_age || ""}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                min_age: e.target.value,
                              });
                            }}
                            input={
                              <OutlinedInput
                                label="Minimum Age"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#FF6C6C",
                                    },
                                }}
                              />
                            }
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#FF6C6C",
                                },
                            }}
                          >
                            <MenuItem value="">
                              <em>No minimum</em>
                            </MenuItem>
                            {Array.from({ length: 100 }, (_, i) => i + 1).map(
                              (age) => (
                                <MenuItem key={age} value={age}>
                                  {age} years
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth disabled={formData.no_age_limit}>
                          <InputLabel
                            id="max-age-label"
                            sx={{
                              color: "rgba(0, 0, 0, 0.6)",
                              "&.Mui-focused": {
                                color: "#FF6C6C",
                              },
                            }}
                          >
                            Maximum Age
                          </InputLabel>
                          <Select
                            labelId="max-age-label"
                            value={formData.max_age || ""}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                max_age: e.target.value,
                              });
                            }}
                            input={
                              <OutlinedInput
                                label="Maximum Age"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#FF6C6C",
                                    },
                                }}
                              />
                            }
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#FF6C6C",
                                },
                            }}
                          >
                            <MenuItem value="">
                              <em>No maximum</em>
                            </MenuItem>
                            {Array.from({ length: 100 }, (_, i) => i + 1).map(
                              (age) => (
                                <MenuItem key={age} value={age}>
                                  {age} years
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </div>
                    </div>

                    {/* Location Selection */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <MapPin className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Location Targeting
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select where your deal can be redeemed
                          </p>
                        </div>
                      </div>

                      {/* Redemption Location Type Selector */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
                      >
                        <h3 className="mb-6 text-lg font-semibold text-gray-900">
                          Target Locations/Branches
                        </h3>
                        <div className="space-y-4">
                          <RadioGroup
                            value={redemptionLocationType}
                            onChange={(e) =>
                              setRedemptionLocationType(e.target.value)
                            }
                            className="space-y-4"
                          >
                            {/* City/Region Based Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() =>
                                setRedemptionLocationType("region")
                              }
                              className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 group
                                ${
                                  redemptionLocationType === "region"
                                    ? "border-[#FF6C6C] bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 shadow-lg shadow-[#FF6C6C]/20"
                                    : "border-gray-200 hover:border-[#FF6C6C]/40 hover:bg-gray-50/50"
                                }`}
                            >
                              <div className="flex gap-4 items-start">
                                <div
                                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200
                                  ${
                                    redemptionLocationType === "region"
                                      ? "border-[#FF6C6C] bg-[#FF6C6C]"
                                      : "border-gray-300 group-hover:border-[#FF6C6C]/60"
                                  }`}
                                >
                                  {redemptionLocationType === "region" && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>

                                <div className="flex-1">
                                  <div className="flex gap-3 items-center mb-2">
                                    <div
                                      className={`p-3 rounded-xl transition-all duration-200
                                      ${
                                        redemptionLocationType === "region"
                                          ? "bg-[#FF6C6C] text-white"
                                          : "bg-[#FF6C6C]/10 text-[#FF6C6C] group-hover:bg-[#FF6C6C]/20"
                                      }`}
                                    >
                                      <MapPin className="w-6 h-6" />
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        City/Region Based
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        Select specific regions and locations
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 items-center text-xs text-gray-500">
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                      <span>Nationwide option</span>
                                    </div>
                                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                      <span>Custom locations</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>

                            {/* Branch Based Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() =>
                                setRedemptionLocationType("branch")
                              }
                              className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 group
                                ${
                                  redemptionLocationType === "branch"
                                    ? "border-[#FF6C6C] bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 shadow-lg shadow-[#FF6C6C]/20"
                                    : "border-gray-200 hover:border-[#FF6C6C]/40 hover:bg-gray-50/50"
                                }`}
                            >
                              <div className="flex gap-4 items-start">
                                <div
                                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200
                                  ${
                                    redemptionLocationType === "branch"
                                      ? "border-[#FF6C6C] bg-[#FF6C6C]"
                                      : "border-gray-300 group-hover:border-[#FF6C6C]/60"
                                  }`}
                                >
                                  {redemptionLocationType === "branch" && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>

                                <div className="flex-1">
                                  <div className="flex gap-3 items-center mb-2">
                                    <div
                                      className={`p-3 rounded-xl transition-all duration-200
                                      ${
                                        redemptionLocationType === "branch"
                                          ? "bg-[#FF6C6C] text-white"
                                          : "bg-[#FF6C6C]/10 text-[#FF6C6C] group-hover:bg-[#FF6C6C]/20"
                                      }`}
                                    >
                                      <Building2 className="w-6 h-6" />
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Branch Based
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        Select from your existing branches
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 items-center text-xs text-gray-500">
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                      <span>Manage branches</span>
                                    </div>
                                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                      <span>Search & filter</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>

                            {/* Partner Based Option */}
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() =>
                                setRedemptionLocationType("partner")
                              }
                              className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 group
                                ${
                                  redemptionLocationType === "partner"
                                    ? "border-[#FF6C6C] bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 shadow-lg shadow-[#FF6C6C]/20"
                                    : "border-gray-200 hover:border-[#FF6C6C]/40 hover:bg-gray-50/50"
                                }`}
                            >
                              <div className="flex gap-4 items-start">
                                <div
                                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200
                                  ${
                                    redemptionLocationType === "partner"
                                      ? "border-[#FF6C6C] bg-[#FF6C6C]"
                                      : "border-gray-300 group-hover:border-[#FF6C6C]/60"
                                  }`}
                                >
                                  {redemptionLocationType === "partner" && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>

                                <div className="flex-1">
                                  <div className="flex gap-3 items-center mb-2">
                                    <div
                                      className={`p-3 rounded-xl transition-all duration-200
                                      ${
                                        redemptionLocationType === "partner"
                                          ? "bg-[#FF6C6C] text-white"
                                          : "bg-[#FF6C6C]/10 text-[#FF6C6C] group-hover:bg-[#FF6C6C]/20"
                                      }`}
                                    >
                                      <Building2 className="w-6 h-6" />
                                    </div>
                                    <div>
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Partner Based
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        Select specific partner branches
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 items-center text-xs text-gray-500">
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                                      <span>Partner network</span>
                                    </div>
                                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                                    <div className="flex gap-1 items-center">
                                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                                      <span>Branch selection</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          </RadioGroup>
                        </div>
                      </motion.div>

                      {/* Region Location Section - Only show if City/Region Based is selected */}
                      {redemptionLocationType === "region" && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-8"
                        >
                          <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
                            <div className="flex gap-3 items-center">
                              <div className="p-2 bg-[#FF6C6C]/10 rounded-xl">
                                <Navigation className="w-6 h-6 text-[#FF6C6C]" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Location Details
                                </h3>
                                <p className="text-sm text-gray-500">
                                  Add up to 5 locations for your deal
                                </p>
                              </div>
                            </div>

                            {/* All Locations Checkbox */}
                            <div className="flex flex-col gap-2 items-start w-full sm:flex-row sm:items-center sm:gap-4 sm:w-auto">
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent form submission
                                  setSelectAllLocations(!selectAllLocations);
                                  if (!selectAllLocations) {
                                    setFormData({
                                      ...formData,
                                      region_location: [
                                        { region: "all", location: "all" },
                                      ],
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      region_location: [
                                        { region: "", location: "" },
                                      ],
                                    });
                                  }
                                }}
                                className={`
                            flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl
                            transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start
                            ${
                              selectAllLocations
                                ? "bg-[#FF6C6C] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }
                          `}
                              >
                                <div
                                  className={`
                            w-4 h-4 border-2 rounded flex items-center justify-center
                            ${
                              selectAllLocations
                                ? "border-white"
                                : "border-gray-400"
                            }
                          `}
                                >
                                  {selectAllLocations && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                Nationwide
                              </motion.button>

                              {!selectAllLocations && (
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent form submission
                                    addRegionLocation();
                                  }}
                                  disabled={
                                    formData.region_location.length >= 5
                                  }
                                  className={`
                              flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl
                              transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start
                              ${
                                formData.region_location.length >= 5
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-[#FF6C6C]/10 text-[#FF6C6C] hover:bg-[#FF6C6C]/20"
                              }
                            `}
                                >
                                  <PlusCircle className="w-4 h-4" />
                                  Add
                                </motion.button>
                              )}
                            </div>
                          </div>

                          <AnimatePresence mode="popLayout">
                            {!selectAllLocations &&
                              formData.region_location.map((item, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, x: -100 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 500,
                                    damping: 30,
                                  }}
                                  className="relative p-4 sm:p-6 mb-4 bg-gray-50/50 rounded-xl border border-gray-100 
                            hover:border-[#FF6C6C]/30 hover:shadow-lg hover:shadow-[#FF6C6C]/5
                            transition-all duration-300 group"
                                >
                                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                                    {/* Region Select */}
                                    <div className="relative">
                                      <div
                                        className={`
                                relative rounded-xl border transition-all duration-200
                                ${
                                  item.region
                                    ? "border-[#FF6C6C]/30"
                                    : "border-gray-200"
                                }
                                group-hover:border-[#FF6C6C]/50
                              `}
                                      >
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                          <Navigation
                                            className={`w-5 h-5 ${
                                              item.region
                                                ? "text-[#FF6C6C]"
                                                : "text-gray-400"
                                            }`}
                                          />
                                        </div>
                                        <select
                                          value={item.region}
                                          onChange={(e) => {
                                            const newRegionLocation = [
                                              ...formData.region_location,
                                            ];
                                            newRegionLocation[index] = {
                                              ...newRegionLocation[index],
                                              region: e.target.value,
                                              location: "",
                                            };
                                            setFormData({
                                              ...formData,
                                              region_location:
                                                newRegionLocation,
                                            });
                                          }}
                                          className="py-3 pr-10 pl-12 w-full text-sm bg-transparent rounded-xl transition-colors appearance-none cursor-pointer outline-none hover:bg-gray-50/50 sm:text-base"
                                        >
                                          <option
                                            value=""
                                            className="text-gray-400"
                                          >
                                            Select Region
                                          </option>
                                          {regionOptions.map((region) => (
                                            <option
                                              key={region}
                                              value={region}
                                              className="text-gray-700"
                                            >
                                              {region}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                          <motion.div
                                            animate={{
                                              rotate: item.region ? 180 : 0,
                                            }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                          </motion.div>
                                        </div>
                                      </div>
                                      <motion.label
                                        className={`absolute -top-2.5 left-4 px-2 text-sm bg-gray-50/50
                                  ${
                                    item.region
                                      ? "text-[#FF6C6C]"
                                      : "text-gray-500"
                                  }`}
                                      >
                                        Region
                                      </motion.label>
                                    </div>

                                    {/* Location Select */}
                                    <div className="relative space-y-2">
                                      <div
                                        className={`
                                relative rounded-xl border transition-all duration-200
                                ${
                                  item.location
                                    ? "border-[#FF6C6C]/30"
                                    : "border-gray-200"
                                }
                                group-hover:border-[#FF6C6C]/50
                                ${!item.region ? "opacity-50" : ""}
                              `}
                                      >
                                        <div className="absolute left-4 top-1/2 z-10 -translate-y-1/2">
                                          <MapPin
                                            className={`w-5 h-5 ${
                                              item.location
                                                ? "text-[#FF6C6C]"
                                                : "text-gray-400"
                                            }`}
                                          />
                                        </div>

                                        {/* Custom Dropdown */}
                                        <div className="relative location-dropdown">
                                          <input
                                            type="text"
                                            value={
                                              locationSearchTerms[index] !==
                                              undefined
                                                ? locationSearchTerms[index]
                                                : item.location &&
                                                  item.location !== "other"
                                                ? item.location
                                                : ""
                                            }
                                            onChange={(e) => {
                                              handleLocationSearch(
                                                index,
                                                e.target.value
                                              );
                                              if (
                                                !locationDropdownOpen[index]
                                              ) {
                                                toggleLocationDropdown(index);
                                              }
                                            }}
                                            onFocus={() => {
                                              if (item.region) {
                                                // Set the current location as search term when focusing to allow editing
                                                if (
                                                  item.location &&
                                                  item.location !== "other" &&
                                                  locationSearchTerms[index] ===
                                                    undefined
                                                ) {
                                                  setLocationSearchTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: item.location,
                                                    })
                                                  );
                                                }
                                                toggleLocationDropdown(index);
                                              }
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === "Escape") {
                                                setLocationDropdownOpen(
                                                  (prev) => ({
                                                    ...prev,
                                                    [index]: false,
                                                  })
                                                );
                                              }
                                            }}
                                            placeholder="Select or search location"
                                            disabled={!item.region}
                                            className="py-3 pr-10 pl-12 w-full text-sm bg-transparent rounded-xl transition-colors outline-none hover:bg-gray-50/50 disabled:cursor-not-allowed sm:text-base"
                                          />

                                          {/* Search/Dropdown/Clear Icons */}
                                          <div className="flex absolute right-4 top-1/2 gap-2 items-center -translate-y-1/2">
                                            {/* Clear button - show when there's a selection or search term */}
                                            {(item.location ||
                                              locationSearchTerms[index]) && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  // Clear both selection and search term
                                                  const newRegionLocation = [
                                                    ...formData.region_location,
                                                  ];
                                                  newRegionLocation[index] = {
                                                    ...newRegionLocation[index],
                                                    location: "",
                                                    customLocation: undefined,
                                                  };
                                                  setFormData({
                                                    ...formData,
                                                    region_location:
                                                      newRegionLocation,
                                                  });
                                                  setLocationSearchTerms(
                                                    (prev) => {
                                                      const newSearchTerms = {
                                                        ...prev,
                                                      };
                                                      delete newSearchTerms[
                                                        index
                                                      ];
                                                      return newSearchTerms;
                                                    }
                                                  );
                                                  setLocationDropdownOpen(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: false,
                                                    })
                                                  );
                                                }}
                                                className="p-1 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            )}

                                            {/* Search/Dropdown Icon */}
                                            <div className="pointer-events-none">
                                              {locationDropdownOpen[index] ? (
                                                <Search className="w-4 h-4 text-gray-400" />
                                              ) : (
                                                <motion.div
                                                  animate={{
                                                    rotate:
                                                      locationDropdownOpen[
                                                        index
                                                      ]
                                                        ? 180
                                                        : 0,
                                                  }}
                                                  transition={{ duration: 0.2 }}
                                                >
                                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                                </motion.div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Dropdown Options */}
                                          <AnimatePresence>
                                            {locationDropdownOpen[index] &&
                                              item.region && (
                                                <motion.div
                                                  initial={{
                                                    opacity: 0,
                                                    y: -10,
                                                  }}
                                                  animate={{ opacity: 1, y: 0 }}
                                                  exit={{ opacity: 0, y: -10 }}
                                                  transition={{ duration: 0.2 }}
                                                  className="overflow-y-auto absolute right-0 left-0 top-full z-50 mt-1 max-h-48 bg-white rounded-xl border border-gray-200 shadow-lg"
                                                >
                                                  {getFilteredLocations(
                                                    item.region,
                                                    locationSearchTerms[index]
                                                  ).length === 0 &&
                                                  locationSearchTerms[index] ? (
                                                    <>
                                                      <div className="px-4 py-3 text-sm text-gray-500">
                                                        No locations found
                                                      </div>
                                                      <div
                                                        onClick={() =>
                                                          handleLocationSelect(
                                                            index,
                                                            "other"
                                                          )
                                                        }
                                                        className="px-4 py-3 text-sm text-gray-700 border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                                                      >
                                                        Other (Custom location)
                                                      </div>
                                                    </>
                                                  ) : (
                                                    <>
                                                      {!locationSearchTerms[
                                                        index
                                                      ] && (
                                                        <div
                                                          onClick={() =>
                                                            handleLocationSelect(
                                                              index,
                                                              ""
                                                            )
                                                          }
                                                          className="px-4 py-3 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
                                                        >
                                                          Select Location
                                                        </div>
                                                      )}
                                                      {getFilteredLocations(
                                                        item.region,
                                                        locationSearchTerms[
                                                          index
                                                        ]
                                                      ).map((location) => (
                                                        <div
                                                          key={location}
                                                          onClick={() =>
                                                            handleLocationSelect(
                                                              index,
                                                              location
                                                            )
                                                          }
                                                          className="flex gap-2 items-center px-4 py-3 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                        >
                                                          <MapPin className="w-4 h-4 text-[#FF6C6C]" />
                                                          {location}
                                                        </div>
                                                      ))}
                                                      <div
                                                        onClick={() =>
                                                          handleLocationSelect(
                                                            index,
                                                            "other"
                                                          )
                                                        }
                                                        className="px-4 py-3 text-sm text-gray-700 border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                                                      >
                                                        Other (Custom location)
                                                      </div>
                                                    </>
                                                  )}
                                                </motion.div>
                                              )}
                                          </AnimatePresence>
                                        </div>
                                      </div>
                                      <motion.label
                                        className={`absolute -top-4 left-4 px-2 text-sm bg-gray-50/50
                                  ${
                                    item.location
                                      ? "text-[#FF6C6C]"
                                      : "text-gray-500"
                                  }`}
                                      >
                                        Location
                                      </motion.label>

                                      {/* Custom Location Input Field */}
                                      {item.location === "other" && (
                                        <div className="relative rounded-xl border px-4 py-1 border-[#FF6C6C]/30 group-hover:border-[#FF6C6C]/50">
                                          <TextField
                                            value={item.customLocation || ""}
                                            onChange={(e) => {
                                              const newRegionLocation = [
                                                ...formData.region_location,
                                              ];
                                              newRegionLocation[index] = {
                                                ...newRegionLocation[index],
                                                location: "other",
                                                customLocation: e.target.value,
                                              };
                                              setFormData({
                                                ...formData,
                                                region_location:
                                                  newRegionLocation,
                                              });
                                            }}
                                            placeholder="Enter custom location"
                                            className="px-4 py-3 w-full"
                                            disabled={!item.region}
                                            variant="standard"
                                            InputProps={{
                                              disableUnderline: true,
                                            }}
                                            sx={{
                                              "& input": {
                                                padding: "0",
                                                height: "40px",
                                              },
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Remove Location Button */}
                                  {formData.region_location.length > 1 && (
                                    <motion.button
                                      initial={{ opacity: 0, scale: 0 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={(e) => {
                                        e.preventDefault(); // Prevent form submission
                                        removeRegionLocation(index);
                                      }}
                                      className="absolute -top-3 -right-3 p-2 text-white bg-[#FF6C6C] rounded-full 
                                shadow-lg hover:bg-[#FF8F8F] transition-colors duration-200
                                opacity-0 group-hover:opacity-100 sm:opacity-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </motion.button>
                                  )}

                                  {/* Location Number Badge */}
                                  <motion.div
                                    className="absolute -left-3 top-1/2 -translate-y-1/2 flex items-center justify-center 
                              w-6 h-6 text-xs font-medium text-white bg-[#FF6C6C] rounded-full"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 500,
                                    }}
                                  >
                                    {index + 1}
                                  </motion.div>
                                </motion.div>
                              ))}
                          </AnimatePresence>
                        </motion.div>
                      )}

                      {/* Branch Based Location Section - Only show if Branch Based is selected */}
                      {redemptionLocationType === "branch" && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-8"
                        >
                          <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
                            <div className="flex gap-3 items-center">
                              <div className="p-2 bg-[#FF6C6C]/10 rounded-xl">
                                <Building2 className="w-6 h-6 text-[#FF6C6C]" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Select Branches
                                </h3>
                                <p className="text-sm text-gray-500">
                                  Choose from your existing branches or add new
                                  ones
                                </p>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setShowBranchModal(true)}
                              className="flex items-center gap-2 px-4 py-2 bg-[#FF6C6C] text-white rounded-xl font-medium hover:bg-[#FF8F8F] transition-all duration-200"
                            >
                              <Plus className="w-4 h-4" />
                              Manage Branches
                            </motion.button>
                          </div>

                          {isLoadingBranches ? (
                            <div className="flex justify-center items-center p-8">
                              <CircularProgress
                                size={40}
                                sx={{ color: "#FF6C6C" }}
                              />
                            </div>
                          ) : branches.length === 0 ? (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200"
                            >
                              <div className="flex flex-col gap-4 items-center">
                                <div className="p-4 bg-gray-100 rounded-full">
                                  <Building2 className="w-8 h-8 text-gray-400" />
                                </div>
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900">
                                    No Branches Found
                                  </h4>
                                  <p className="mt-1 text-gray-500">
                                    You need to add branches before using this
                                    feature
                                  </p>
                                </div>
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => setShowBranchModal(true)}
                                  className="flex items-center gap-2 px-4 py-2 bg-[#FF6C6C] text-white rounded-xl font-medium hover:bg-[#FF8F8F] transition-all duration-200"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add Your First Branch
                                </motion.button>
                              </div>
                            </motion.div>
                          ) : (
                            <div className="space-y-4">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                                <input
                                  type="text"
                                  placeholder="Search branches..."
                                  value={branchSearchTerm}
                                  onChange={(e) =>
                                    setBranchSearchTerm(e.target.value)
                                  }
                                  className="py-3 pr-4 pl-10 w-full bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-[#FF6C6C] focus:border-[#FF6C6C]"
                                />
                              </div>

                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {filteredBranches.map((branch) => (
                                  <motion.div
                                    key={branch.branch_slug}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() =>
                                      toggleBranchSelection(branch)
                                    }
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
                                    ${
                                      selectedBranches.some(
                                        (b) =>
                                          b.branch_slug === branch.branch_slug
                                      )
                                        ? "border-[#FF6C6C] bg-[#FF6C6C]/5"
                                        : "border-gray-200 hover:border-[#FF6C6C]/30"
                                    }`}
                                  >
                                    <div className="flex gap-3 items-start">
                                      <div
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                      ${
                                        selectedBranches.some(
                                          (b) =>
                                            b.branch_slug === branch.branch_slug
                                        )
                                          ? "bg-[#FF6C6C]/20"
                                          : "bg-gray-100"
                                      }`}
                                      >
                                        <Building2
                                          className={`w-5 h-5 
                                        ${
                                          selectedBranches.some(
                                            (b) =>
                                              b.branch_slug ===
                                              branch.branch_slug
                                          )
                                            ? "text-[#FF6C6C]"
                                            : "text-gray-500"
                                        }`}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center">
                                          <h6 className="font-medium text-gray-900 truncate">
                                            {branch.name}
                                          </h6>
                                          <Checkbox
                                            checked={selectedBranches.some(
                                              (b) =>
                                                b.branch_slug ===
                                                branch.branch_slug
                                            )}
                                            sx={{
                                              color: "#FF6C6C",
                                              "&.Mui-checked": {
                                                color: "#FF6C6C",
                                              },
                                            }}
                                          />
                                        </div>
                                        <div className="mt-1 space-y-1 text-sm">
                                          <div className="flex gap-1 items-center text-gray-600">
                                            <MapPin className="w-3 h-3 text-[#FF6C6C]" />
                                            <span className="truncate">
                                              {branch.location}, {branch.region}
                                            </span>
                                          </div>
                                          {branch.phone_number && (
                                            <div className="flex gap-1 items-center text-gray-600">
                                              <Phone className="w-3 h-3 text-[#FF6C6C]" />
                                              <span className="truncate">
                                                {branch.phone_number}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {/* Partner Based Location Section - Only show if Partner Based is selected */}
                      {redemptionLocationType === "partner" && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-8"
                        >
                          <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
                            <div className="flex gap-3 items-center">
                              <div className="p-2 bg-[#FF6C6C]/10 rounded-xl">
                                <Building2 className="w-6 h-6 text-[#FF6C6C]" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Partner Branches
                                </h3>
                                <p className="text-sm text-gray-500">
                                  Select partners and their branches for
                                  redemption
                                </p>
                              </div>
                            </div>
                            {partners.length > 0 && userCanAddPartner && (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleAddPartnerClick}
                                className="flex items-center gap-2 px-4 py-2 bg-[#FF6C6C] text-white rounded-xl font-medium hover:bg-[#FF8F8F] transition-all duration-200"
                              >
                                <Plus className="w-4 h-4" />
                                Add Partner
                              </motion.button>
                            )}
                          </div>

                          {isLoadingPartners ? (
                            <div className="flex justify-center items-center p-8">
                              <CircularProgress
                                size={40}
                                sx={{ color: "#FF6C6C" }}
                              />
                            </div>
                          ) : partners.length === 0 ? (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200"
                            >
                              <div className="flex flex-col gap-4 items-center">
                                <div className="p-4 bg-gray-100 rounded-full">
                                  <Building2 className="w-8 h-8 text-gray-400" />
                                </div>
                                <div>
                                  <h4 className="text-lg font-medium text-gray-900">
                                    No Partners Found
                                  </h4>
                                  <p className="mt-1 text-gray-500">
                                    {userCanAddPartner
                                      ? "You need to add partners before using this feature"
                                      : "You don't have permission to add partners"}
                                  </p>
                                </div>
                                <Button
                                  variant="contained"
                                  onClick={handleAddPartnerClick}
                                  style={{
                                    backgroundColor: "#FF6C6C",
                                    borderRadius: "12px",
                                    marginTop: "8px",
                                  }}
                                >
                                  Add Partner
                                </Button>
                              </div>
                            </motion.div>
                          ) : (
                            <div className="space-y-6">
                              {partners.map((partner) => (
                                <motion.div
                                  key={partner.partner_slug}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className={`p-6 rounded-xl border-2 transition-all duration-300 
                              ${
                                selectedPartners.includes(partner.partner_slug)
                                  ? "border-[#FF6C6C] bg-[#FF6C6C]/5"
                                  : "border-gray-200 hover:border-[#FF6C6C]/30"
                              }`}
                                >
                                  {/* Partner Header */}
                                  <div className="flex gap-4 items-start">
                                    <div
                                      className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 
                                ${
                                  selectedPartners.includes(
                                    partner.partner_slug
                                  )
                                    ? "bg-[#FF6C6C]"
                                    : "bg-gray-100"
                                }`}
                                    >
                                      <Building2
                                        className={`w-6 h-6 
                                  ${
                                    selectedPartners.includes(
                                      partner.partner_slug
                                    )
                                      ? "text-white"
                                      : "text-gray-500"
                                  }`}
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex justify-between items-center">
                                        <h4 className="text-lg font-semibold text-gray-900">
                                          {partner.name}
                                        </h4>
                                        <div className="flex gap-2 items-center">
                                          <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={(e) =>
                                              togglePartnerExpansion(
                                                partner.partner_slug,
                                                e
                                              )
                                            }
                                            className="p-2 rounded-full hover:bg-gray-100"
                                          >
                                            <motion.div
                                              animate={{
                                                rotate: expandedPartners[
                                                  partner.partner_slug
                                                ]
                                                  ? 180
                                                  : 0,
                                              }}
                                              transition={{ duration: 0.2 }}
                                            >
                                              <ChevronDown className="w-5 h-5 text-gray-500" />
                                            </motion.div>
                                          </motion.button>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={selectedPartners.includes(
                                                  partner.partner_slug
                                                )}
                                                onChange={() =>
                                                  handlePartnerToggle(
                                                    partner.partner_slug
                                                  )
                                                }
                                                sx={{
                                                  color: "#FF6C6C",
                                                  "&.Mui-checked": {
                                                    color: "#FF6C6C",
                                                  },
                                                }}
                                              />
                                            }
                                            label="Select"
                                          />
                                        </div>
                                      </div>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {partner.branches.length}{" "}
                                        {partner.branches.length === 1
                                          ? "branch"
                                          : "branches"}{" "}
                                        available
                                      </p>
                                    </div>
                                  </div>

                                  {/* Branches Grid - Only show if partner is selected AND expanded */}
                                  <AnimatePresence>
                                    {selectedPartners.includes(
                                      partner.partner_slug
                                    ) &&
                                      expandedPartners[
                                        partner.partner_slug
                                      ] && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{
                                            height: "auto",
                                            opacity: 1,
                                          }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.3 }}
                                          className="pt-6 mt-6 border-t border-gray-200"
                                        >
                                          <h5 className="mb-4 text-sm font-medium text-gray-700">
                                            Select branches for redemption:
                                          </h5>
                                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            {partner.branches.map((branch) => (
                                              <motion.div
                                                key={branch.branch_slug}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() =>
                                                  handleBranchToggle(
                                                    partner.partner_slug,
                                                    branch
                                                  )
                                                }
                                                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200
                                          ${
                                            isBranchSelected(
                                              partner.partner_slug,
                                              branch.branch_slug
                                            )
                                              ? "border-[#FF6C6C] bg-[#FF6C6C]/5"
                                              : "border-gray-200 hover:border-[#FF6C6C]/30"
                                          }`}
                                              >
                                                <div className="flex gap-3 items-start">
                                                  <div
                                                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                            ${
                                              isBranchSelected(
                                                partner.partner_slug,
                                                branch.branch_slug
                                              )
                                                ? "bg-[#FF6C6C]/20"
                                                : "bg-gray-100"
                                            }`}
                                                  >
                                                    <Store
                                                      className={`w-5 h-5 
                                              ${
                                                isBranchSelected(
                                                  partner.partner_slug,
                                                  branch.branch_slug
                                                )
                                                  ? "text-[#FF6C6C]"
                                                  : "text-gray-500"
                                              }`}
                                                    />
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-center">
                                                      <h6 className="font-medium text-gray-900 truncate">
                                                        {branch.name}
                                                      </h6>
                                                      <Checkbox
                                                        checked={isBranchSelected(
                                                          partner.partner_slug,
                                                          branch.branch_slug
                                                        )}
                                                        sx={{
                                                          color: "#FF6C6C",
                                                          "&.Mui-checked": {
                                                            color: "#FF6C6C",
                                                          },
                                                        }}
                                                      />
                                                    </div>
                                                    <div className="mt-1 space-y-1 text-sm">
                                                      <div className="flex gap-1 items-center text-gray-600">
                                                        <MapPin className="w-3 h-3 text-[#FF6C6C]" />
                                                        <span className="truncate">
                                                          {branch.location},{" "}
                                                          {branch.region}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </motion.div>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                  </AnimatePresence>
                                </motion.div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>

                    {/* Step 3 Navigation */}
                    <div className="flex flex-col gap-4 justify-between items-center p-6 bg-white rounded-xl border border-gray-100 sm:flex-row">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={prevStep}
                        className="flex gap-2 justify-center items-center px-6 py-3 w-full text-gray-600 bg-gray-100 rounded-xl transition-colors sm:w-auto hover:bg-gray-200"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Back to Supporting Details</span>
                        <span className="sm:hidden">Back</span>
                      </motion.button>
                      <div className="flex gap-2 items-center text-sm text-gray-500">
                        <Target className="w-4 h-4" />
                        <span className="hidden sm:inline">Step 3 of 5</span>
                        <span className="sm:hidden">3/4</span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={nextStep}
                        disabled={!stepValidation[3]}
                        className={`w-full sm:w-auto px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                          stepValidation[3]
                            ? "bg-[#FF6C6C] text-white hover:bg-[#FF8F8F] shadow-lg shadow-[#FF6C6C]/20"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <span className="hidden sm:inline">Continue to Redemption Details</span>
                        <span className="sm:hidden">Next</span>
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Step 4: Content & Media */}
                {currentStep === 4 && (
                  <div className="space-y-8">
                    {/* Redemption Options */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <CheckCircle className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Redemption Options
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select how customers can redeem this deal
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6">
                        <FormControl fullWidth>
                          <InputLabel
                            id="redemption-options-label"
                            sx={{ "&.Mui-focused": { color: "#FF6C6C" } }}
                          >
                            Redemption Options
                          </InputLabel>
                          <Select
                            labelId="redemption-options-label"
                            multiple
                            value={formData.redemption_type}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                redemption_type: e.target.value,
                              })
                            }
                            input={<OutlinedInput label="Redemption Options" />}
                            renderValue={(selected) => selected.join(", ")}
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#FF6C6C",
                                },
                            }}
                          >
                            {["Online", "In-store"].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {/* Online Link Field - Only show when Online is selected */}
                      {formData.redemption_type.includes("Online") && (
                        <div className="mt-6">
                          <TextField
                            label="Online Link"
                            type="url"
                            fullWidth
                            value={formData.online_link || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                online_link: e.target.value,
                              })
                            }
                            placeholder="https://yourwebsite.com/redeem"
                            helperText="Must be a valid URL. Required if Online redemption is selected."
                            error={
                              formData.redemption_type.includes("Online") &&
                              (!formData.online_link ||
                                !isValidUrl(formData.online_link))
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                "&:hover fieldset": { borderColor: "#FF6C6C" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#FF6C6C",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FF6C6C",
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Redemption Limits */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 rounded-xl bg-purple-500/10">
                          <svg
                            className="w-6 h-6 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Redemption Limits
                          </h3>
                          <p className="text-sm text-gray-500">
                            Set how many times a customer can redeem this deal
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {/* No Limits Option */}
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="no-limits"
                            name="limits-option"
                            checked={formData.redemption_limits.length === 0}
                            onChange={() =>
                              setFormData({
                                ...formData,
                                redemption_limits: [],
                              })
                            }
                            className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                          />
                          <label
                            htmlFor="no-limits"
                            className="text-sm font-medium text-gray-700"
                          >
                            No redemption limits (unlimited)
                          </label>
                        </div>

                        {/* Custom Limits Option */}
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="custom-limits"
                            name="limits-option"
                            checked={formData.redemption_limits.length > 0}
                            onChange={() => {
                              if (formData.redemption_limits.length === 0) {
                                setFormData({
                                  ...formData,
                                  redemption_limits: ["1"],
                                });
                              }
                            }}
                            className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                          />
                          <label
                            htmlFor="custom-limits"
                            className="text-sm font-medium text-gray-700"
                          >
                            Set redemption limit per customer
                          </label>
                        </div>

                         {/* Number Input for Limit */}
                        {formData.redemption_limits.length > 0 && (
                          <div className="pl-7">
                            <div className="flex gap-3 items-center max-w-md">
                              <TextField
                                type="number"
                                size="small"
                                label="Maximum Redemptions"
                                value={formData.redemption_limits[0] || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Only allow positive integers
                                  if (value === "" || (parseInt(value) > 0 && !value.includes("."))) {
                                    setFormData({
                                      ...formData,
                                      redemption_limits: [value],
                                    });
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  step: 1,
                                }}
                                placeholder="e.g., 1, 2, 3"
                                helperText={
                                  formData.redemption_limits[0]
                                    ? `Customers can redeem this deal ${formData.redemption_limits[0]} time${parseInt(formData.redemption_limits[0]) > 1 ? 's' : ''}`
                                    : "Enter the maximum number of times a customer can redeem"
                                }
                                sx={{
                                  flex: 1,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&:hover fieldset": {
                                      borderColor: "#9333EA",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#9333EA",
                                    },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#9333EA",
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Exclusions */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 rounded-xl bg-red-500/10">
                          <svg
                            className="w-6 h-6 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                            />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Exclusions
                          </h3>
                          <p className="text-sm text-gray-500">
                            Specify what's excluded from this deal (optional)
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {/* No Exclusions Option */}
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="no-exclusions"
                            name="exclusions-option"
                            checked={formData.exclusions.length === 0}
                            onChange={() =>
                              setFormData({ ...formData, exclusions: [] })
                            }
                            className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                          />
                          <label
                            htmlFor="no-exclusions"
                            className="text-sm font-medium text-gray-700"
                          >
                            No exclusions
                          </label>
                        </div>

                        {/* Custom Exclusions Option */}
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="custom-exclusions"
                            name="exclusions-option"
                            checked={formData.exclusions.length > 0}
                            onChange={() => {
                              if (formData.exclusions.length === 0) {
                                setFormData({ ...formData, exclusions: [""] });
                              }
                            }}
                            className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                          />
                          <label
                            htmlFor="custom-exclusions"
                            className="text-sm font-medium text-gray-700"
                          >
                            Set custom exclusions
                          </label>
                        </div>

                        {/* Dynamic Exclusion Inputs */}
                        {formData.exclusions.length > 0 && (
                          <div className="pl-7 space-y-3">
                            {formData.exclusions.map((exclusion, index) => (
                              <div
                                key={index}
                                className="flex gap-3 items-center"
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={exclusion}
                                  onChange={(e) => {
                                    const newExclusions = [
                                      ...formData.exclusions,
                                    ];
                                    newExclusions[index] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      exclusions: newExclusions,
                                    });
                                  }}
                                  placeholder={`Exclusion ${
                                    index + 1
                                  } (e.g., "Sale items excluded", "Not valid on electronics", "Excludes clearance items")`}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      "&:hover fieldset": {
                                        borderColor: "#DC2626",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#DC2626",
                                      },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#DC2626",
                                    },
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newExclusions =
                                      formData.exclusions.filter(
                                        (_, i) => i !== index
                                      );
                                    setFormData({
                                      ...formData,
                                      exclusions: newExclusions,
                                    });
                                  }}
                                  className="p-2 text-red-500 rounded-lg transition-colors duration-200 hover:text-red-700 hover:bg-red-50"
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ))}

                            {/* Add Exclusion Button */}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  exclusions: [...formData.exclusions, ""],
                                });
                              }}
                              className="flex gap-2 items-center px-4 py-2 text-red-600 bg-red-50 rounded-lg border border-red-200 transition-colors duration-200 hover:bg-red-100"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                              </svg>
                              Add Another Exclusion
                            </button>

                            {/* Examples */}
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                              <div className="flex gap-2 items-start">
                                <div className="p-1 bg-red-100 rounded">
                                  <svg
                                    className="w-4 h-4 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-red-800">
                                    Example Exclusions
                                  </p>
                                  <ul className="mt-1 space-y-1 text-xs text-red-600">
                                    <li>
                                      • "Sale items excluded from this offer"
                                    </li>
                                    <li>
                                      • "Not valid on electronics and gadgets"
                                    </li>
                                    <li>
                                      • "Excludes clearance and outlet items"
                                    </li>
                                    <li>• "Not applicable to gift cards"</li>
                                    <li>• "Excludes premium brands"</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Redemption Location */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 rounded-xl bg-blue-500/10">
                          <MapPin className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Redemption Locations
                          </h3>
                          <p className="text-sm text-gray-500">
                            Select where customers can redeem this deal
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <RadioGroup
                          value={redemptionLocationTypeStep4}
                          onChange={(e) =>
                            setRedemptionLocationTypeStep4(e.target.value)
                          }
                          className="space-y-4"
                        >
                          {/* City/Region Based Option */}
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() =>
                              setRedemptionLocationTypeStep4("region")
                            }
                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 group
                            ${
                              redemptionLocationTypeStep4 === "region"
                                ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-25 shadow-md shadow-blue-500/20"
                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                            }`}
                          >
                            <div className="flex gap-3 items-start">
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200
                              ${
                                redemptionLocationTypeStep4 === "region"
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-gray-300 group-hover:border-blue-400"
                              }`}
                              >
                                {redemptionLocationTypeStep4 === "region" && (
                                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex gap-2 items-center mb-1">
                                  <div
                                    className={`p-2 rounded-lg transition-all duration-200
                                  ${
                                    redemptionLocationTypeStep4 === "region"
                                      ? "bg-blue-500 text-white"
                                      : "bg-blue-100 text-blue-600 group-hover:bg-blue-200"
                                  }`}
                                  >
                                    <MapPin className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <h4 className="text-base font-semibold text-gray-900">
                                      City/Region Based
                                    </h4>
                                    <p className="text-xs text-gray-600">
                                      Select specific regions and locations
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>

                          {/* Branch Based Option */}
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() =>
                              setRedemptionLocationTypeStep4("branch")
                            }
                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 group
                            ${
                              redemptionLocationTypeStep4 === "branch"
                                ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-25 shadow-md shadow-blue-500/20"
                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                            }`}
                          >
                            <div className="flex gap-3 items-start">
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200
                              ${
                                redemptionLocationTypeStep4 === "branch"
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-gray-300 group-hover:border-blue-400"
                              }`}
                              >
                                {redemptionLocationTypeStep4 === "branch" && (
                                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex gap-2 items-center mb-1">
                                  <div
                                    className={`p-2 rounded-lg transition-all duration-200
                                  ${
                                    redemptionLocationTypeStep4 === "branch"
                                      ? "bg-blue-500 text-white"
                                      : "bg-blue-100 text-blue-600 group-hover:bg-blue-200"
                                  }`}
                                  >
                                    <Building2 className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <h4 className="text-base font-semibold text-gray-900">
                                      Branch Based
                                    </h4>
                                    <p className="text-xs text-gray-600">
                                      Select specific branches
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>

                          {/* Partner Based Option */}
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() =>
                              setRedemptionLocationTypeStep4("partner")
                            }
                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 group
                            ${
                              redemptionLocationTypeStep4 === "partner"
                                ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-25 shadow-md shadow-blue-500/20"
                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                            }`}
                          >
                            <div className="flex gap-3 items-start">
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200
                              ${
                                redemptionLocationTypeStep4 === "partner"
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-gray-300 group-hover:border-blue-400"
                              }`}
                              >
                                {redemptionLocationTypeStep4 === "partner" && (
                                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex gap-2 items-center mb-1">
                                  <div
                                    className={`p-2 rounded-lg transition-all duration-200
                                  ${
                                    redemptionLocationTypeStep4 === "partner"
                                      ? "bg-blue-500 text-white"
                                      : "bg-blue-100 text-blue-600 group-hover:bg-blue-200"
                                  }`}
                                  >
                                    <Store className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <h4 className="text-base font-semibold text-gray-900">
                                      Partner Based
                                    </h4>
                                    <p className="text-xs text-gray-600">
                                      Select partner locations
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </RadioGroup>
                      </div>

                      {/* Conditional Location Inputs */}
                      {redemptionLocationTypeStep4 === "region" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 mt-6 rounded-xl border border-blue-200 bg-blue-50/50"
                        >
                          <div className="flex gap-4 items-center mb-4">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectAllRedemptionLocations(
                                  !selectAllRedemptionLocations
                                );
                                if (!selectAllRedemptionLocations) {
                                  setFormData({
                                    ...formData,
                                    redemption_region_location: [
                                      { region: "all", location: "all" },
                                    ],
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    redemption_region_location: [
                                      { region: "", location: "" },
                                    ],
                                  });
                                }
                              }}
                              className={`
                              flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
                              transition-all duration-200
                              ${
                                selectAllRedemptionLocations
                                  ? "text-white bg-blue-500"
                                  : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                              }
                            `}
                            >
                              <div
                                className={`
                                w-3 h-3 border-2 rounded flex items-center justify-center
                                ${
                                  selectAllRedemptionLocations
                                    ? "border-white"
                                    : "border-gray-400"
                                }
                              `}
                              >
                                {selectAllRedemptionLocations && (
                                  <Check className="w-2 h-2 text-white" />
                                )}
                              </div>
                              Nationwide
                            </motion.button>

                            {!selectAllRedemptionLocations && (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  addRedemptionRegionLocation();
                                }}
                                disabled={
                                  formData.redemption_region_location.length >=
                                  5
                                }
                                className={`
                                flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
                                transition-all duration-200
                                ${
                                  formData.redemption_region_location.length >=
                                  5
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-blue-500/10 text-blue-600 hover:bg-blue-500/20"
                                }
                              `}
                              >
                                <PlusCircle className="w-3 h-3" />
                                Add Location
                              </motion.button>
                            )}
                          </div>

                          <AnimatePresence mode="popLayout">
                            {!selectAllRedemptionLocations &&
                              formData.redemption_region_location.map(
                                (item, index) => (
                                  <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    className="relative p-4 mb-3 bg-white rounded-lg border border-blue-200 group"
                                  >
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                      <div className="relative w-full">
                                        <select
                                          value={item.region}
                                          onChange={(e) => {
                                            const newRegionLocations = [
                                              ...formData.redemption_region_location,
                                            ];
                                            newRegionLocations[index].region =
                                              e.target.value;
                                            newRegionLocations[index].location =
                                              "";
                                            setFormData({
                                              ...formData,
                                              redemption_region_location:
                                                newRegionLocations,
                                            });
                                          }}
                                          className="px-4 py-3 w-full text-sm rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                          <option value="">
                                            Select Region
                                          </option>
                                          {regionOptions.map((region) => (
                                            <option key={region} value={region}>
                                              {region}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="relative">
                                        <div
                                          className={`
                                        relative rounded-lg border transition-all duration-200
                                        ${
                                          item.location
                                            ? "border-blue-500/30"
                                            : "border-blue-200"
                                        }
                                        group-hover:border-blue-500/50
                                      `}
                                        >
                                          <input
                                            type="text"
                                            value={
                                              redemptionLocationSearchTerms[
                                                index
                                              ] !== undefined
                                                ? redemptionLocationSearchTerms[
                                                    index
                                                  ]
                                                : item.location &&
                                                  item.location !== "other"
                                                ? item.location
                                                : ""
                                            }
                                            onChange={(e) => {
                                              handleRedemptionLocationSearch(
                                                index,
                                                e.target.value
                                              );
                                              if (
                                                !redemptionLocationDropdownOpen[
                                                  index
                                                ]
                                              ) {
                                                toggleRedemptionLocationDropdown(
                                                  index
                                                );
                                              }
                                            }}
                                            onFocus={() => {
                                              if (item.region) {
                                                // Set the current location as search term when focusing to allow editing
                                                if (
                                                  item.location &&
                                                  item.location !== "other" &&
                                                  redemptionLocationSearchTerms[
                                                    index
                                                  ] === undefined
                                                ) {
                                                  setRedemptionLocationSearchTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: item.location,
                                                    })
                                                  );
                                                }
                                                toggleRedemptionLocationDropdown(
                                                  index
                                                );
                                              }
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === "Escape") {
                                                setRedemptionLocationDropdownOpen(
                                                  (prev) => ({
                                                    ...prev,
                                                    [index]: false,
                                                  })
                                                );
                                              }
                                            }}
                                            placeholder="Search or select location..."
                                            className="px-3 py-2 w-full text-sm bg-transparent border-none outline-none focus:ring-0"
                                            disabled={!item.region}
                                          />

                                          <div className="flex absolute right-3 top-1/2 gap-2 items-center -translate-y-1/2">
                                            {/* Clear button - show when there's a selection or search term */}
                                            {(item.location ||
                                              redemptionLocationSearchTerms[
                                                index
                                              ]) && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const newRegionLocation = [
                                                    ...formData.redemption_region_location,
                                                  ];
                                                  newRegionLocation[index] = {
                                                    ...newRegionLocation[index],
                                                    location: "",
                                                    customLocation: undefined,
                                                  };
                                                  setFormData({
                                                    ...formData,
                                                    redemption_region_location:
                                                      newRegionLocation,
                                                  });
                                                  setRedemptionLocationSearchTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: undefined,
                                                    })
                                                  );
                                                }}
                                                className="p-1 text-gray-400 rounded-full transition-colors hover:text-red-500"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            )}

                                            {/* Search/Dropdown Icon */}
                                            <div className="pointer-events-none">
                                              {redemptionLocationDropdownOpen[
                                                index
                                              ] ? (
                                                <Search className="w-3 h-3 text-gray-400" />
                                              ) : (
                                                <motion.div
                                                  animate={{
                                                    rotate:
                                                      redemptionLocationDropdownOpen[
                                                        index
                                                      ]
                                                        ? 180
                                                        : 0,
                                                  }}
                                                  transition={{ duration: 0.2 }}
                                                >
                                                  <ChevronDown className="w-3 h-3 text-gray-400" />
                                                </motion.div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Dropdown Options */}
                                          <AnimatePresence>
                                            {redemptionLocationDropdownOpen[
                                              index
                                            ] &&
                                              item.region && (
                                                <motion.div
                                                  initial={{
                                                    opacity: 0,
                                                    y: -10,
                                                  }}
                                                  animate={{ opacity: 1, y: 0 }}
                                                  exit={{ opacity: 0, y: -10 }}
                                                  transition={{ duration: 0.2 }}
                                                  className="overflow-y-auto absolute right-0 left-0 top-full z-50 mt-1 max-h-48 bg-white rounded-lg border border-blue-200 shadow-lg"
                                                >
                                                  {getFilteredRedemptionLocations(
                                                    item.region,
                                                    redemptionLocationSearchTerms[
                                                      index
                                                    ]
                                                  ).length === 0 &&
                                                  redemptionLocationSearchTerms[
                                                    index
                                                  ] ? (
                                                    <>
                                                      <div className="px-3 py-2 text-sm text-gray-500">
                                                        No locations found
                                                      </div>
                                                      <div
                                                        onClick={() =>
                                                          handleRedemptionLocationSelect(
                                                            index,
                                                            "other"
                                                          )
                                                        }
                                                        className="px-3 py-2 text-sm text-blue-600 cursor-pointer hover:bg-blue-50"
                                                      >
                                                        Add "
                                                        {
                                                          redemptionLocationSearchTerms[
                                                            index
                                                          ]
                                                        }
                                                        " as custom location
                                                      </div>
                                                    </>
                                                  ) : (
                                                    <>
                                                      {!redemptionLocationSearchTerms[
                                                        index
                                                      ] && (
                                                        <div
                                                          onClick={() =>
                                                            handleRedemptionLocationSelect(
                                                              index,
                                                              ""
                                                            )
                                                          }
                                                          className="px-3 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
                                                        >
                                                          Select Location
                                                        </div>
                                                      )}
                                                      {getFilteredRedemptionLocations(
                                                        item.region,
                                                        redemptionLocationSearchTerms[
                                                          index
                                                        ]
                                                      ).map((location) => (
                                                        <div
                                                          key={location}
                                                          onClick={() =>
                                                            handleRedemptionLocationSelect(
                                                              index,
                                                              location
                                                            )
                                                          }
                                                          className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-blue-50"
                                                        >
                                                          {location}
                                                        </div>
                                                      ))}
                                                      <div
                                                        onClick={() =>
                                                          handleRedemptionLocationSelect(
                                                            index,
                                                            "other"
                                                          )
                                                        }
                                                        className="px-3 py-2 text-sm text-blue-600 border-t border-gray-100 cursor-pointer hover:bg-blue-50"
                                                      >
                                                        + Add custom location
                                                      </div>
                                                    </>
                                                  )}
                                                </motion.div>
                                              )}
                                          </AnimatePresence>
                                        </div>

                                        {/* Custom Location Input Field */}
                                        {item.location === "other" && (
                                          <div className="relative px-3 py-1 mt-2 rounded-lg border border-blue-500/30">
                                            <input
                                              value={item.customLocation || ""}
                                              onChange={(e) => {
                                                const newRegionLocation = [
                                                  ...formData.redemption_region_location,
                                                ];
                                                newRegionLocation[index] = {
                                                  ...newRegionLocation[index],
                                                  customLocation:
                                                    e.target.value,
                                                };
                                                setFormData({
                                                  ...formData,
                                                  redemption_region_location:
                                                    newRegionLocation,
                                                });
                                              }}
                                              placeholder="Enter custom location"
                                              className="px-3 py-2 w-full text-sm bg-transparent border-none outline-none focus:ring-0"
                                              disabled={!item.region}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.9 }}
                                      type="button"
                                      onClick={() =>
                                        removeRedemptionRegionLocation(index)
                                      }
                                      className="absolute -top-3 -right-3 p-2 text-white bg-red-500 rounded-full shadow-lg opacity-0 transition-colors duration-200 hover:bg-red-600 group-hover:opacity-100 sm:opacity-0"
                                    >
                                      <X className="w-4 h-4" />
                                    </motion.button>
                                  </motion.div>
                                )
                              )}
                          </AnimatePresence>
                        </motion.div>
                      )}

                      {redemptionLocationTypeStep4 === "branch" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 mt-6 rounded-xl border border-blue-200 bg-blue-50/50"
                        >
                          {isLoadingBranches ? (
                            <div className="flex justify-center items-center p-4">
                              <CircularProgress
                                size={30}
                                sx={{ color: "#3B82F6" }}
                              />
                            </div>
                          ) : branches.length === 0 ? (
                            <div className="p-4 text-center">
                              <div className="flex flex-col gap-3 items-center">
                                <div className="p-3 bg-gray-100 rounded-full">
                                  <Building2 className="w-6 h-6 text-gray-400" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    No Branches Found
                                  </h4>
                                  <p className="mt-1 text-xs text-gray-500">
                                    You need to add branches before using this
                                    feature
                                  </p>
                                </div>
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => setShowBranchModal(true)}
                                  className="flex gap-2 items-center px-3 py-2 font-medium text-white bg-blue-500 rounded-lg transition-all duration-200 hover:bg-blue-600"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Your First Branch
                                </motion.button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                                <input
                                  type="text"
                                  placeholder="Search branches..."
                                  value={branchSearchTerm}
                                  onChange={(e) =>
                                    setBranchSearchTerm(e.target.value)
                                  }
                                  className="py-2 pr-4 pl-9 w-full text-sm bg-white rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              <div className="grid overflow-y-auto grid-cols-1 gap-2 max-h-64 sm:grid-cols-2 md:max-h-80">
                                {filteredBranches.map((branch) => (
                                  <motion.div
                                    key={branch.branch_slug}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    onClick={() =>
                                      toggleRedemptionBranchSelection(branch)
                                    }
                                    className={`p-3 rounded-lg border cursor-pointer transition-all duration-200
                                    ${
                                      selectedRedemptionBranches.some(
                                        (b) =>
                                          b.branch_slug === branch.branch_slug
                                      )
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-blue-200 hover:border-blue-300"
                                    }`}
                                  >
                                    <div className="flex gap-2 items-center">
                                      <div
                                        className={`w-6 h-6 rounded flex items-center justify-center
                                      ${
                                        selectedRedemptionBranches.some(
                                          (b) =>
                                            b.branch_slug === branch.branch_slug
                                        )
                                          ? "bg-blue-500"
                                          : "bg-gray-100"
                                      }`}
                                      >
                                        <Building2
                                          className={`w-3 h-3 
                                        ${
                                          selectedRedemptionBranches.some(
                                            (b) =>
                                              b.branch_slug ===
                                              branch.branch_slug
                                          )
                                            ? "text-white"
                                            : "text-gray-500"
                                        }`}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h6 className="text-sm font-medium text-gray-900 truncate">
                                          {branch.name}
                                        </h6>
                                        <p className="text-xs text-gray-600 truncate">
                                          {branch.location}, {branch.region}
                                        </p>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {redemptionLocationTypeStep4 === "partner" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 mt-6 rounded-xl border border-blue-200 bg-blue-50/50"
                        >
                          {isLoadingPartners ? (
                            <div className="flex justify-center items-center p-4">
                              <CircularProgress
                                size={30}
                                sx={{ color: "#3B82F6" }}
                              />
                            </div>
                          ) : partners.length === 0 ? (
                            <div className="p-4 text-center">
                              <div className="flex flex-col gap-3 items-center">
                                <div className="p-3 bg-gray-100 rounded-full">
                                  <Store className="w-6 h-6 text-gray-400" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    No Partners Found
                                  </h4>
                                  <p className="mt-1 text-xs text-gray-500">
                                    {userCanAddPartner
                                      ? "You need to add partners before using this feature"
                                      : "You don't have permission to add partners"}
                                  </p>
                                </div>
                                {userCanAddPartner && (
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleAddPartnerClick}
                                    className="flex gap-2 items-center px-3 py-2 font-medium text-white bg-blue-500 rounded-lg transition-all duration-200 hover:bg-blue-600"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add Partner
                                  </motion.button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {partners.map((partner) => {
                                const isPartnerSelected = isRedemptionPartnerSelected(partner.partner_slug);
                                const partnerLocation = formData.redemption_partner_location.find(
                                  (p) => p.partner_id === partner.partner_slug
                                );
                                const selectedBranchCount = partnerLocation?.branches?.length || 0;
                                
                                return (
                                <motion.div
                                  key={partner.partner_slug}
                                  className={`p-4 rounded-lg border-2 transition-all duration-300
                                  ${
                                    isPartnerSelected
                                      ? "bg-blue-50 border-blue-500"
                                      : "border-gray-200"
                                  }`}
                                >
                                  <div className="flex gap-3 items-start">
                                    <div
                                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                      ${
                                        isPartnerSelected
                                          ? "bg-blue-500"
                                          : "bg-gray-100"
                                      }`}
                                    >
                                      <Store
                                        className={`w-5 h-5 
                                        ${
                                          isPartnerSelected
                                            ? "text-white"
                                            : "text-gray-500"
                                        }`}
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <h4 className="text-sm font-semibold text-gray-900">
                                            {partner.name}
                                          </h4>
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            {partner.branches?.length || 0} branch{partner.branches?.length !== 1 ? 'es' : ''} available
                                          </p>
                                          {selectedBranchCount > 0 && (
                                            <p className="mt-1 text-xs font-medium text-blue-600">
                                              ✓ {selectedBranchCount} branch{selectedBranchCount !== 1 ? 'es' : ''} selected
                                            </p>
                                          )}
                                        </div>
                                        <div className="flex gap-2 items-center">
                                          <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={(e) =>
                                              toggleRedemptionPartnerExpansion(
                                                partner.partner_slug,
                                                e
                                              )
                                            }
                                            className="p-2 rounded-full hover:bg-gray-100"
                                          >
                                            <ChevronDown
                                              className={`w-5 h-5 transition-transform duration-200
                                              ${
                                                expandedRedemptionPartners[partner.partner_slug]
                                                  ? "rotate-180"
                                                  : ""
                                              }`}
                                            />
                                          </motion.button>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={isPartnerSelected}
                                                onChange={() =>
                                                  handleRedemptionPartnerToggle(
                                                    partner.partner_slug
                                                  )
                                                }
                                                sx={{
                                                  color: "#3B82F6",
                                                  "&.Mui-checked": {
                                                    color: "#3B82F6",
                                                  },
                                                }}
                                              />
                                            }
                                            label="Select"
                                          />
                                        </div>
                                      </div>

                                      <AnimatePresence>
                                        {expandedRedemptionPartners[partner.partner_slug] && (
                                          <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 space-y-2"
                                          >
                                            {partner.branches?.map((branch) => (
                                              <motion.div
                                                key={branch.branch_slug}
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                                onClick={() =>
                                                  handleRedemptionBranchToggle(
                                                    partner.partner_slug,
                                                    branch
                                                  )
                                                }
                                                className={`p-2 rounded border cursor-pointer transition-all duration-200
                                                ${
                                                  isRedemptionBranchSelected(
                                                    partner.partner_slug,
                                                    branch.branch_slug
                                                  )
                                                    ? "border-blue-500 bg-blue-100"
                                                    : "border-gray-200 hover:border-blue-300"
                                                }`}
                                              >
                                                <div className="flex gap-2 items-center">
                                                  <div
                                                    className={`w-4 h-4 rounded flex items-center justify-center
                                                  ${
                                                    isRedemptionBranchSelected(
                                                      partner.partner_slug,
                                                      branch.branch_slug
                                                    )
                                                      ? "bg-blue-500"
                                                      : "bg-gray-200"
                                                  }`}
                                                  >
                                                    {isRedemptionBranchSelected(
                                                      partner.partner_slug,
                                                      branch.branch_slug
                                                    ) && (
                                                      <Check className="w-2 h-2 text-white" />
                                                    )}
                                                  </div>
                                                  <span className="text-xs text-gray-700">
                                                    {branch.name}
                                                  </span>
                                                </div>
                                              </motion.div>
                                            ))}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  </div>
                                </motion.div>
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>

                    {/* Delivery Fee Section */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex gap-3 items-center mb-6">
                        <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                          <Truck className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Delivery Fee
                          </h3>
                          <p className="text-sm text-gray-500">
                            Choose how delivery fees apply to this deal
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="text-sm font-medium text-gray-700">
                          Delivery Fee Options
                        </label>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          {/* Not Apply Option */}
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                              formData.delivery_fee === "not_apply"
                                ? "border-[#FF6C6C] bg-[#FF6C6C]/5 shadow-md"
                                : "border-gray-200 bg-white hover:border-[#FF6C6C]/50 hover:bg-[#FF6C6C]/5"
                            }`}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                delivery_fee: "not_apply",
                              })
                            }
                          >
                            <div className="flex flex-col items-center space-y-2">
                              <div
                                className={`rounded-full p-3 transition-colors duration-200 ${
                                  formData.delivery_fee === "not_apply"
                                    ? "bg-[#FF6C6C] text-white"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                <X className="w-6 h-6" />
                              </div>
                              <div className="text-center">
                                <p
                                  className={`text-sm font-medium ${
                                    formData.delivery_fee === "not_apply"
                                      ? "text-[#FF6C6C]"
                                      : "text-gray-700"
                                  }`}
                                >
                                  Not Apply
                                </p>
                                <p className="text-xs text-gray-500">
                                  No delivery fee
                                </p>
                              </div>
                            </div>
                            {formData.delivery_fee === "not_apply" && (
                              <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#FF6C6C] flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </motion.div>

                          {/* Apply Option */}
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                              formData.delivery_fee === "apply"
                                ? "border-orange-500 bg-orange-50 shadow-md"
                                : "border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50"
                            }`}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                delivery_fee: "apply",
                              })
                            }
                          >
                            <div className="flex flex-col items-center space-y-2">
                              <div
                                className={`rounded-full p-3 transition-colors duration-200 ${
                                  formData.delivery_fee === "apply"
                                    ? "bg-orange-500 text-white"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                <Truck className="w-6 h-6" />
                              </div>
                              <div className="text-center">
                                <p
                                  className={`text-sm font-medium ${
                                    formData.delivery_fee === "apply"
                                      ? "text-orange-600"
                                      : "text-gray-700"
                                  }`}
                                >
                                  Apply
                                </p>
                                <p className="text-xs text-gray-500">
                                  Standard delivery fee
                                </p>
                              </div>
                            </div>
                            {formData.delivery_fee === "apply" && (
                              <div className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-orange-500 rounded-full">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </motion.div>

                          {/* Free Within Location Option */}
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                              formData.delivery_fee === "free_within_location"
                                ? "border-green-500 bg-green-50 shadow-md"
                                : "border-gray-200 bg-white hover:border-green-300 hover:bg-green-50"
                            }`}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                delivery_fee: "free_within_location",
                              })
                            }
                          >
                            <div className="flex flex-col items-center space-y-2">
                              <div
                                className={`rounded-full p-3 transition-colors duration-200 ${
                                  formData.delivery_fee ===
                                  "free_within_location"
                                    ? "bg-green-500 text-white"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                <MapPin className="w-6 h-6" />
                              </div>
                              <div className="text-center">
                                <p
                                  className={`text-sm font-medium ${
                                    formData.delivery_fee ===
                                    "free_within_location"
                                      ? "text-green-600"
                                      : "text-gray-700"
                                  }`}
                                >
                                  Free Within Location
                                </p>
                                <p className="text-xs text-gray-500">
                                  Free delivery in area
                                </p>
                              </div>
                            </div>
                            {formData.delivery_fee ===
                              "free_within_location" && (
                              <div className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 bg-green-500 rounded-full">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </motion.div>
                        </div>

                        {/* Free Delivery Areas - Only show when "Free within location" is selected */}
                        {formData.delivery_fee === "free_within_location" && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-6 space-y-4"
                          >
                            <div className="flex gap-3 items-center">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <MapPin className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  Free Delivery Areas
                                </h4>
                                <p className="text-sm text-gray-500">
                                  Specify which areas get free delivery
                                </p>
                              </div>
                            </div>

                            <div className="space-y-4">
                              {/* Nationwide Option */}
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  id="nationwide-delivery"
                                  name="delivery-coverage"
                                  checked={selectAllFreeDeliveryLocations}
                                  onChange={() => {
                                    setSelectAllFreeDeliveryLocations(true);
                                    setFormData({
                                      ...formData,
                                      free_delivery_areas: [],
                                    });
                                  }}
                                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                />
                                <label
                                  htmlFor="nationwide-delivery"
                                  className="text-sm font-medium text-gray-700"
                                >
                                  Nationwide free delivery
                                </label>
                              </div>

                              {/* Specific Areas Option */}
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  id="specific-delivery-areas"
                                  name="delivery-coverage"
                                  checked={!selectAllFreeDeliveryLocations}
                                  onChange={() => {
                                    setSelectAllFreeDeliveryLocations(false);
                                    if (
                                      formData.free_delivery_areas.length === 0
                                    ) {
                                      addFreeDeliveryRegionLocation();
                                    }
                                  }}
                                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                />
                                <label
                                  htmlFor="specific-delivery-areas"
                                  className="text-sm font-medium text-gray-700"
                                >
                                  Specific areas only
                                </label>
                              </div>

                              {/* Dynamic Area Inputs */}
                              {!selectAllFreeDeliveryLocations && (
                                <div className="pl-7 space-y-3">
                                  {formData.free_delivery_areas.map(
                                    (area, index) => (
                                      <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="relative p-4 bg-green-50 rounded-xl border border-green-200 group"
                                      >
                                        <div className="flex gap-3 items-center">
                                          <div className="flex-1">
                                            <label className="block mb-2 text-sm font-medium text-green-800">
                                              Area {index + 1}
                                            </label>

                                            {/* Region and Location Selection */}
                                            <div className="relative">
                                              <div className="relative">
                                                <input
                                                  type="text"
                                                  placeholder="Search regions (e.g., Greater Accra) or locations (e.g., Accra Central)..."
                                                  value={
                                                    freeDeliveryLocationSearchTerms[
                                                      index
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleFreeDeliveryLocationSearch(
                                                      index,
                                                      e.target.value
                                                    )
                                                  }
                                                  onFocus={() =>
                                                    setFreeDeliveryLocationDropdownOpen(
                                                      (prev) => ({
                                                        ...prev,
                                                        [index]: true,
                                                      })
                                                    )
                                                  }
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Escape") {
                                                      setFreeDeliveryLocationDropdownOpen(
                                                        (prev) => ({
                                                          ...prev,
                                                          [index]: false,
                                                        })
                                                      );
                                                    }
                                                  }}
                                                  className="p-3 pl-10 w-full text-sm bg-white rounded-lg border border-green-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                                />
                                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                                  <svg
                                                    className="w-4 h-4 text-green-500"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                    />
                                                  </svg>
                                                </div>
                                              </div>

                                              {/* Dropdown */}
                                              {freeDeliveryLocationDropdownOpen[
                                                index
                                              ] && (
                                                <div
                                                  data-delivery-dropdown
                                                  className="overflow-y-auto absolute z-50 mt-1 w-full max-h-60 bg-white rounded-lg border border-green-300 shadow-lg"
                                                >
                                                  {getFilteredFreeDeliveryLocations(
                                                    index
                                                  ).length > 0 ? (
                                                    <>
                                                      {/* Show total results count */}
                                                      <div className="px-4 py-2 text-xs text-gray-500 bg-green-50 border-b border-green-200">
                                                        {
                                                          getFilteredFreeDeliveryLocations(
                                                            index
                                                          ).length
                                                        }{" "}
                                                        location
                                                        {getFilteredFreeDeliveryLocations(
                                                          index
                                                        ).length !== 1
                                                          ? "s"
                                                          : ""}{" "}
                                                        found
                                                      </div>

                                                      {getFilteredFreeDeliveryLocations(
                                                        index
                                                      ).map((item, idx) => (
                                                        <button
                                                          key={idx}
                                                          type="button"
                                                          onClick={() =>
                                                            handleFreeDeliveryLocationSelect(
                                                              index,
                                                              item.region,
                                                              item.location
                                                            )
                                                          }
                                                          className="px-4 py-3 w-full text-sm text-left border-b border-gray-100 hover:bg-green-50 focus:bg-green-50 focus:outline-none last:border-b-0"
                                                        >
                                                          <div className="flex gap-3 items-start">
                                                            <div className="p-1 bg-green-100 rounded mt-0.5">
                                                              <MapPin className="w-3 h-3 text-green-600" />
                                                            </div>
                                                            <div className="flex-1">
                                                              <div className="font-medium text-gray-900">
                                                                {item.region}
                                                              </div>
                                                              <div className="text-gray-500 text-xs mt-0.5">
                                                                {item.location}
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </button>
                                                      ))}
                                                    </>
                                                  ) : (
                                                    <div className="px-4 py-3 text-sm text-center text-gray-500">
                                                      <div className="flex flex-col gap-2 items-center">
                                                        <svg
                                                          className="w-8 h-8 text-gray-300"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          viewBox="0 0 24 24"
                                                        >
                                                          <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709"
                                                          />
                                                        </svg>
                                                        <span>
                                                          No locations found
                                                        </span>
                                                        <span className="text-xs">
                                                          Try searching for a
                                                          region or location
                                                        </span>
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* Custom "Other" Option */}
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const customRegion =
                                                        prompt(
                                                          "Enter custom region:"
                                                        );
                                                      const customLocation =
                                                        prompt(
                                                          "Enter custom location:"
                                                        );
                                                      if (
                                                        customRegion &&
                                                        customLocation
                                                      ) {
                                                        handleFreeDeliveryLocationSelect(
                                                          index,
                                                          customRegion,
                                                          customLocation
                                                        );
                                                      }
                                                    }}
                                                    className="px-4 py-3 w-full text-sm text-left text-green-600 border-t border-green-200 hover:bg-green-50 focus:bg-green-50 focus:outline-none bg-green-50/50"
                                                  >
                                                    <div className="flex gap-2 items-center">
                                                      <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          strokeWidth={2}
                                                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                        />
                                                      </svg>
                                                      Add custom location
                                                    </div>
                                                  </button>
                                                </div>
                                              )}
                                            </div>

                                            {/* Selected Area Display */}
                                            {area.region && area.location && (
                                              <div className="p-2 mt-2 bg-white rounded-lg border border-green-300">
                                                <div className="text-sm font-medium text-green-800">
                                                  {area.region}
                                                </div>
                                                <div className="text-xs text-green-600">
                                                  {area.location}
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {/* Remove Button */}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeFreeDeliveryRegionLocation(
                                                index
                                              )
                                            }
                                            className="absolute -top-3 -right-3 p-2 text-white bg-red-500 rounded-full shadow-lg opacity-0 transition-colors duration-200 hover:bg-red-600 group-hover:opacity-100 sm:opacity-0"
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      </motion.div>
                                    )
                                  )}

                                  {/* Add Area Button */}
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    type="button"
                                    onClick={addFreeDeliveryRegionLocation}
                                    disabled={
                                      formData.free_delivery_areas.length >= 5
                                    }
                                    className="flex gap-2 items-center px-4 py-2 text-green-600 bg-green-50 rounded-lg border border-green-200 transition-colors duration-200 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                      />
                                    </svg>
                                    Add Another Area
                                  </motion.button>

                                  {/* Info Box */}
                                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                    <div className="flex gap-2 items-start">
                                      <div className="p-1 bg-green-100 rounded">
                                        <svg
                                          className="w-4 h-4 text-green-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-green-800">
                                          Free Delivery Areas
                                        </p>
                                        <p className="mt-1 text-xs text-green-600">
                                          Search through all 16 regions and
                                          hundreds of locations across Ghana.
                                          You can add up to 5 specific areas or
                                          choose nationwide coverage. Use the
                                          search to quickly find your desired
                                          region or location.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div className="grid grid-cols-1 gap-6">
                        {/* Terms & Conditions Tabbed Interface */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          <div className="flex gap-3 items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <FileText className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">
                                Terms & Conditions
                              </h4>
                              <p className="text-sm text-gray-500">
                                Choose how to set your terms and conditions
                              </p>
                            </div>
                          </div>

                          {/* Tab Navigation */}
                          <div className="p-1 bg-gray-100 rounded-xl">
                            <div className="grid grid-cols-3 gap-1">
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setTermsInputType("url")}
                                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                                  termsInputType === "url"
                                    ? "bg-white text-[#FF6C6C] shadow-md border border-[#FF6C6C]/20"
                                    : "text-gray-600 hover:text-gray-800"
                                }`}
                              >
                                <Link className="w-4 h-4" />
                                <span className="hidden sm:inline">URL</span>
                              </motion.button>

                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setTermsInputType("custom")}
                                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                                  termsInputType === "custom"
                                    ? "bg-white text-[#FF6C6C] shadow-md border border-[#FF6C6C]/20"
                                    : "text-gray-600 hover:text-gray-800"
                                }`}
                              >
                                <FileText className="w-4 h-4" />
                                <span className="hidden sm:inline">Custom</span>
                              </motion.button>

                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setTermsInputType("template")}
                                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                                  termsInputType === "template"
                                    ? "bg-white text-[#FF6C6C] shadow-md border border-[#FF6C6C]/20"
                                    : "text-gray-600 hover:text-gray-800"
                                }`}
                              >
                                <NotepadTextDashed className="w-4 h-4" />
                                <span className="hidden sm:inline">
                                  Template
                                </span>
                              </motion.button>
                            </div>
                          </div>

                          {/* Tab Content */}
                          <AnimatePresence mode="wait">
                            {/* URL Tab */}
                            {termsInputType === "url" && (
                              <motion.div
                                key="url"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
                              >
                                <div className="flex gap-3 items-start mb-4">
                                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                                    <Link className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-blue-900">
                                      External Terms & Conditions
                                    </h5>
                                    <p className="text-sm text-blue-700">
                                      Link to your existing terms and conditions
                                      document
                                    </p>
                                  </div>
                                </div>

                                <TextField
                                  label="Terms & Conditions URL"
                                  placeholder="https://yourwebsite.com/terms"
                                  value={termsUrl}
                                  onChange={(e) => setTermsUrl(e.target.value)}
                                  variant="outlined"
                                  fullWidth
                                  type="url"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "12px",
                                      backgroundColor: "white",
                                      "&:hover fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#FF6C6C",
                                    },
                                  }}
                                />

                                {termsUrl && (
                                  <div className="p-3 mt-3 bg-white rounded-lg border border-blue-200">
                                    <p className="text-sm text-gray-600">
                                      Preview:
                                    </p>
                                    <p className="mt-1 text-sm font-medium text-gray-900">
                                      Terms & Conditions: {termsUrl}
                                    </p>
                                  </div>
                                )}
                              </motion.div>
                            )}

                            {/* Custom Text Tab */}
                            {termsInputType === "custom" && (
                              <motion.div
                                key="custom"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-4"
                              >
                                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                                  <div className="flex gap-3 items-start">
                                    <div className="p-2 bg-green-100 rounded-lg shrink-0">
                                      <FileText className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                      <h5 className="font-semibold text-green-900">
                                        Custom Terms & Conditions
                                      </h5>
                                      <p className="text-sm text-green-700">
                                        Write your own terms and conditions from
                                        scratch
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <TextField
                                  label="Terms & Conditions"
                                  variant="outlined"
                                  fullWidth
                                  multiline
                                  rows={6}
                                  required
                                  value={formData.terms_and_conditions}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      terms_and_conditions: e.target.value,
                                    })
                                  }
                                  error={
                                    !!validationErrors.terms_and_conditions
                                  }
                                  helperText={
                                    validationErrors.terms_and_conditions
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "12px",
                                      "&:hover fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#FF6C6C",
                                      },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: "#FF6C6C",
                                    },
                                  }}
                                />

                                <div className="flex justify-between items-center px-2">
                                  <span className="text-xs text-gray-500">
                                    Minimum 10 characters required
                                  </span>
                                  <span
                                    className={`text-xs font-medium ${
                                      formData.terms_and_conditions.length >
                                      1000
                                        ? "text-red-500"
                                        : formData.terms_and_conditions.length >
                                          800
                                        ? "text-amber-500"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {formData.terms_and_conditions.length}/1000
                                  </span>
                                </div>
                              </motion.div>
                            )}

                            {/* Template Tab */}
                            {termsInputType === "template" && (
                              <motion.div
                                key="template"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                              >
                                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                                  <div className="flex gap-3 items-start">
                                    <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                                      <NotepadTextDashed className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                      <h5 className="font-semibold text-purple-900">
                                        Professional Template
                                      </h5>
                                      <p className="text-sm text-purple-700">
                                        Use our comprehensive template by
                                        filling in the blanks. Fields
                                        auto-populate from your deal data.
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Template Fields */}
                                <div className="space-y-6">
                                  {/* Basic Information and Policies - Side by Side */}
                                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    {/* Basic Information */}
                                    <div className="space-y-4">
                                      <h6 className="flex gap-2 items-center pb-2 text-sm font-semibold text-gray-700 border-b border-gray-200">
                                        <Zap className="w-4 h-4 text-[#FF6C6C]" />
                                        Basic Information
                                      </h6>

                                      <TextField
                                        label="Vendor/Brand Name"
                                        size="small"
                                        fullWidth
                                        value={templateFields.vendor}
                                        onChange={(e) =>
                                          setTemplateFields((prev) => ({
                                            ...prev,
                                            vendor: e.target.value,
                                          }))
                                        }
                                        placeholder="Your brand name"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            "&:hover fieldset": {
                                              borderColor: "#FF6C6C",
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: "#FF6C6C",
                                            },
                                          },
                                          "& .MuiInputLabel-root.Mui-focused": {
                                            color: "#FF6C6C",
                                          },
                                        }}
                                      />

                                      <TextField
                                        label="Platform"
                                        size="small"
                                        fullWidth
                                        value="DealBoxx"
                                        readOnly
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            backgroundColor: "#f5f5f5",
                                            "& fieldset": {
                                              borderColor: "#d1d5db",
                                            },
                                            "&:hover fieldset": {
                                              borderColor: "#d1d5db",
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: "#d1d5db",
                                            },
                                          },
                                          "& .MuiInputLabel-root": {
                                            color: "#6b7280",
                                          },
                                          "& .MuiInputLabel-root.Mui-focused": {
                                            color: "#6b7280",
                                          },
                                        }}
                                      />
                                    </div>

                                    {/* Policy Links & Limits */}
                                    <div className="space-y-4">
                                      <h6 className="flex gap-2 items-center pb-2 text-sm font-semibold text-gray-700 border-b border-gray-200">
                                        <Link className="w-4 h-4 text-[#FF6C6C]" />
                                        Policies & Links
                                      </h6>

                                      <div className="relative">
                                        <TextField
                                          label="Privacy Policy Link (Optional)"
                                          size="small"
                                          type="url"
                                          fullWidth
                                          value={templateFields.privacyLink}
                                          onChange={(e) =>
                                            setTemplateFields((prev) => ({
                                              ...prev,
                                              privacyLink: e.target.value,
                                            }))
                                          }
                                          placeholder="https://yourwebsite.com/privacy"
                                          InputProps={{
                                            startAdornment: (
                                              <div className="flex items-center mr-2">
                                                <Link className="w-4 h-4 text-blue-500" />
                                              </div>
                                            ),
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "8px",
                                              backgroundColor: "#F8FAFC",
                                              "&:hover fieldset": {
                                                borderColor: "#3B82F6",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#3B82F6",
                                              },
                                            },
                                            "& .MuiInputLabel-root.Mui-focused":
                                              { color: "#3B82F6" },
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Redemption Process - Below both sections */}
                                <div className="space-y-4">
                                  <h6 className="flex gap-2 items-center pb-2 text-sm font-semibold text-gray-700 border-b border-gray-200">
                                    <CheckCircle className="w-4 h-4 text-[#FF6C6C]" />
                                    Redemption Process
                                  </h6>

                                  <TextField
                                    label="Redemption Process (Optional)"
                                    size="small"
                                    multiline
                                    rows={8}
                                    fullWidth
                                    value={templateFields.redemptionProcess}
                                    onChange={(e) =>
                                      setTemplateFields((prev) => ({
                                        ...prev,
                                        redemptionProcess: e.target.value,
                                      }))
                                    }
                                    placeholder="Leave empty to use auto-generated process based on your selections...

Example custom format (press Enter for new lines):
In-Store Redemption:
Available at: Your store locations

Process:
1. Present your DealBoxx code
2. Complete purchase
3. Receive confirmation

Important Notes:
- Code valid for one-time use
- Must redeem within validity period
- Cannot combine with other offers

Online Redemption:
Process:
1. Visit our website
2. Enter code at checkout
3. Complete order

Important Notes:
- Delivery fees may apply
- Refunds subject to policy"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        "&:hover fieldset": {
                                          borderColor: "#FF6C6C",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#FF6C6C",
                                        },
                                      },
                                      "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#FF6C6C",
                                      },
                                    }}
                                  />

                                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex gap-2 items-start">
                                      <div className="p-1 bg-blue-100 rounded">
                                        <CheckCircle className="w-4 h-4 text-blue-600" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-blue-800">
                                          Auto-Generated Process
                                        </p>
                                        <p className="mt-1 text-xs text-blue-600">
                                          If left empty, the system will
                                          automatically generate a comprehensive
                                          redemption process based on your
                                          selected redemption types and
                                          locations. Use Enter key to create new
                                          lines and format your custom process
                                          with bullet points (-) and numbered
                                          lists (1., 2., etc.).
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Category-Specific Fields */}
                                {formData.category.length > 0 && (
                                  <div className="space-y-4">
                                    <h6 className="flex gap-2 items-center pb-2 text-sm font-semibold text-gray-700 border-b border-gray-200">
                                      <Package className="w-4 h-4 text-[#FF6C6C]" />
                                      Category-Specific Terms
                                    </h6>

                                    <div className="flex flex-wrap gap-2 mb-4">
                                      {formData.category.map((cat, index) => (
                                        <Chip
                                          key={index}
                                          label={cat}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#FF6C6C",
                                            color: "white",
                                            "& .MuiChip-deleteIcon": {
                                              color: "white",
                                            },
                                          }}
                                        />
                                      ))}
                                    </div>

                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                      <div className="flex gap-3 items-start">
                                        <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                                          <Package className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                          <h6 className="mb-2 font-semibold text-blue-900">
                                            Customize Category Terms
                                          </h6>
                                          <p className="text-sm text-blue-700">
                                            Customize warranty periods, return
                                            windows, and other terms specific to
                                            your selected categories.
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Dynamic Category-Specific Input Fields */}
                                    <div className="space-y-6">
                                      <AnimatePresence>
                                        {/* Electronics & Gadgets */}
                                        {formData.category.includes(
                                          "Electronics & Gadgets"
                                        ) && (
                                          <motion.div
                                            key="electronics"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-blue-100 rounded-lg">
                                                <Zap className="w-5 h-5 text-blue-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-blue-900">
                                                Electronics & Gadgets
                                              </h6>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#3B82F6",
                                                    },
                                                  }}
                                                >
                                                  Warranty Period
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.warrantyPeriod
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        warrantyPeriod:
                                                          e.target.value,
                                                        warrantyPeriodCustom:
                                                          e.target.value !== "custom" ? "" : prev.warrantyPeriodCustom,
                                                      })
                                                    )
                                                  }
                                                  label="Warranty Period"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#3B82F6",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#3B82F6",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 months
                                                  </MenuItem>
                                                  <MenuItem value="6">
                                                    6 months
                                                  </MenuItem>
                                                  <MenuItem value="12">
                                                    12 months (1 year)
                                                  </MenuItem>
                                                  <MenuItem value="24">
                                                    24 months (2 years)
                                                  </MenuItem>
                                                  <MenuItem value="36">
                                                    36 months (3 years)
                                                  </MenuItem>
                                                  <MenuItem value="48">
                                                    48 months (4 years)
                                                  </MenuItem>
                                                  <MenuItem value="60">
                                                    60 months (5 years)
                                                  </MenuItem>
                                                  <MenuItem value="72">
                                                    72 months (6 years)
                                                  </MenuItem>
                                                  <MenuItem value="84">
                                                    84 months (7 years)
                                                  </MenuItem>
                                                  <MenuItem value="custom">
                                                    Custom
                                                  </MenuItem>
                                                  <MenuItem value="not_applicable">
                                                    Not Applicable
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              {categorySpecificTerms.warrantyPeriod === "custom" && (
                                                <TextField
                                                  size="small"
                                                  fullWidth
                                                  label="Custom Warranty Period (months)"
                                                  type="number"
                                                  value={categorySpecificTerms.warrantyPeriodCustom}
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        warrantyPeriodCustom:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  placeholder="Enter warranty period in months"
                                                  sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                      borderRadius: "8px",
                                                      "&:hover fieldset": {
                                                        borderColor: "#3B82F6",
                                                      },
                                                      "&.Mui-focused fieldset": {
                                                        borderColor: "#3B82F6",
                                                      },
                                                    },
                                                  }}
                                                />
                                              )}
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#3B82F6",
                                                    },
                                                  }}
                                                >
                                                  DOA Replacement Days
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.doaReplacementDays
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        doaReplacementDays:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  label="DOA Replacement Days"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#3B82F6",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#3B82F6",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 days
                                                  </MenuItem>
                                                  <MenuItem value="7">
                                                    7 days
                                                  </MenuItem>
                                                  <MenuItem value="14">
                                                    14 days
                                                  </MenuItem>
                                                  <MenuItem value="30">
                                                    30 days
                                                  </MenuItem>
                                                  <MenuItem value="not_applicable">
                                                    Not Applicable
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                            </div>
                                          </motion.div>
                                        )}

                                        {/* Groceries */}
                                        {formData.category.includes(
                                          "Groceries"
                                        ) && (
                                          <motion.div
                                            key="groceries"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-green-100 rounded-lg">
                                                <ShoppingBag className="w-5 h-5 text-green-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-green-900">
                                                Groceries
                                              </h6>
                                            </div>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel
                                                sx={{
                                                  "&.Mui-focused": {
                                                    color: "#059669",
                                                  },
                                                }}
                                              >
                                                Return Window for Damaged Items
                                              </InputLabel>
                                              <Select
                                                value={
                                                  categorySpecificTerms.returnWindow
                                                }
                                                onChange={(e) =>
                                                  setCategorySpecificTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      returnWindow:
                                                        e.target.value,
                                                    })
                                                  )
                                                }
                                                label="Return Window for Damaged Items"
                                                sx={{
                                                  borderRadius: "8px",
                                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#059669" },
                                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#059669" },
                                                }}
                                              >
                                                <MenuItem value="12">
                                                  12 hours
                                                </MenuItem>
                                                <MenuItem value="24">
                                                  24 hours
                                                </MenuItem>
                                                <MenuItem value="48">
                                                  48 hours
                                                </MenuItem>
                                                <MenuItem value="72">
                                                  72 hours
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </motion.div>
                                        )}

                                        {/* Fashion & Accessories */}
                                        {formData.category.includes(
                                          "Fashion & Accessories"
                                        ) && (
                                          <motion.div
                                            key="fashion"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl border border-pink-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-pink-100 rounded-lg">
                                                <Gift className="w-5 h-5 text-pink-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-pink-900">
                                                Fashion & Accessories
                                              </h6>
                                            </div>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel
                                                sx={{
                                                  "&.Mui-focused": {
                                                    color: "#E11D48",
                                                  },
                                                }}
                                              >
                                                Return Period
                                              </InputLabel>
                                              <Select
                                                value={
                                                  categorySpecificTerms.fashionReturnDays
                                                }
                                                onChange={(e) =>
                                                  setCategorySpecificTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      fashionReturnDays:
                                                        e.target.value,
                                                    })
                                                  )
                                                }
                                                label="Return Period"
                                                sx={{
                                                  borderRadius: "8px",
                                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#E11D48" },
                                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#E11D48" },
                                                }}
                                              >
                                                <MenuItem value="7">
                                                  7 days
                                                </MenuItem>
                                                <MenuItem value="14">
                                                  14 days
                                                </MenuItem>
                                                <MenuItem value="21">
                                                  21 days
                                                </MenuItem>
                                                <MenuItem value="30">
                                                  30 days
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </motion.div>
                                        )}

                                        {/* Real Estate & Housing */}
                                        {formData.category.includes(
                                          "Real Estate & Housing"
                                        ) && (
                                          <motion.div
                                            key="realestate"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-amber-100 rounded-lg">
                                                <Building2 className="w-5 h-5 text-amber-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-amber-900">
                                                Real Estate & Housing
                                              </h6>
                                            </div>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel
                                                sx={{
                                                  "&.Mui-focused": {
                                                    color: "#D97706",
                                                  },
                                                }}
                                              >
                                                Advance Notice for Termination
                                              </InputLabel>
                                              <Select
                                                value={
                                                  categorySpecificTerms.advanceNoticeDays
                                                }
                                                onChange={(e) =>
                                                  setCategorySpecificTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      advanceNoticeDays:
                                                        e.target.value,
                                                    })
                                                  )
                                                }
                                                label="Advance Notice for Termination"
                                                sx={{
                                                  borderRadius: "8px",
                                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#D97706" },
                                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#D97706" },
                                                }}
                                              >
                                                <MenuItem value="30">
                                                  30 days
                                                </MenuItem>
                                                <MenuItem value="60">
                                                  60 days
                                                </MenuItem>
                                                <MenuItem value="90">
                                                  90 days
                                                </MenuItem>
                                                <MenuItem value="120">
                                                  120 days
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </motion.div>
                                        )}

                                        {/* Furniture */}
                                        {formData.category.includes(
                                          "Furniture"
                                        ) && (
                                          <motion.div
                                            key="furniture"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-purple-100 rounded-lg">
                                                <Package className="w-5 h-5 text-purple-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-purple-900">
                                                Furniture
                                              </h6>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#7C3AED",
                                                    },
                                                  }}
                                                >
                                                  Warranty Period
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.furnitureWarrantyPeriod
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        furnitureWarrantyPeriod:
                                                          e.target.value,
                                                        furnitureWarrantyPeriodCustom:
                                                          e.target.value !== "custom" ? "" : prev.furnitureWarrantyPeriodCustom,
                                                      })
                                                    )
                                                  }
                                                  label="Warranty Period"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#7C3AED",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#7C3AED",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 months
                                                  </MenuItem>
                                                  <MenuItem value="6">
                                                    6 months
                                                  </MenuItem>
                                                  <MenuItem value="12">
                                                    12 months (1 year)
                                                  </MenuItem>
                                                  <MenuItem value="24">
                                                    24 months (2 years)
                                                  </MenuItem>
                                                  <MenuItem value="36">
                                                    36 months (3 years)
                                                  </MenuItem>
                                                  <MenuItem value="48">
                                                    48 months (4 years)
                                                  </MenuItem>
                                                  <MenuItem value="60">
                                                    60 months (5 years)
                                                  </MenuItem>
                                                  <MenuItem value="72">
                                                    72 months (6 years)
                                                  </MenuItem>
                                                  <MenuItem value="84">
                                                    84 months (7 years)
                                                  </MenuItem>
                                                  <MenuItem value="custom">
                                                    Custom
                                                  </MenuItem>
                                                  <MenuItem value="not_applicable">
                                                    Not Applicable
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              {categorySpecificTerms.furnitureWarrantyPeriod === "custom" && (
                                                <TextField
                                                  size="small"
                                                  fullWidth
                                                  label="Custom Warranty Period (months)"
                                                  type="number"
                                                  value={categorySpecificTerms.furnitureWarrantyPeriodCustom}
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        furnitureWarrantyPeriodCustom:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  placeholder="Enter warranty period in months"
                                                  sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                      borderRadius: "8px",
                                                      "&:hover fieldset": {
                                                        borderColor: "#7C3AED",
                                                      },
                                                      "&.Mui-focused fieldset": {
                                                        borderColor: "#7C3AED",
                                                      },
                                                    },
                                                  }}
                                                />
                                              )}
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#7C3AED",
                                                    },
                                                  }}
                                                >
                                                  Return Period
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.furnitureReturnDays
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        furnitureReturnDays:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  label="Return Period"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#7C3AED",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#7C3AED",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 days
                                                  </MenuItem>
                                                  <MenuItem value="7">
                                                    7 days
                                                  </MenuItem>
                                                  <MenuItem value="14">
                                                    14 days
                                                  </MenuItem>
                                                  <MenuItem value="30">
                                                    30 days
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                            </div>
                                          </motion.div>
                                        )}

                                        {/* Beauty, Spas & Wellness */}
                                        {formData.category.includes(
                                          "Beauty, Spas & Wellness"
                                        ) && (
                                          <motion.div
                                            key="beauty"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-teal-100 rounded-lg">
                                                <Zap className="w-5 h-5 text-teal-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-teal-900">
                                                Beauty, Spas & Wellness
                                              </h6>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#0D9488",
                                                    },
                                                  }}
                                                >
                                                  Reschedule Notice
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.rescheduleNoticeHours
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        rescheduleNoticeHours:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  label="Reschedule Notice"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#0D9488",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#0D9488",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="12">
                                                    12 hours
                                                  </MenuItem>
                                                  <MenuItem value="24">
                                                    24 hours
                                                  </MenuItem>
                                                  <MenuItem value="48">
                                                    48 hours
                                                  </MenuItem>
                                                  <MenuItem value="72">
                                                    72 hours
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#0D9488",
                                                    },
                                                  }}
                                                >
                                                  Package Validity
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.packageValidityMonths
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        packageValidityMonths:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  label="Package Validity"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#0D9488",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#0D9488",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 months
                                                  </MenuItem>
                                                  <MenuItem value="6">
                                                    6 months
                                                  </MenuItem>
                                                  <MenuItem value="12">
                                                    12 months
                                                  </MenuItem>
                                                  <MenuItem value="18">
                                                    18 months
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                            </div>
                                          </motion.div>
                                        )}

                                        {/* Add remaining categories... */}
                                        {/* For brevity, I'll add a few more key ones */}

                                        {/* Tourism & Travel */}
                                        {formData.category.includes(
                                          "Tourism & Travel"
                                        ) && (
                                          <motion.div
                                            key="travel"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl border border-sky-200"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-sky-100 rounded-lg">
                                                <Navigation className="w-5 h-5 text-sky-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-sky-900">
                                                Tourism & Travel
                                              </h6>
                                            </div>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel
                                                sx={{
                                                  "&.Mui-focused": {
                                                    color: "#0284C7",
                                                  },
                                                }}
                                              >
                                                Rescheduling Notice Required
                                              </InputLabel>
                                              <Select
                                                value={
                                                  categorySpecificTerms.travelNoticeHours
                                                }
                                                onChange={(e) =>
                                                  setCategorySpecificTerms(
                                                    (prev) => ({
                                                      ...prev,
                                                      travelNoticeHours:
                                                        e.target.value,
                                                    })
                                                  )
                                                }
                                                label="Rescheduling Notice Required"
                                                sx={{
                                                  borderRadius: "8px",
                                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#0284C7" },
                                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: "#0284C7" },
                                                }}
                                              >
                                                <MenuItem value="24">
                                                  24 hours
                                                </MenuItem>
                                                <MenuItem value="48">
                                                  48 hours
                                                </MenuItem>
                                                <MenuItem value="72">
                                                  72 hours
                                                </MenuItem>
                                                <MenuItem value="96">
                                                  96 hours
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </motion.div>
                                        )}

                                        {/* Automotive */}
                                        {formData.category.includes(
                                          "Automotive"
                                        ) && (
                                          <motion.div
                                            key="automotive"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                              opacity: 1,
                                              height: "auto",
                                            }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="p-6 bg-gradient-to-br from-gray-50 rounded-xl border border-gray-200 to-slate-50"
                                          >
                                            <div className="flex gap-3 items-center mb-4">
                                              <div className="p-2 bg-gray-100 rounded-lg">
                                                <Navigation className="w-5 h-5 text-gray-600" />
                                              </div>
                                              <h6 className="text-lg font-semibold text-gray-900">
                                                Automotive
                                              </h6>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#374151",
                                                    },
                                                  }}
                                                >
                                                  Warranty Period
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.autoWarrantyPeriod
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        autoWarrantyPeriod:
                                                          e.target.value,
                                                        autoWarrantyPeriodCustom:
                                                          e.target.value !== "custom" ? "" : prev.autoWarrantyPeriodCustom,
                                                      })
                                                    )
                                                  }
                                                  label="Warranty Period"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#374151",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#374151",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 months
                                                  </MenuItem>
                                                  <MenuItem value="6">
                                                    6 months
                                                  </MenuItem>
                                                  <MenuItem value="12">
                                                    12 months (1 year)
                                                  </MenuItem>
                                                  <MenuItem value="24">
                                                    24 months (2 years)
                                                  </MenuItem>
                                                  <MenuItem value="36">
                                                    36 months (3 years)
                                                  </MenuItem>
                                                  <MenuItem value="48">
                                                    48 months (4 years)
                                                  </MenuItem>
                                                  <MenuItem value="60">
                                                    60 months (5 years)
                                                  </MenuItem>
                                                  <MenuItem value="72">
                                                    72 months (6 years)
                                                  </MenuItem>
                                                  <MenuItem value="84">
                                                    84 months (7 years)
                                                  </MenuItem>
                                                  <MenuItem value="custom">
                                                    Custom
                                                  </MenuItem>
                                                  <MenuItem value="not_applicable">
                                                    Not Applicable
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              {categorySpecificTerms.autoWarrantyPeriod === "custom" && (
                                                <TextField
                                                  size="small"
                                                  fullWidth
                                                  label="Custom Warranty Period (months)"
                                                  type="number"
                                                  value={categorySpecificTerms.autoWarrantyPeriodCustom}
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        autoWarrantyPeriodCustom:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  placeholder="Enter warranty period in months"
                                                  sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                      borderRadius: "8px",
                                                      "&:hover fieldset": {
                                                        borderColor: "#374151",
                                                      },
                                                      "&.Mui-focused fieldset": {
                                                        borderColor: "#374151",
                                                      },
                                                    },
                                                  }}
                                                />
                                              )}
                                              <FormControl
                                                size="small"
                                                fullWidth
                                              >
                                                <InputLabel
                                                  sx={{
                                                    "&.Mui-focused": {
                                                      color: "#374151",
                                                    },
                                                  }}
                                                >
                                                  Return Period
                                                </InputLabel>
                                                <Select
                                                  value={
                                                    categorySpecificTerms.autoReturnDays
                                                  }
                                                  onChange={(e) =>
                                                    setCategorySpecificTerms(
                                                      (prev) => ({
                                                        ...prev,
                                                        autoReturnDays:
                                                          e.target.value,
                                                      })
                                                    )
                                                  }
                                                  label="Return Period"
                                                  sx={{
                                                    borderRadius: "8px",
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#374151",
                                                      },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#374151",
                                                      },
                                                  }}
                                                >
                                                  <MenuItem value="3">
                                                    3 days
                                                  </MenuItem>
                                                  <MenuItem value="7">
                                                    7 days
                                                  </MenuItem>
                                                  <MenuItem value="14">
                                                    14 days
                                                  </MenuItem>
                                                  <MenuItem value="30">
                                                    30 days
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  </div>
                                )}

                                {/* Preview */}
                                {formData.terms_and_conditions && (
                                  <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                                    <h6 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-800">
                                      <FileText className="w-5 h-5 text-[#FF6C6C]" />
                                      Terms & Conditions Preview
                                    </h6>
                                    <div className="overflow-y-auto p-4 max-h-96 bg-gray-50 rounded-lg border">
                                      <div className="space-y-4 text-sm leading-relaxed text-gray-700 terms-preview">
                                        {(() => {
                                          // Split by double newlines first, then process each section
                                          const sections =
                                            formData.terms_and_conditions.split(
                                              "\n\n"
                                            );
                                          const processedSections = [];

                                          sections.forEach((section, index) => {
                                            if (section.trim() === "") return;

                                            // Check if it's the main title
                                            if (
                                              section.trim() ===
                                              "TERMS AND CONDITIONS"
                                            ) {
                                              processedSections.push(
                                                <div
                                                  key={`title-${index}`}
                                                  className="text-center py-4 mb-4 border-b-2 border-[#FF6C6C]"
                                                >
                                                  <h2 className="text-xl font-bold tracking-wide text-gray-800 uppercase">
                                                    {section.trim()}
                                                  </h2>
                                                </div>
                                              );
                                              return;
                                            }

                                            // Check if it's a single line with colon (like "Brand Name: Minimax")
                                            if (
                                              section.includes(":") &&
                                              !section.includes("\n") &&
                                              section.split(":")[1].trim() !==
                                                ""
                                            ) {
                                              const [header, content] =
                                                section.split(":");

                                              // Special styling for Brand Name and Redemption Location
                                              const isBrandName = header
                                                .trim()
                                                .toLowerCase()
                                                .includes("brand name");
                                              const isRedemptionLocation =
                                                header
                                                  .trim()
                                                  .toLowerCase()
                                                  .includes(
                                                    "redemption location"
                                                  );

                                              processedSections.push(
                                                <div
                                                  key={`single-${index}`}
                                                  className={`border-l-4 pl-4 py-3 rounded-r ${
                                                    isBrandName ||
                                                    isRedemptionLocation
                                                      ? "border-green-500 bg-gradient-to-r from-green-50 to-white"
                                                      : "border-[#FF6C6C] bg-gradient-to-r from-gray-50 to-white"
                                                  }`}
                                                >
                                                  <h4
                                                    className={`mb-2 text-base font-semibold ${
                                                      isBrandName ||
                                                      isRedemptionLocation
                                                        ? "text-green-800"
                                                        : "text-gray-800"
                                                    }`}
                                                  >
                                                    {header.trim()}:
                                                  </h4>
                                                  <p
                                                    className={`ml-2 ${
                                                      isBrandName ||
                                                      isRedemptionLocation
                                                        ? "text-green-700 font-medium"
                                                        : "text-gray-700"
                                                    }`}
                                                  >
                                                    {content.trim()}
                                                  </p>
                                                </div>
                                              );
                                              return;
                                            }

                                            // Check if it's a header with content below (like "Vendor Information:" followed by content)
                                            if (
                                              section.includes(":") &&
                                              section.split(":")[1].trim() ===
                                                ""
                                            ) {
                                              processedSections.push(
                                                <div
                                                  key={`header-${index}`}
                                                  className="py-2"
                                                >
                                                  <h3 className="pb-1 text-lg font-bold text-gray-800 border-b border-gray-300">
                                                    {section.replace(":", "")}
                                                  </h3>
                                                </div>
                                              );
                                              return;
                                            }

                                            // Check if it contains bullet points
                                            if (section.includes("•")) {
                                              processedSections.push(
                                                <div
                                                  key={`bullets-${index}`}
                                                  className="p-4 bg-gray-50 rounded-lg border"
                                                >
                                                  <div className="space-y-2">
                                                    {section
                                                      .split("\n")
                                                      .map(
                                                        (line, lineIndex) => {
                                                          if (
                                                            line
                                                              .trim()
                                                              .startsWith("•")
                                                          ) {
                                                            return (
                                                              <div
                                                                key={lineIndex}
                                                                className="flex gap-2 items-start"
                                                              >
                                                                <span className="text-[#FF6C6C] font-bold">
                                                                  •
                                                                </span>
                                                                <span className="text-gray-700">
                                                                  {line
                                                                    .replace(
                                                                      "•",
                                                                      ""
                                                                    )
                                                                    .trim()}
                                                                </span>
                                                              </div>
                                                            );
                                                          }
                                                          return (
                                                            <p
                                                              key={lineIndex}
                                                              className="mb-2 font-medium text-gray-700"
                                                            >
                                                              {line.trim()}
                                                            </p>
                                                          );
                                                        }
                                                      )}
                                                  </div>
                                                </div>
                                              );
                                              return;
                                            }

                                            // Check if it's a category-specific section
                                            if (
                                              section.includes(
                                                "Electronics & Gadgets:"
                                              ) ||
                                              section.includes(
                                                "Fashion & Accessories:"
                                              ) ||
                                              section.includes(
                                                "Food & Beverage:"
                                              ) ||
                                              section.includes(
                                                "Beauty, Spas & Wellness:"
                                              ) ||
                                              section.includes("Automotive:") ||
                                              section.includes(
                                                "Real Estate & Housing:"
                                              ) ||
                                              section.includes(
                                                "Tourism & Travel:"
                                              ) ||
                                              section.includes(
                                                "Events and Entertainment:"
                                              )
                                            ) {
                                              processedSections.push(
                                                <div
                                                  key={`category-${index}`}
                                                  className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400"
                                                >
                                                  <div className="flex gap-3 items-start">
                                                    <Package className="w-5 h-5 text-blue-600 mt-0.5" />
                                                    <p className="font-medium text-blue-800">
                                                      {section.trim()}
                                                    </p>
                                                  </div>
                                                </div>
                                              );
                                              return;
                                            }

                                            // Regular paragraph
                                            processedSections.push(
                                              <div
                                                key={`para-${index}`}
                                                className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
                                              >
                                                <p className="leading-relaxed text-gray-700 whitespace-pre-wrap">
                                                  {section.trim()}
                                                </p>
                                              </div>
                                            );
                                          });

                                          return processedSections;
                                        })()}
                                      </div>

                                      {/* Footer */}
                                      <div className="pt-4 mt-6 border-t border-gray-200">
                                        <p className="text-xs italic text-center text-gray-500">
                                          These terms and conditions are legally
                                          binding. Generated on{" "}
                                          {new Date().toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      </div>
                    </div>

                    {/* Promotion Option */}
                    {/* <div className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "#FF6C6C",
                        "&.Mui-checked": { color: "#FF6C6C" },
                      }}
                    />
                  }
                  label="Promote this deal on our platform"
                  className="text-gray-700"
                />
              </div> */}

                    {/* Step 4 Navigation & Submit */}
                    <div className="flex flex-col gap-4 justify-between items-center p-6 bg-white rounded-xl border border-gray-100 sm:flex-row">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={prevStep}
                        className="px-6 py-3 w-full font-medium text-gray-600 bg-gray-100 rounded-xl transition-all duration-200 sm:w-auto hover:bg-gray-200"
                      >
                        <span className="hidden sm:inline">Back to Target Criteria</span>
                        <span className="sm:hidden">Back</span>
                      </motion.button>
                      <div className="flex gap-2 items-center text-sm text-gray-500">
                        <CloudUpload className="w-4 h-4" />
                        <span className="hidden sm:inline">Step 4 of 5</span>
                        <span className="sm:hidden">4/5</span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={nextStep}
                        disabled={!stepValidation[4]}
                        className={`w-full sm:w-auto px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                          stepValidation[4]
                            ? "bg-[#FF6C6C] text-white hover:bg-[#FF8F8F] shadow-lg shadow-[#FF6C6C]/20"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <span className="hidden sm:inline">Review & Publish</span>
                        <span className="sm:hidden">Review</span>
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Step 5: Review & Submit */}
                {currentStep === 5 && (
                  <ReviewStep
                    formData={formData}
                    startDate={startDate}
                    endDate={endDate}
                    redemptionLocationType={redemptionLocationType}
                    selectedBranches={selectedBranches}
                    redemptionLocationTypeStep4={redemptionLocationTypeStep4}
                    selectAllRedemptionLocations={selectAllRedemptionLocations}
                    selectedRedemptionBranches={selectedRedemptionBranches}
                    selectAllLocations={selectAllLocations}
                    selectAllFreeDeliveryLocations={selectAllFreeDeliveryLocations}
                    selectedPartners={selectedPartners}
                    primaryImagePreview={primaryImagePreview}
                    imagePreview={imagePreview}
                    isSubmitting={isSubmitting}
                    isPriceValid={isPriceValid}
                    onPrevStep={prevStep}
                    onGoToStep={setCurrentStep}
                    onSubmit={handleSubmit}
                  />
                )}

              </motion.div>
          </motion.div>
        </div>

        {/* Inline Partner Modal */}
        <InlinePartnerModal
          isOpen={showInlinePartnerModal}
          onClose={() => setShowInlinePartnerModal(false)}
          onPartnerAdded={handlePartnerAdded}
        />

        {/* Permission Denied Modal */}
        <PermissionDeniedModal
          isOpen={showPermissionDeniedModal}
          onClose={() => setShowPermissionDeniedModal(false)}
          message="You need Administrator permission to add partners. Please contact your administrator to request CREATE_PARTNER permission."
          requiredPermission="CREATE_PARTNER"
        />

        {/* Branch Management Modal */}
        <BranchManagementModal
          isOpen={showBranchModal}
          onClose={() => setShowBranchModal(false)}
          onBranchesUpdated={() => {
            fetchBranches();
            toast.success("Branches updated successfully");
          }}
        />
      </div>
    </div>
  );
};

export default NewDealForm;
